<template>
  <div class="masonry-marketplace">
    <!-- Toolbar + lists column wrapper -->
    <div
      class="market-toolbar-and-lists column"
      :class="{ 'hidden-toolbar': !navbarVisible }"
    >
      <FiltersComponent
        @update:searchQuery="searchQuery = $event"
        @update:filterType="filterType = $event"
        @update:chainFilter="chainFilter = $event"
        @update:sortOrder="sortOrder = $event"
        @update:selectedCategory="selectedCategory = $event"
        @update:locationFilter="handleLocationFilterUpdate"
        @update:orgMode="handleOrgModeUpdate"
        :categories="categories"
        :initialFilterType="filterType"
        :orgMode="internalOrgMode"
        :isTradeAllowed="isTradeAllowed"
        :showSellingAction="showSellingAction"
        :hideFavourites="
          !isWishlistFeatureEnabled ||
          showItems === 'profileUser' ||
          showItems === 'organisation'
        "
      >
        <template #post-action>
          <slot name="post-action"></slot>
        </template>
        <template #selling-action>
          <slot name="selling-action"></slot>
        </template>
      </FiltersComponent>
      <!-- Injected lists (favourites / recently added) positioned between toolbar and grid -->
      <div class="above-grid-lists" v-if="$slots['above-grid']">
        <slot name="above-grid"></slot>
      </div>
    </div>

    <!-- Feed scope toggle — above the grid, in the scrollable content area -->
    <div
      v-if="showItems !== 'profileUser' && !hideFeedToggle"
      class="feed-scope-toggle"
    >
      <SingleSelectToggleRow
        :options="feedScopeOptions"
        v-model="feedScope"
        aria-label="Marketplace feed scope"
        variant="segmented"
        :wrap="false"
        press-on-focus
      />
      <OrgContextPicker
        v-if="
          feedScope === 'org' &&
          hasMultipleOrganisations &&
          isOrgContextFeatureEnabled
        "
        @change="handleSelectedOrgChange"
      />
    </div>

    <section
      v-if="
        showItems !== 'profileUser' &&
        !hideFeedToggle &&
        authUserUid &&
        !hasActiveOrganisations
      "
      class="feed-organisation-zero-state"
      aria-labelledby="feed-organisation-zero-title"
    >
      <div>
        <h2 id="feed-organisation-zero-title">Start an organisation feed</h2>
        <p>Create an organisation or use an invite link to join one.</p>
      </div>
      <div class="feed-organisation-zero-state__actions">
        <UnitButton variant="text" @click="openOrganisationCreation">
          Create organisation
        </UnitButton>
        <UnitButton variant="text" @click="openInvites">
          Use an invite link
        </UnitButton>
      </div>
    </section>

    <!-- Initial loading skeleton below filters -->
    <div v-if="isInitialLoading" class="initial-skeleton-container">
      <div
        v-for="n in getNumberOfColumns()"
        :key="`column-${n}`"
        class="masonry-column"
      >
        <div v-for="i in 2" :key="`skeleton-${n}-${i}`" class="skeleton-item">
          <div class="skeleton-image shimmer"></div>
          <div class="skeleton-details">
            <div class="skeleton-title shimmer"></div>
            <div class="skeleton-price shimmer"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Regular masonry grid container (shown when not in initial loading) -->
    <div v-else class="masonry-grid-container">
      <!-- Timeline fetch error state with retry -->
      <div
        v-if="timelineError && !isLoadingMoreItems"
        class="no-results-container"
      >
        <div class="no-results-message">
          <svg class="no-results-icon">
            <use href="@/assets/icons/iconset.svg#warning"></use>
          </svg>
          <h3>We couldn't load items</h3>
          <p>{{ timelineError }}</p>
          <UnitButton
            variant="secondary"
            class="marketplace-state-action"
            @click="retryTimelineFetch"
          >
            Try again
          </UnitButton>
        </div>
      </div>

      <!-- No matching items message -->
      <div
        v-else-if="noMatchingItems && !isLoadingMoreItems"
        class="no-results-container"
      >
        <div class="no-results-message">
          <svg class="no-results-icon small">
            <use href="@/assets/icons/iconset.svg#filter_slider"></use>
          </svg>
          <h3>{{ emptyStateTitle }}</h3>
          <p>{{ emptyStateDescription }}</p>
          <UnitButton
            variant="secondary"
            class="marketplace-state-action"
            @click="resetFilters"
            v-if="hasActiveFilters"
          >
            Clear all filters
          </UnitButton>
        </div>
      </div>

      <!-- Grid with items -->
      <div v-else class="masonry-grid">
        <div
          v-for="(column, colIndex) in columns"
          :key="`column-${colIndex}`"
          class="masonry-column"
        >
          <transition-group name="fade" tag="div" class="masonry-column-items">
            <figure
              class="masonry-item"
              v-for="(item, itemIndex) in column"
              :key="item.id"
            >
              <!-- Title at the top-left (hide if no image; title is already inside placeholder). Also hide when title is empty or a known placeholder. -->
              <div
                class="masonry-item-title-row"
                v-if="hasRealTitle(item) && hasImages(item)"
              >
                <div class="masonry-item-title">
                  {{ truncateTitleForBand(getDisplayTitle(item)) }}
                </div>
              </div>

              <div class="image-container">
                <!-- Use ImageCard for all items (media, products, services) -->
                <ImageCard
                  :item="item"
                  :favouritesLoading="favouritesLoading"
                  :saving="
                    isSaveLoading &&
                    currentProduct &&
                    currentProduct.id === item.id
                  "
                  :loadingPriority="itemIndex === 0 ? 'eager' : 'lazy'"
                  @select="selectMarketplaceCard(item)"
                >
                  <template v-slot:dropdown-menu>
                    <Dropdown
                      v-if="!item._authorityPresentation"
                      :icon-only="true"
                      :compact="true"
                      :button-class="'cleanIconButton'"
                      :trigger-label="`Open actions for ${getDisplayTitle(
                        item
                      )}`"
                      :panel-class="'actions-menu'"
                      :z-index="3"
                      openFrom="rightdown"
                    >
                      <template #default="{ close }">
                        <div class="actions-menu">
                          <!-- Unlist NFT action (only for owner of LISTED NFTs) -->
                          <div
                            v-if="
                              (isItemOwnedByCurrentUser(item) ||
                                item.ownerUid === authUserUid) &&
                              item.isNFTListing
                            "
                            class="menu-item delete-item"
                            :class="{
                              'confirm-phase':
                                compactUnlistConfirmItemId === item.id,
                            }"
                            @click.stop="
                              handleCompactUnlistMenuClick(item, close)
                            "
                          >
                            <svg class="compactImg">
                              <use
                                href="@/assets/icons/iconset.svg#unpost_unlist"
                              ></use>
                            </svg>
                            <template
                              v-if="compactUnlistConfirmItemId === item.id"
                              >Unlist? <strong>Yes</strong></template
                            >
                            <template v-else>Unlist</template>
                          </div>
                          <!-- Delete action with trash icon (only for owner, NOT for minted NFTs) -->
                          <div
                            v-if="
                              isItemOwnedByCurrentUser(item) &&
                              !isItemMinted(item)
                            "
                            class="menu-item delete-item"
                            :class="{
                              'confirm-phase':
                                compactDeleteConfirmItemId === item.id,
                            }"
                            @click.stop="
                              handleCompactDeleteMenuClick(item, close)
                            "
                          >
                            <svg class="tinyImg">
                              <use
                                href="@/assets/icons/iconset.svg#trash_compact"
                              ></use>
                            </svg>
                            <template
                              v-if="compactDeleteConfirmItemId === item.id"
                              >Delete? <strong>Yes</strong></template
                            >
                            <template v-else>Delete</template>
                          </div>
                          <!-- Burn NFT action (only for owner of MINTED NFTs) -->
                          <div
                            v-if="
                              isItemOwnedByCurrentUser(item) &&
                              isItemMinted(item)
                            "
                            class="menu-item delete-item"
                            @click.stop="
                              handleBurnNFT(item);
                              close();
                            "
                          >
                            <svg class="tinyImg">
                              <use
                                href="@/assets/icons/iconset.svg#trash_compact"
                              ></use>
                            </svg>
                            Burn NFT
                          </div>
                          <!-- Edit action with pencil icon (only for owner, NOT for minted NFTs) -->
                          <div
                            v-if="
                              isItemOwnedByCurrentUser(item) &&
                              !isItemMinted(item)
                            "
                            class="menu-item"
                            @click.stop="
                              editItem(item);
                              close();
                            "
                          >
                            <svg class="tinyImg">
                              <use
                                href="@/assets/icons/iconset.svg#pencil"
                              ></use>
                            </svg>
                            Edit
                          </div>
                          <!-- Mint NFT action with nft icon (only for owner if mintable) -->
                          <div
                            v-if="
                              isItemOwnedByCurrentUser(item) &&
                              canMintItem(item) &&
                              !isMultiImageItem(item)
                            "
                            class="menu-item"
                            @click.stop="
                              startMint(item);
                              close();
                            "
                          >
                            <svg class="tinyImg">
                              <use
                                href="@/assets/icons/iconset.svg#nft_compact"
                              ></use>
                            </svg>
                            {{
                              mintingStatus[item.id] ? "Minting…" : "Mint NFT"
                            }}
                          </div>
                          <!-- Batch Mint action for multi-image posts -->
                          <div
                            v-if="
                              isItemOwnedByCurrentUser(item) &&
                              canMintItem(item) &&
                              isMultiImageItem(item)
                            "
                            class="menu-item"
                            @click.stop="
                              startBatchMintFromPost(item);
                              close();
                            "
                          >
                            <svg class="tinyImg">
                              <use
                                href="@/assets/icons/iconset.svg#nft_compact"
                              ></use>
                            </svg>
                            Batch Mint ({{ getItemImageCount(item) }})
                          </div>
                          <!-- Favourites action with star icon -->
                          <div
                            v-if="authUserUid && isWishlistFeatureEnabled"
                            class="menu-item"
                            @click.stop="
                              toggleWishlist(item);
                              close();
                            "
                            :class="{ disabled: favouritesLoading[item.id] }"
                          >
                            <svg class="tinyImg" v-if="isWishlisted[item.id]">
                              <use
                                href="@/assets/icons/iconset.svg#star_filled_compact"
                              ></use>
                            </svg>
                            <svg class="tinyImg" v-else>
                              <use
                                href="@/assets/icons/iconset.svg#star_compact"
                              ></use>
                            </svg>
                            {{
                              isWishlisted[item.id]
                                ? "Unfavourite"
                                : "Favourite"
                            }}
                          </div>
                          <!-- Share actions -->
                          <div
                            v-if="authUserUid"
                            class="menu-item"
                            @click.stop="
                              copyItemLink(item);
                              close();
                            "
                          >
                            <svg class="tinyImg">
                              <use
                                href="@/assets/icons/iconset.svg#link-compact"
                              ></use>
                            </svg>
                            Copy link
                          </div>
                          <div
                            v-if="authUserUid"
                            class="menu-item"
                            @click.stop="
                              shareItem(item);
                              close();
                            "
                          >
                            <svg class="tinyImg">
                              <use
                                href="@/assets/icons/iconset.svg#message_compact"
                              ></use>
                            </svg>
                            Share in Messages
                          </div>
                        </div>
                      </template>
                    </Dropdown>
                  </template>
                  <template v-slot:clap-button>
                    <ClapButtonArea
                      v-if="authUserUid && item.type === 'media'"
                      :placement="resolveClapPlacement(item)"
                      :receiverUid="item.owner || null"
                    />
                  </template>
                  <template #info-footer>
                    <div
                      v-if="
                        item.type === 'product' ||
                        item.type === 'service' ||
                        item.type === 'nft' ||
                        item.isNFTListing
                      "
                      class="info-footer-row"
                    >
                      <!-- Price (left side) -->
                      <div class="price-inline">
                        <AuthorityListingPurchase
                          v-if="item._authorityPresentation"
                          :item="item"
                          :actor-uid="authUserUid"
                          @purchased="handleAuthorityPurchaseSettled"
                        />
                        <!-- NFT listing price (crypto) -->
                        <template
                          v-else-if="
                            authUserUid &&
                            (item.type === 'nft' || item.isNFTListing)
                          "
                        >
                          <UnitButton
                            v-if="item.listingPrice > 0"
                            @click="redirectToProductView(item)"
                            variant="secondary"
                            size="micro"
                            class="marketplace-price-button smallerClickableHeight"
                          >
                            {{ formatNumberTo4Digits(item.listingPrice) }}
                            {{ item.currency || item.coinSymbol || "ETH" }}
                          </UnitButton>
                          <UnitButton
                            v-else
                            @click="redirectToProductView(item)"
                            variant="secondary"
                            size="micro"
                            class="marketplace-price-button smallerClickableHeight"
                          >
                            Make Offer
                          </UnitButton>
                        </template>
                        <template
                          v-else-if="authUserUid && item.type === 'product'"
                        >
                          <CentTranslatedPrice
                            :isProductOwner="isItemOwnedByCurrentUser(item)"
                            :product="item"
                            @price-clicked="handlePriceClicked"
                            :compact="true"
                            buttonClass="marketplace-price-control"
                          />
                        </template>
                        <template
                          v-else-if="authUserUid && item.type === 'service'"
                        >
                          <CentTranslatedPrice
                            v-if="isItemOwnedByCurrentUser(item)"
                            :isProductOwner="true"
                            :product="item"
                            @price-clicked="handlePriceClicked"
                            :compact="true"
                            buttonClass="marketplace-price-control"
                          />
                          <!-- For other users' services -->
                          <template v-else>
                            <!-- Loading state -->
                            <UnitButton
                              v-if="favouritesLoading[`price_${item.id}`]"
                              variant="secondary"
                              size="micro"
                              class="marketplace-price-button smallerClickableHeight"
                              disabled
                              aria-label="Loading price"
                            >
                              <UnitSpinner :size="14" :thickness="2" />
                            </UnitButton>
                            <!-- Calculated price button -->
                            <UnitButton
                              v-else-if="
                                convertedPrices[item.id]?.convertedPrice !==
                                  null &&
                                convertedPrices[item.id]?.convertedPrice !==
                                  undefined
                              "
                              @click="redirectToProductView(item)"
                              variant="secondary"
                              size="micro"
                              class="marketplace-price-button smallerClickableHeight"
                            >
                              {{
                                formatNumberTo4Digits(
                                  convertedPrices[item.id].convertedPrice
                                )
                              }}
                              {{ convertedPrices[item.id].coinSymbol }}
                            </UnitButton>
                            <!-- Refresh button fallback -->
                            <UnitButton
                              v-else
                              variant="secondary"
                              size="micro"
                              class="marketplace-price-button smallerClickableHeight"
                              @click="fetchConvertedPrice(item)"
                            >
                              Refresh
                            </UnitButton>
                          </template>
                        </template>
                        <small
                          v-else
                          class="login-message actionText"
                          @click="toggleLoginModal"
                          >Login</small
                        >
                      </div>
                      <!-- Stock (right side) -->
                      <div class="stock-inline">
                        <small
                          v-if="
                            item.type === 'product' && item.existingStock === 0
                          "
                          class="soldout"
                          >Sold</small
                        >
                        <small
                          v-else-if="
                            item.type === 'product' && item.existingStock < 10
                          "
                          :class="{ stockWarningColor: item.existingStock < 2 }"
                        >
                          {{ item.existingStock }} left
                        </small>
                        <small
                          v-else-if="item.type === 'service'"
                          class="service-label"
                          >Service</small
                        >
                      </div>
                    </div>
                  </template>
                </ImageCard>
                <!-- For multimedia (P5 sketch) -->
                <P5Sketch
                  v-if="item && item.type === 'multimedia' && item.p5SketchUrl"
                  :sketchUrl="item.p5SketchUrl"
                  @mounted="console.log('Loaded Sketch:', item.p5SketchUrl)"
                />

                <!-- No external no-image fallback needed; ImageCard handles placeholder -->
              </div>
            </figure>
          </transition-group>
          <!-- Add skeleton loaders at the bottom of each column after real items -->
          <div v-if="isLoadingMoreItems" class="skeleton-item skeleton-loader">
            <div class="skeleton-image shimmer"></div>
            <div class="skeleton-details">
              <div class="skeleton-title shimmer"></div>
              <div class="skeleton-price shimmer"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <UnitButton
      v-if="showManualLoadMore"
      class="manual-load-more"
      variant="secondary"
      @click="loadMoreFromUserAction"
    >
      Load more
    </UnitButton>
    <Overlay
      v-if="showOverlay"
      :visible="showOverlay"
      :title="dynamicTitle"
      :smallTextAfterTitle="overlaySubtitle"
      :showBackArrow="shouldShowBackArrow"
      :currentStep="currentStep"
      :currentProduct="currentProduct"
      @close="closeOverlay"
      @previousStep="goToPreviousStep"
    >
      <template #before-buttons="{ product }">
        <AddProductForm
          v-if="
            !isFetching && currentProduct && currentProduct.type === 'product'
          "
          ref="addFormRef"
          :key="formKey"
          :currentProduct="currentProduct"
          :userUnitsOrg="userUnitsOrg || {}"
          :currentStep="currentStep"
          @productAddedSuccessfully="handleProductAdded"
          @update-title="handleTitleUpdate"
          @update-canPreview="canPreview = $event"
        />
        <AddServiceForm
          v-if="
            !isFetching && currentProduct && currentProduct.type === 'service'
          "
          ref="addFormRef"
          :key="formKey"
          :user="user || {}"
          :currentProduct="currentProduct"
          :userUnitsOrg="userUnitsOrg || {}"
          :currentStep="currentStep"
          @serviceAddedSuccessfully="handleServiceAdded"
          @update-title="handleTitleUpdate"
          @update-canPreview="canPreview = $event"
        />
        <PostComposer
          v-if="
            !isFetching && currentProduct && currentProduct.type === 'media'
          "
          ref="postComposerRef"
          :key="composerKey"
          :isProcessing="isSaveLoading"
          @update-canPreview="canPreview = $event"
          @next="handleComposerNext"
          @submit="handleComposerSubmit"
          @cancel="closeOverlay"
          @close="closeOverlay"
        />
        <LoadingState
          v-if="isFetching"
          variant="section"
          message="Loading posts…"
        />
      </template>
      <!-- Hide the entire button row for media edits — PostComposer handles its own controls -->
      <div v-if="!isMediaEdit" class="row alignCentreRow">
        <!-- Delete button (not for minted NFTs, not for media edit) -->
        <UnitButton
          variant="secondary"
          type="button"
          @click="currentStep = 'delete'"
          v-if="
            !isMediaEdit &&
            currentStep !== 'delete' &&
            currentStep !== 'burn' &&
            currentStep !== 'preview' &&
            !isCurrentProductMinted
          "
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#trash_compact"></use>
          </svg>
          Delete item
        </UnitButton>

        <!-- Go back from delete -->
        <UnitButton
          variant="secondary"
          @click="currentStep = 'initial'"
          v-if="!isMediaEdit && !isSaveLoading && currentStep == 'delete'"
          v-haptic
        >
          Go back
        </UnitButton>
        <!-- Confirm delete -->
        <UnitButton
          variant="danger"
          type="button"
          @click="confirmDelete"
          v-if="!isMediaEdit && currentStep == 'delete' && !isSaveLoading"
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#trash_compact"></use>
          </svg>
          Yes, delete
        </UnitButton>

        <!-- Burn NFT button (only for minted NFTs, not for media edit) -->
        <UnitButton
          variant="danger"
          class="delete-item"
          type="button"
          @click="currentStep = 'burn'"
          v-if="
            !isMediaEdit &&
            currentStep !== 'burn' &&
            currentStep !== 'delete' &&
            currentStep !== 'preview' &&
            isCurrentProductMinted
          "
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#trash_compact"></use>
          </svg>
          Burn NFT
        </UnitButton>

        <!-- Go back from burn -->
        <UnitButton
          variant="secondary"
          @click="currentStep = 'initial'"
          v-if="!isMediaEdit && !isSaveLoading && currentStep == 'burn'"
          v-haptic
        >
          Go back
        </UnitButton>
        <!-- Confirm burn -->
        <UnitButton
          variant="danger"
          type="button"
          class="delete-item"
          @click="executeBurn"
          v-if="!isMediaEdit && currentStep == 'burn' && !isSaveLoading"
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#trash_compact"></use>
          </svg>
          Yes, burn forever
        </UnitButton>

        <!-- Cancel button (not for delete/burn steps, not for media edit) -->
        <UnitButton
          variant="secondary"
          @click="closeOverlay"
          v-if="
            !isMediaEdit &&
            !isSaveLoading &&
            currentStep !== 'delete' &&
            currentStep !== 'burn' &&
            !isCurrentProductMinted
          "
          v-haptic
        >
          Cancel
        </UnitButton>
        <!-- Cancel button for minted NFTs (not in burn step, not for media edit) -->
        <UnitButton
          variant="secondary"
          @click="closeOverlay"
          v-if="
            !isMediaEdit &&
            !isSaveLoading &&
            currentStep !== 'burn' &&
            isCurrentProductMinted
          "
          v-haptic
        >
          Cancel
        </UnitButton>
        <UnitButton
          v-if="
            !isMediaEdit &&
            !canPreview &&
            currentStep !== 'preview' &&
            currentStep !== 'delete' &&
            currentStep !== 'burn'
          "
          @click="validateForm"
        >
          Next
        </UnitButton>
        <UnitButton
          @click="goToNextStep"
          v-if="
            !isMediaEdit &&
            canPreview &&
            !isSaveLoading &&
            currentStep !== 'preview' &&
            currentStep !== 'delete'
          "
          v-haptic
        >
          Next
        </UnitButton>

        <UnitButton
          @click="saveItem"
          v-if="!isMediaEdit && currentStep === 'preview' && !isSaveLoading"
          :disabled="isSaveLoading"
          v-haptic
        >
          Save
        </UnitButton>
        <UnitButton
          v-if="!isMediaEdit && isSaveLoading"
          loading
          loading-label="Processing..."
        >
          Save
        </UnitButton>
      </div>
    </Overlay>

    <!-- Simplified Mint Modal -->
    <SimpleMintModal
      v-if="showMintModal"
      :visible="showMintModal"
      :listingTitle="mintModalListingTitle"
      :previewImage="mintModalPreviewImage"
      :mediaUrl="pendingMint?.mediaUrl"
      :mediaType="pendingMint?.mediaType"
      :loading="mintModalLoading"
      :defaultRoyaltyBps="mintModalDefaultRoyaltyBps"
      @close="handleMintModalClose"
      @confirm="handleSimpleMintConfirm"
    />

    <!-- Batch Mint Modal -->
    <BatchMintModal
      v-if="showBatchMintModal"
      :visible="showBatchMintModal"
      :sourceItem="pendingBatchMintItem"
      @close="closeBatchMintModal"
      @success="handleBatchMintSuccess"
    />

    <!-- Image Slideshow Overlay -->
    <ImageSlideshowOverlay
      v-if="slideshowOverlayVisible"
      :visible="slideshowOverlayVisible"
      :item="slideshowItem"
      :can-navigate-previous="canNavigateSlideshowPrevious"
      :can-navigate-next="canNavigateSlideshowNext"
      :item-navigation-loading="isLoadingSlideshowNextItem"
      @close="closeLocalOverlay"
      @edit="handleSlideshowEdit"
      @delete="handleSlideshowDelete"
      @mint="handleSlideshowMint"
      @favouritesUpdated="handleSlideshowFavouritesUpdated"
      @purchase-settled="handleAuthorityPurchaseSettled"
      @next="handleSlideshowNext"
      @prev="handleSlideshowPrev"
    />
  </div>
</template>

<script>
/**
 * MasonryMarketplace Component
 *
 * BASIC IMPLEMENTATION - Current complex sorting and filtering implementation
 *
 * This component currently uses complex multi-collection fetching and client-side
 * sorting/filtering. It will be enhanced to use the ItemsTimeline collection for
 * optimized chronological loading when no filters are active.
 *
 * Strategy:
 * - Default: Will use ItemsTimeline for fast chronological display (future)
 * - Filtered: Uses current complex sorting logic (this implementation)
 * - Maintains all existing functionality for backward compatibility
 */

import {
  ref,
  computed,
  reactive,
  onMounted,
  onActivated,
  watch,
  watchEffect,
  onBeforeUnmount,
  onDeactivated,
  nextTick,
  defineAsyncComponent,
} from "vue";
import { useStore } from "vuex";
import { isServerFeedPresentation } from "@/utils/feedPresentation";
import {
  legacyFirestoreBatch as writeBatch,
  legacyFirestoreCollection as collection,
  legacyFirestoreDelete as deleteDoc,
  legacyFirestoreDocument as doc,
  legacyFirestoreRead as getDoc,
  legacyFirestoreRead as getDocs,
  legacyFirestoreServerTimestamp as serverTimestamp,
  legacyFirestoreWrite as setDoc,
} from "@/services/legacyMarketplaceDataBoundary";
import { auth } from "@/firebase";
import CentTranslatedPrice from "@/components/DashboardComponents/EcosystemComponents/ProductListComponents/CentTranslatedPrice.vue";

import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { getConvertedPrice } from "@/utils/priceUtils";
import { formatNumberTo4Digits } from "@/utils/numberFormattingTo4digits";
import { smartTitleCase } from "@/utils/smartTitleCase";
import { resolveClapPlacement } from "@/utils/transferClapMoney";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import UnitSpinner from "@/components/GenericComponents/UnitSpinner.vue";
import { useMarketplaceMinting } from "@/composables/useMarketplaceMinting";
import { fetchItemById } from "@/composables/useItemFetch";
import { afterBrowserFrame, beginBootStep } from "@/utils/bootDiagnostics";
import { withUiDeadline } from "@/utils/uiDeadline";
import {
  canHandleFeedDocumentScroll,
  isCurrentFeedRequest,
  isNearDocumentBottom,
  readDocumentScrollMetrics,
  resolveFeedActivationAction,
  resolveTimelinePageState,
} from "@/utils/feedPagination";
import {
  closeItemOverlayRoute,
  findAdjacentFeedItem,
  mergeFeedNavigationContext,
  openItemOverlayRoute,
  replaceItemOverlayRoute,
  restoreRoundRobinFeedOrder,
} from "@/utils/overlayNavigation";
import { preloadAdjacentOverlayItems } from "@/utils/overlayMediaPreload";
import {
  isFollowingFeedFeatureEnabled,
  isItemAuthorityReadsFeatureEnabled,
  isOrgContextFeatureEnabled,
  isWishlistFeatureEnabled,
} from "@/config/runtimeConfig";
import { listContentSubscriptions } from "@/services/contentSubscriptionService";
import { createMarketplaceItemDeletionService } from "@/services/marketplaceItemDeletionService";
import { createMarketplaceFeedSource } from "@/services/marketplaceFeedSource";
import { itemMediaUrlResolver } from "@/services/itemMediaUrlResolver";
import {
  buildFeedCacheKey,
  shouldForceOrganisationFeedScope,
} from "@/utils/feedContext";

// BASIC IMPLEMENTATION - Import new timeline fetcher for optimized loading
import {
  shouldUseTimeline,
  normalizeTimelineItems,
} from "@/utils/timelineFetcher";

import LoadingState from "@/components/GenericComponents/LoadingState.vue";
import SingleSelectToggleRow from "@/components/GenericComponents/SingleSelectToggleRow.vue";
import OrgContextPicker from "@/components/OrgComponents/OrgContextPicker.vue";
import FiltersComponent from "@/components/DashboardComponents/EcosystemComponents/MasonryMarketplaceComponents/FiltersComponent.vue";
import ImageCard from "@/components/DashboardComponents/EcosystemComponents/MasonryMarketplaceComponents/ImageCard.vue";
import Dropdown from "@/components/GenericComponents/Dropdown.vue";
import { nftConfig } from "@/config/nft";

// Lazy-load heavier interactive components to trim the above-the-fold bundle
const ClapButtonArea = defineAsyncComponent(() =>
  import(
    "@/components/DashboardComponents/EcosystemComponents/ProductListComponents/ClapButtonArea.vue"
  )
);
const P5Sketch = defineAsyncComponent(() =>
  import(
    "@/components/DashboardComponents/EcosystemComponents/OddFormats/P5Sketch.vue"
  )
);
const AddProductForm = defineAsyncComponent(() =>
  import("@/components/DashboardComponents/EcosystemComponents/AddProductForm.vue")
);
const AddServiceForm = defineAsyncComponent(() =>
  import("@/components/DashboardComponents/EcosystemComponents/AddServiceForm.vue")
);
const AuthorityListingPurchase = defineAsyncComponent(() =>
  import("@/components/DashboardComponents/EcosystemComponents/AuthorityListingPurchase.vue")
);
const PostComposer = defineAsyncComponent(() =>
  import("@/components/PostComposer/PostComposer.vue")
);
const Overlay = defineAsyncComponent(() =>
  import("@/components/GenericComponents/Overlay.vue")
);
const ImageSlideshowOverlay = defineAsyncComponent(() =>
  import("@/components/GenericComponents/ImageSlideshowOverlay.vue")
);
const SimpleMintModal = defineAsyncComponent(() =>
  import("@/components/GenericComponents/SimpleMintModal.vue")
);
const BatchMintModal = defineAsyncComponent(() =>
  import("@/components/GenericComponents/BatchMintModal.vue")
);

export default {
  name: "MasonryMarketplace",
  components: {
    CentTranslatedPrice,
    AddProductForm,
    AddServiceForm,
    AuthorityListingPurchase,
    PostComposer,
    Overlay,
    ImageSlideshowOverlay,
    LoadingState,
    FiltersComponent,
    P5Sketch,
    ImageCard,
    ClapButtonArea,
    SimpleMintModal,
    BatchMintModal,
    Dropdown,
    UnitButton,
    UnitSpinner,
    SingleSelectToggleRow,
    OrgContextPicker,
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    services: {
      type: Array,
      default: () => [],
    },
    multimediaPosts: { type: Array, default: () => [] },
    showItems: {
      type: String,
      default: "allUsers",
    },
    currentUserOrgUid: String,
    profileUserUid: String,
    buyerOrgUID: String,
    organisationUid: String,
    showSellingAction: {
      type: Boolean,
      default: false,
    },
    showAboveGridSeparator: {
      type: Boolean,
      default: true,
    },
    hideFeedToggle: {
      type: Boolean,
      default: false,
    },
    publicOrganisationView: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, expose }) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const marketplaceItemDeletion = createMarketplaceItemDeletionService();
    const privateAwareFeedSource = createMarketplaceFeedSource();
    // Track navbar visibility so the mobile toolbar can mirror its hide/show
    const navbarVisible = computed(() => store.state.navbarVisible);
    const hasInitialItems = ref(false);
    const searchQuery = ref("");
    // Unified default filter list (keep in sync across reset & detection logic)
    const DEFAULT_FILTER_TYPES =
      "product,service,nft,media_image,media_video,media_audio,media_text,docs"; // docs covers PDFs / document media; media_text covers text/poster/tweet posts
    // A blank initial value means no stored preference; the filter control will
    // activate every supported type. __none__ is an intentional empty choice.
    const filterType = ref("");
    // Chain filter: default to Ethereum only; FiltersComponent can toggle more
    const chainFilter = ref(["0x1"]);
    const isDefaultFilterState = (val) =>
      !val || val === DEFAULT_FILTER_TYPES;
    const sortOrder = ref("timestamp_desc"); // Display newest items first (last added)
    // Helpers for title display in masonry band

    const sanitizeTitle = (raw) => {
      const t = String(raw || "").trim();
      if (!t) return "";
      const lower = t.toLowerCase();
      const placeholders = [
        "edit title",
        "give your post a title",
        "add a title",
        "post title",
      ];
      if (placeholders.some((p) => lower === p || lower.startsWith(p))) {
        return "";
      }
      return t;
    };
    const getDisplayTitle = (item) =>
      smartTitleCase(sanitizeTitle(item?.itemName || item?.title || ""));
    const hasRealTitle = (item) => !!getDisplayTitle(item);
    const hasImages = (item) => {
      try {
        if (Array.isArray(item?.images) && item.images.length > 0) return true;
        if (item?.thumbnail) return true;
        if (item?.mediaUrl) return true;
        // Embed posts (YouTube/SoundCloud) have visual content but no images array
        if (item?.embedHtml || item?.mediaType === "embed") return true;
        // Audio items have visual content (the player)
        if (item?.mediaType === "audio") return true;
      } catch {}
      return false;
    };

    // Check if item is a multi-image post (2+ images, not video/audio/documents)
    const isMultiImageItem = (item) => {
      if (!item) return false;
      // Skip non-image types
      const mt = item.mediaType;
      if (mt === "video" || mt === "audio" || mt === "documents") return false;
      // Check images array length
      const imgs = Array.isArray(item.images) ? item.images : [];
      // Filter out video files
      const imageUrls = imgs.filter(
        (u) => u && !/(\.(mp4|webm|mov|m4v))(\?|$)/i.test(u)
      );
      return imageUrls.length > 1;
    };

    // Get count of images in item (excluding video files)
    const getItemImageCount = (item) => {
      if (!item) return 0;
      const imgs = Array.isArray(item.images) ? item.images : [];
      return imgs.filter((u) => u && !/(\.(mp4|webm|mov|m4v))(\?|$)/i.test(u))
        .length;
    };

    const truncateTitleForBand = (itemName) => {
      if (!itemName) return "";
      const t = String(itemName).trim();
      if (t.length <= 139) return t;
      return t.substring(0, 139).trimEnd() + "…";
    };
    const itemsToLoad = ref(16); // Initial batch size

    // Timeline-only mode: legacy readiness flags removed
    const isOrgUIDLoaded = ref(false);

    // Log the initial filter state for debugging
    console.log("[MasonryMarketplace] Initial filter state:", {
      filterType: filterType.value,
      sortOrder: sortOrder.value,
      itemsToLoad: itemsToLoad.value,
    });
    const authUserUid = computed(
      () =>
        store.getters.userCredentials?.data?.uid ||
        store.getters.user?.data?.uid ||
        auth.currentUser?.uid ||
        null
    );
    const isWishlisted = reactive({});
    let wishlistRequestGeneration = 0;
    const convertedPrices = reactive({});
    const compactDeleteConfirmItemId = ref(null);
    let compactDeleteConfirmTimer = null;
    const compactUnlistConfirmItemId = ref(null);
    let compactUnlistConfirmTimer = null;
    const isFetching = ref(false);
    const isLoadingMoreItems = ref(false);
    const isInitialLoading = ref(true); // Add a new reactive state variable for initial loading
    // Guard to ensure only one initial fetch/flow runs
    const isInitializing = ref(true);
    // Force this component to operate in timeline-only rendering mode.
    // Used to guard legacy props/store-driven paths to avoid dual sources of truth.
    const isTimelineOnly = ref(true);
    // Track timeline fetch errors to show a retry UI
    const timelineError = ref(null);
    const timelineGeneration = ref(0);
    const lastTimelineRefreshAt = ref(0);
    const currentProduct = ref({});
    const showOverlay = ref(false);
    const currentStep = ref("initial");
    const dynamicTitle = ref("Edit product details");

    // Computed subtitle for overlay based on current step
    const overlaySubtitle = computed(() => {
      if (currentStep.value === "delete") {
        return "All information related to this item will be permanently deleted.";
      }
      if (currentStep.value === "burn") {
        return "This NFT will be permanently removed from the blockchain.";
      }
      return "You can change this later";
    });

    // Image slideshow overlay state
    const slideshowOverlayVisible = ref(false);
    const slideshowItem = ref(null);
    const slideshowNavigationItems = ref([]);
    const isLoadingSlideshowNextItem = ref(false);
    // Batch mint modal state
    const showBatchMintModal = ref(false);
    // Pending batch mint from existing post (multi-image)
    const pendingBatchMintItem = ref(null);

    // Start batch mint from an existing multi-image post
    const startBatchMintFromPost = (item) => {
      if (!item || !isMultiImageItem(item)) {
        console.warn(
          "[MasonryMarketplace] Cannot batch mint: not a multi-image item"
        );
        return;
      }
      pendingBatchMintItem.value = item;
      showBatchMintModal.value = true;
    };

    // Close batch mint modal and reset state
    const closeBatchMintModal = () => {
      showBatchMintModal.value = false;
      pendingBatchMintItem.value = null;
    };

    // Batch mint selection state (Set of item IDs)
    const batchMintSelectedItems = ref(new Set());

    // Check if item is selectable for batch mint (multi-image, owned, not yet minted)
    const isItemSelectableForBatchMint = (item) => {
      return (
        isItemOwnedByCurrentUser(item) &&
        canMintItem(item) &&
        isMultiImageItem(item)
      );
    };

    // Handle selection change from ImageCard
    const handleBatchMintSelectionChange = ({ item, selected }) => {
      const newSet = new Set(batchMintSelectedItems.value);
      if (selected) {
        newSet.add(item.id);
      } else {
        newSet.delete(item.id);
      }
      batchMintSelectedItems.value = newSet;

      // If item is selected, open batch mint modal for that item
      if (selected && item) {
        startBatchMintFromPost(item);
        // Clear selection after starting modal
        batchMintSelectedItems.value = new Set();
      }
    };

    // Force a fresh form instance per edit session
    const formKey = ref(0);

    // Location filter state
    const locationFilter = ref({
      active: false,
      coordinates: null,
      radius: 10,
      cityName: "",
    });

    // Handle location filter updates from the LocationFilterComponent
    const handleLocationFilterUpdate = (filter) => {
      console.log("[Location] Filter updated:", filter);
      locationFilter.value = filter;
    };

    // Calculate distance between two coordinates using the Haversine formula
    const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371; // Earth radius in km
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLon = ((lon2 - lon1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c; // Distance in km
    };

    // Legacy props/media fetching removed in timeline-only mode

    const isCurrentUserOrg = computed(() => {
      return props.organisationUid === props.currentUserOrgUid;
    });

    const categoryMapping = {
      Baby: "Home & Living",
      "Arts and Collectibles": "Arts & Culture",
      "Toys and Games": "Sports & Outdoors",
      "Health and Beauty": "Health & Wellness",
      "Pet Supplies": "Home & Living",
      "Toys and Hobbies": "Sports & Outdoors",
      "Business and Industry": "Business & Industrial",
      "Electronics & Tech Goods": "Electronics & Technology",
      "Fashion & Accessories": "Fashion & Beauty",
      "Arts & Entertainment": "Arts & Culture",
      "Other/Specialty": "Other",
      "Healthcare and Wellness": "Health & Wellness",
      "Retail and Wholesale": "Business & Industrial",
      "Education, Training, and Coaching": "Education",
      "Hospitality, Events, and Entertainment": "Services",
      "Business and Professional": "Business & Industrial",
      "Finance and Banking": "Business & Industrial",
      "Transportation and Logistics": "Transportation",
      "Government and Security": "Services",
      "Technology and Digital": "Electronics & Technology",
      "Design, and Media": "Arts & Culture",
      "Home and Personal Services": "Services",
      "Environmental and Utilities": "Services",
      "Real Estate and Property": "Home & Living",
      "Agriculture and Fisheries": "Business & Industrial",
      "Animal Care and Services": "Services",
      "Other Services": "Services",
    };

    const organisationUid = computed(() => {
      return isCurrentUserOrg.value
        ? props.organisationUid
        : props.organisationUid;
    });
    const categories = reactive([
      {
        name: "Technology",
        type: "both", // 'product', 'service', or 'both'
        conditions: [
          "New",
          "Open Box",
          "Refurbished",
          "Used",
          "For Parts/Not Working",
        ],
      },
      {
        name: "Fashion & Beauty",
        type: "both",
        conditions: [
          "New with Tags",
          "New without Tags",
          "Used",
          "Like New",
          "Good",
          "Acceptable",
        ],
      },
      {
        name: "Home & Living",
        type: "both",
        conditions: [
          "New",
          "Used",
          "Refurbished",
          "Like New",
          "Good",
          "Acceptable",
        ],
      },
      {
        name: "Arts & Culture",
        type: "both",
        conditions: ["New", "Used", "Collectible", "Digital Goods"],
      },
      {
        name: "Business",
        type: "both",
        conditions: ["New", "Used", "Refurbished"],
      },
      {
        name: "Health & Wellness",
        type: "both",
        conditions: ["New", "Open Box", "Used", "For Parts/Not Working"],
      },
      {
        name: "Sports & Outdoors",
        type: "both",
        conditions: ["New", "Used", "Refurbished"],
      },
      {
        name: "Transportation",
        type: "both",
        conditions: ["New", "Used", "Refurbished", "For Parts/Not Working"],
      },
      {
        name: "Education",
        type: "service",
        // No conditions for services
      },
      {
        name: "Services",
        type: "service",
        // A general category for other services
      },
      {
        name: "Other",
        type: "both",
        conditions: ["New", "Used", "Refurbished"],
      },
    ]);

    const updateCategoryNames = async () => {
      // Define the mapping from old category names to new category names
      const categoryNameMapping = {
        "Electronics & Technology": "Technology",
        "Arts & Entertainment": "Arts & Culture",
        "Business & Industrial": "Business",
        "Automotive & Transportation": "Transportation",
        "Education & Training": "Education",
        // The rest remain the same
        "Fashion & Beauty": "Fashion & Beauty",
        "Home & Living": "Home & Living",
        "Health & Wellness": "Health & Wellness",
        "Sports & Outdoors": "Sports & Outdoors",
        Services: "Services",
        Other: "Other",
      };

      // Confirm the action with the user
      const confirmed = confirm(
        "This action will update the category names of all items in the database. Do you want to proceed?"
      );

      if (!confirmed) {
        return;
      }

      try {
        // Fetch all products and services
        const productsSnapshot = await getDocs(collection(null, "products"));
        const servicesSnapshot = await getDocs(collection(null, "services"));

        // Combine products and services (legacy removed); use fetched snapshots directly below

        // Prepare batch updates
        const batchSize = 500; // Firestore batch limit
        let batch = writeBatch(null);
        let counter = 0;

        for (const docSnap of [
          ...productsSnapshot.docs,
          ...servicesSnapshot.docs,
        ]) {
          const data = docSnap.data();
          const oldCategory = data.category;

          if (oldCategory && categoryNameMapping[oldCategory]) {
            const newCategory = categoryNameMapping[oldCategory];
            batch.update(docSnap.ref, { category: newCategory });
            counter++;

            // Commit batch if batchSize is reached
            if (counter % batchSize === 0) {
              await batch.commit();
              batch = writeBatch(null);
              console.log(`${counter} documents updated so far...`);
            }
          }
        }

        // Commit any remaining batch operations
        if (counter % batchSize !== 0) {
          await batch.commit();
        }

        alert(
          `Finished updating categories. Total documents updated: ${counter}`
        );

        // Refresh items to reflect changes
        // Optionally trigger a UI refresh: in timeline-only mode this is not required
      } catch (error) {
        console.error("Error updating categories:", error);
        alert("An error occurred while updating categories.");
      }
    };
    // refreshItems removed in timeline-only mode

    const selectedCategory = ref("");

    const buyerOrgUID = computed(() => {
      return isCurrentUserOrg.value ? props.organisationUid : props.buyerOrgUID;
    });
    const shouldShowBackArrow = computed(() => {
      // Show back arrow for any step except "initial"
      // This ensures it shows for "availability", "customization", "preview", and "delete" steps
      return currentStep.value !== "initial";
    });
    const isMediaEdit = computed(
      () => currentProduct.value && currentProduct.value.type === "media"
    );
    const isCurrentProductMinted = computed(() =>
      currentProduct.value ? isItemMinted(currentProduct.value) : false
    );
    const isSaveLoading = ref(false);
    const canPreview = ref(false);

    const addFormRef = ref(null);
    const postComposerRef = ref(null);
    const composerKey = ref(0);

    const user = ref(null);
    const userUnitsOrg = ref({});

    const columns = ref([]);
    const processedItems = ref([]);
    // Legacy layout trigger flag retained for compatibility with existing calls
    const forceRedistribute = ref(false);

    const {
      mintingStatus,
      mintedCache,
      showMintModal,
      pendingMint,
      mintModalAcknowledged,
      mintModalLoading,
      hasMintFeeConfigured,
      mintFeeEthDisplay,
      canMintItem,
      isItemMinted,
      isItemOwnedByCurrentUser,
      startMint,
      confirmMint,
      cancelMint,
      setPendingRoyaltyBps,
      setSaleConfig,
      burnNFT,
    } = useMarketplaceMinting({ currentUserUid: authUserUid, processedItems });

    const mintModalRoyaltyBps = ref(nftConfig.defaultRoyaltyBps || 0);
    const mintModalDefaultRoyaltyBps = computed(
      () => nftConfig.defaultRoyaltyBps || 0
    );
    const mintModalMaxRoyaltyBps = computed(() => nftConfig.maxRoyaltyBps || 0);

    // Chain selection state (default to Base)
    const mintModalSelectedChain = ref("0x2105");
    const isSwitchingChain = ref(false); // Guard to prevent multiple switch requests

    // Draft mode state - for NFT mint from PostComposer (deferred save)
    const pendingDraftMint = ref(null); // { composerRef, ownerId, organisationId, draft }

    watch(
      () => pendingMint.value,
      (next) => {
        if (next && typeof next.royaltyBps === "number") {
          mintModalRoyaltyBps.value = next.royaltyBps;
        } else {
          mintModalRoyaltyBps.value = mintModalDefaultRoyaltyBps.value;
        }
      },
      { immediate: true }
    );

    const mintModalTitle = computed(() => {
      if (!pendingMint.value) {
        return "Confirm Mint Details";
      }

      const target =
        pendingMint.value.combined || pendingMint.value.item || null;
      const displayTitle = target ? getDisplayTitle(target) : "";

      // truncate to max N characters
      const truncate = (str, n = 20) =>
        str.length > n ? str.slice(0, n) + "…" : str;

      return displayTitle
        ? `Confirm mint: ${truncate(displayTitle, 30)}`
        : "Confirm Mint Details";
    });

    const mintModalListingTitle = computed(() => {
      // Draft mode: use draft title
      if (pendingDraftMint.value?.draft?.title) {
        return pendingDraftMint.value.draft.title;
      }
      if (!pendingMint.value) return "";
      const target =
        pendingMint.value.combined || pendingMint.value.item || null;
      return target ? getDisplayTitle(target) : "";
    });

    const mintModalFeeText = computed(() => {
      if (pendingMint.value?.mintFeeEthText) {
        return pendingMint.value.mintFeeEthText;
      }
      return hasMintFeeConfigured.value ? mintFeeEthDisplay.value : "0";
    });

    const mintModalTreasuryAddress = computed(() => {
      return pendingMint.value?.treasuryAddress || "";
    });

    const mintModalPreviewImage = computed(() => {
      // Draft mode: use draft preview image (blob URL)
      if (pendingDraftMint.value?.draft?.previewImage) {
        return pendingDraftMint.value.draft.previewImage;
      }
      return pendingMint.value?.previewImage || "";
    });

    const mintModalCreatorWallet = computed(() => {
      return (
        pendingMint.value?.creatorWallet ||
        pendingMint.value?.metadata?.properties?.creatorWallet ||
        pendingMint.value?.metadata?.creatorWallet ||
        ""
      );
    });

    const mintModalMetadata = computed(() => {
      return pendingMint.value?.metadata || {};
    });

    const handleMintModalClose = () => {
      // Clear draft mode if active
      if (pendingDraftMint.value) {
        console.log("[Masonry] Draft mint cancelled");
        pendingDraftMint.value = null;
        showMintModal.value = false;
        return;
      }
      if (pendingMint.value) {
        toast.info("Mint cancelled — terms not accepted.");
      }
      cancelMint();
    };

    const handleMintModalConfirm = async () => {
      // Prevent multiple simultaneous calls
      if (isSwitchingChain.value) {
        console.log(
          "[MasonryMarketplace] Chain switch already in progress, ignoring duplicate call"
        );
        return;
      }

      isSwitchingChain.value = true;

      // Switch to selected chain before minting
      const targetChain = mintModalSelectedChain.value;

      try {
        // Resolve active wallet provider (WalletConnect session or MetaMask)
        let eip1193Provider = null;
        try {
          const { default: WalletConnectAdapter } = await import(
            "@/services/externalWallet/WalletConnectAdapter"
          );
          const wcAdapter = new WalletConnectAdapter();
          const ok = await wcAdapter.initialize();
          if (
            ok &&
            wcAdapter.hasPersistedSession() &&
            wcAdapter.provider &&
            wcAdapter.accounts?.length
          ) {
            eip1193Provider = wcAdapter.provider;
          }
        } catch (wcErr) {
          console.warn(
            "[MasonryMarketplace] WalletConnect session restore failed",
            wcErr
          );
        }
        if (!eip1193Provider && window.ethereum) {
          eip1193Provider = window.ethereum;
        }
        if (!eip1193Provider) {
          toast.error("No wallet detected. Connect a wallet to continue.");
          isSwitchingChain.value = false;
          return;
        }

        // Get current chain
        let rawChainId = await eip1193Provider.request({
          method: "eth_chainId",
        });
        let currentChainId = (
          typeof rawChainId === "number"
            ? "0x" + rawChainId.toString(16)
            : rawChainId
        )?.toLowerCase();
        const normalizedTargetChain = targetChain?.toLowerCase();

        console.log(
          `[MasonryMarketplace] Current chain: ${currentChainId}, Target chain: ${normalizedTargetChain}`
        );

        // If not on selected chain, request switch
        if (currentChainId !== normalizedTargetChain) {
          console.log(
            `[MasonryMarketplace] Switching from ${currentChainId} to ${normalizedTargetChain}`
          );

          try {
            await eip1193Provider.request({
              method: "wallet_switchEthereumChain",
              params: [{ chainId: normalizedTargetChain }],
            });

            console.log(
              `[MasonryMarketplace] Chain switch completed successfully`
            );

            // Wait a moment for the chain switch to fully propagate
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Verify the chain actually changed
            const verifyChainId = await eip1193Provider.request({
              method: "eth_chainId",
            });
            const normalizedVerify = (
              typeof verifyChainId === "number"
                ? "0x" + verifyChainId.toString(16)
                : verifyChainId
            )?.toLowerCase();
            console.log(
              `[MasonryMarketplace] Verified current chain after switch: ${normalizedVerify}`
            );

            if (normalizedVerify !== normalizedTargetChain) {
              toast.error(
                `Chain switch failed. Expected ${normalizedTargetChain} but got ${normalizedVerify}`
              );
              return;
            }

            toast.info(
              `Switched to ${
                normalizedTargetChain === "0x1" ? "Ethereum" : "Base"
              }. Proceeding with mint...`
            );
          } catch (switchError) {
            // This error code indicates that the chain has not been added to MetaMask
            if (switchError.code === 4902) {
              toast.error("Please add this network to your wallet first.");
              isSwitchingChain.value = false;
              return;
            }
            // User rejected the request
            if (switchError.code === 4001) {
              toast.info("Chain switch cancelled. Mint aborted.");
              isSwitchingChain.value = false;
              return;
            }
            // Request already pending (user clicked multiple times)
            if (switchError.code === -32002) {
              console.log(
                "[MasonryMarketplace] Switch request already pending, waiting..."
              );
              toast.info(
                "Please check your wallet — a request is already pending."
              );
              isSwitchingChain.value = false;
              return;
            }
            console.error(
              "[MasonryMarketplace] Error switching chain:",
              switchError
            );
            toast.error("Failed to switch network. Please try again.");
            isSwitchingChain.value = false;
            return;
          }
        } else {
          console.log(
            `[MasonryMarketplace] Already on target chain ${normalizedTargetChain}, proceeding with mint...`
          );
        }

        // Proceed with mint on selected chain (after switch is complete)
        confirmMint(eip1193Provider);
      } catch (error) {
        console.error("[MasonryMarketplace] Error switching chain:", error);
        if (error.code === 4001) {
          toast.info("Chain switch cancelled. Mint aborted.");
        } else {
          toast.error("Failed to switch network. Please try again.");
        }
      } finally {
        // Always reset the switching flag
        isSwitchingChain.value = false;
      }
    };

    const updateMintModalAcknowledged = (value) => {
      mintModalAcknowledged.value = value;
    };

    const updateMintModalRoyalty = (value) => {
      const applied = setPendingRoyaltyBps(value);
      mintModalRoyaltyBps.value = applied;
    };

    const updateMintModalSelectedChain = (chainId) => {
      mintModalSelectedChain.value = chainId;
      console.log("[MasonryMarketplace] Selected chain for minting:", chainId);
    };

    /**
     * Handle confirmation from SimpleMintModal.
     * Sets sale configuration (if enabled) and proceeds with mint.
     * In draft mode: saves media first, then proceeds with mint.
     */
    const handleSimpleMintConfirm = async (config) => {
      // DRAFT MODE: Save media first, then proceed with mint
      if (pendingDraftMint.value) {
        console.log("[Masonry] Draft mode confirm - saving media now");
        const { composerRef, ownerId, organisationId } = pendingDraftMint.value;

        if (!composerRef) {
          window.$toast?.error?.("Cannot mint: composer reference missing");
          pendingDraftMint.value = null;
          showMintModal.value = false;
          return;
        }

        mintModalLoading.value = true;
        window.$toast?.info?.("Saving media...");

        try {
          // Save the media using the composer ref
          const result = await composerRef.save({
            owner: ownerId,
            organisation: organisationId,
          });

          console.log("[Masonry] Draft saved, result:", result);

          if (!result?.id) {
            console.warn("[Masonry] Save returned no id");
            window.$toast?.error?.("Failed to save media. Please try again.");
            mintModalLoading.value = false;
            pendingDraftMint.value = null;
            showMintModal.value = false;
            return;
          }

          // Clear draft mode
          pendingDraftMint.value = null;

          // Refresh items to get the newly saved one
          await requestTimelineFetch(true);

          // Find the saved item
          let item = processedItems.value.find((i) => i.id === result.id);

          // Fallback: fetch directly from itemsTimeline
          if (!item) {
            console.log(
              "[Masonry] Item not in processedItems, fetching from itemsTimeline"
            );
            const itemDoc = await getDoc(doc(null, "itemsTimeline", result.id));
            if (itemDoc.exists()) {
              item = { id: itemDoc.id, ...itemDoc.data() };
              console.log(
                "[Masonry] Fetched item from itemsTimeline:",
                item.id
              );
            }
          }

          // Second fallback: check legacy collections
          if (
            !item &&
            result.collection &&
            result.collection !== "itemsTimeline"
          ) {
            console.log(
              "[Masonry] Checking legacy collection:",
              result.collection
            );
            const legacyDoc = await getDoc(
              doc(null, result.collection, result.id)
            );
            if (legacyDoc.exists()) {
              item = { id: legacyDoc.id, ...legacyDoc.data() };
              console.log(
                "[Masonry] Fetched item from legacy collection:",
                item.id
              );
            }
          }

          if (!item) {
            console.warn("[Masonry] Could not find saved item:", result.id);
            window.$toast?.error?.(
              "Media saved but couldn't start mint. Please find your item and try again."
            );
            mintModalLoading.value = false;
            showMintModal.value = false;
            return;
          }

          // Close current modal, start the real mint flow
          showMintModal.value = false;
          mintModalLoading.value = false;

          // Start the mint with saved item (this will open SimpleMintModal again with real data)
          await startMint(item);

          // Set sale config if provided
          if (config) {
            setSaleConfig(config);
          }

          // Use the selected chain from config
          if (config && config.chainId) {
            mintModalSelectedChain.value = config.chainId;
          }

          // Now proceed with the actual mint
          await handleMintModalConfirm();
        } catch (saveErr) {
          console.error("[Masonry] Failed to save draft for minting:", saveErr);
          window.$toast?.error?.(
            "Failed to save media: " + (saveErr?.message || "Unknown error")
          );
          mintModalLoading.value = false;
          pendingDraftMint.value = null;
          showMintModal.value = false;
        }
        return;
      }

      // NORMAL MODE: Item already saved
      // Set sale config before minting - config IS the saleConfig from SimpleMintModal
      if (config) {
        setSaleConfig(config);
      }

      // Use the selected chain from config or fall back to current selection
      if (config && config.chainId) {
        mintModalSelectedChain.value = config.chainId;
      }

      // Delegate to existing handler for chain switching and mint
      await handleMintModalConfirm();
    };

    /**
     * Handle successful batch mint
     */
    const handleBatchMintSuccess = (result) => {
      console.log("[Masonry] Batch mint success:", result);
      showBatchMintModal.value = false;

      // If this was from a draft, clear the pending draft state
      if (pendingDraftMint.value) {
        console.log(
          "[Masonry] Clearing pendingDraftMint after batch mint success"
        );
        pendingDraftMint.value = null;
      }

      // Clear pendingBatchMintItem
      pendingBatchMintItem.value = null;

      // Mark source item as minted in local state (immediate UI update)
      if (result.sourceItemId) {
        const item = processedItems.value.find(
          (i) => i.id === result.sourceItemId
        );
        if (item) {
          item.mintStatus = "minted";
          item.batchMintTxHash = result.txHash;
          item.batchMintCollectionAddress = result.collectionAddress;
          console.log(
            "[Masonry] Marked source item as minted in local state:",
            result.sourceItemId
          );
        }
      }

      // Refresh the timeline to show newly minted items
      requestTimelineFetch(true).catch((e) =>
        console.error("[Masonry] Error refreshing after batch mint:", e)
      );

      // Dispatch event to close any open composer overlays
      window.dispatchEvent(
        new CustomEvent("marketplace:batch-mint-complete", { detail: result })
      );
    };

    // Simple column distribution without complex layout manager
    const getNumberOfColumns = () => {
      const width = window.innerWidth;
      if (width < 590) return 2;
      if (width < 1080) return 3;
      return 3;
    };

    const numberOfColumns = ref(getNumberOfColumns());

    // Simple distribution function - evenly distribute items across columns
    const distributeToColumns = (items, columnCount = 3) => {
      const columns = Array.from({ length: columnCount }, () => []);
      items.forEach((item, index) => {
        columns[index % columnCount].push(item);
      });
      return columns;
    };

    // Debounce function for performance
    const debounceDistribute = (fn, delay) => {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    };

    // Map to store which organizations allow trades
    const orgAllowsTradesMap = ref({});

    // Function to check if an organization allows trades
    const checkOrgAllowsTrades = async (orgUID) => {
      try {
        // Skip if already checked
        if (orgAllowsTradesMap.value[orgUID] !== undefined) {
          return orgAllowsTradesMap.value[orgUID];
        }

        // Get from Vuex if already checked there
        const result = await store.dispatch(
          "UIState/checkOrgAllowsTrades",
          orgUID
        );
        orgAllowsTradesMap.value[orgUID] = result;
        return result;
      } catch (error) {
        console.error(`Error checking if org ${orgUID} allows trades:`, error);
        return true; // Default to allowing trades on error
      }
    };

    // Check if current user can access items based on economy restrictions
    // A user should always be able to see their own items
    // Create computed property for isTradeAllowed
    const isTradeAllowed = computed(() => {
      return store.getters["UIState/isTradeAllowed"];
    });
    const isEconomyRestrictionsLoaded = computed(
      () => store.getters["UIState/isEconomyRestrictionsLoaded"]
    );
    const economyRequiresOrgScope = computed(() =>
      shouldForceOrganisationFeedScope({
        economyLoaded: isEconomyRestrictionsLoaded.value,
        tradeAllowed: isTradeAllowed.value,
      })
    );

    const canAccessItem = (item) => {
      if (isServerFeedPresentation(item)) return true;
      // If viewing profile of current user, always allow
      if (
        props.showItems === "profileUser" &&
        props.profileUserUid === auth.currentUser?.uid
      ) {
        return true;
      }

      // If the item belongs to current user, always allow
      if (item.owner === auth.currentUser?.uid) {
        return true;
      }

      // If item org is same as current user org, allow
      if (item.organisation === props.organisationUid) {
        return true;
      }

      // For global view, check if trades are allowed
      if (!isTradeAllowed.value) {
        return false;
      }

      // At this point, we know it's another organization and trades are allowed for current user org
      // Check if the other org allows trades
      return orgAllowsTradesMap.value[item.organisation] !== false;
    };

    // Flag to track if there are no items matching current filters
    const noMatchingItems = ref(false);

    // Store last filter result count for comparison
    const lastFilteredCount = ref(0);

    // Track the last processed item count to identify new items only
    const lastProcessedCount = ref(0);

    // Store the original item positions by ID to prevent reordering
    const itemPositions = ref(new Map());

    // Previous filter state to track if we need to reset everything
    const previousFilterState = ref({});

    const filteredItems = computed(() => {
      // Timeline-only: filter directly from processedItems
      let items = processedItems.value;

      console.log(
        `[Economy] Starting with ${items.length} timeline items to filter`
      );

      if (items.length === 0) {
        // No items at all - set flag for empty state handling
        noMatchingItems.value = true;
        return [];
      }

      // Hard scoping for Organisation and Profile modes before economy gating
      if (isOrgMode.value) {
        const before = items.length;
        const targetOrg = props.organisationUid || store.getters.currentOrgUID;
        items = targetOrg
          ? items.filter(
              (item) => item.organisation && item.organisation === targetOrg
            )
          : [];
        console.log(
          `[OrgScope] Scoped to organisation ${
            props.organisationUid || targetOrg
          }: ${before} -> ${items.length}`
        );
      } else if (props.showItems === "profileUser" && props.profileUserUid) {
        const before = items.length;
        items = items.filter(
          (item) => item.owner && item.owner === props.profileUserUid
        );
        console.log(
          `[ProfileScope] Scoped to profile ${props.profileUserUid}: ${before} -> ${items.length}`
        );
      }

      // Chain filter: default ETH-only; clicking toggles add more networks to view
      const beforeChain = items.length;
      const activeChains = Array.isArray(chainFilter.value)
        ? chainFilter.value.map((c) => String(c).toLowerCase())
        : ["0x1"];
      items = items.filter((item) => {
        // NFT listings bypass chain filter — they should always be visible
        // regardless of which chain toggle is active
        const isNFTListing =
          item?.type === "nft" ||
          item?.mediaType === "nft" ||
          item?.isNFTListing === true;
        if (isNFTListing) return true;

        const raw =
          item.chainId || item.chain || item.network || item.blockchain;
        if (!raw) return true; // keep legacy items lacking chain info
        const s = String(raw).toLowerCase();
        const nameToHex = (v) => {
          if (v === "1" || v === "0x1" || v.includes("ethereum") || v === "eth")
            return "0x1";
          if (v === "0x89" || v.includes("polygon") || v === "matic")
            return "0x89";
          if (v === "0xa" || v.includes("optimism")) return "0xa";
          if (v === "0x2105" || v.includes("base")) return "0x2105";
          if (v === "0xa4b1" || v.includes("arbitrum")) return "0xa4b1";
          if (v === "0xa4ec" || v.includes("celo")) return "0xa4ec";
          return v;
        };
        const normalized = nameToHex(s);
        return activeChains.includes(normalized);
      });
      if (beforeChain !== items.length) {
        console.log(
          `[ChainFilter] Active [${activeChains.join(
            ", "
          )}] ${beforeChain} -> ${items.length}`
        );
      }

      // Profile timelines are already hard-scoped to their owner above. Economy
      // permissions still govern actions, but must not hide that owner's posts.
      const isProfileTimeline =
        props.showItems === "profileUser" && Boolean(props.profileUserUid);

      // Apply organization filtering and economy restrictions for general cases
      let gatingReason = null;
      if (isOrgMode.value) {
        gatingReason = isOrgMode.value
          ? "org-mode"
          : "closed-economy-hard-lock";
        // Organisation views always remain scoped to their requested organisation.
        const before = items.length;
        const targetOrg = props.organisationUid || store.getters.currentOrgUID;
        items = targetOrg
          ? items.filter((item) => item.organisation === targetOrg)
          : [];
        console.log(
          `[Economy] Org-only gating applied (${gatingReason}) for ${targetOrg}: ${before} -> ${items.length}`
        );
      } else if (isProfileTimeline) {
        console.log(
          `[Economy] Profile display gating bypassed for ${props.profileUserUid}; action permissions remain enforced`
        );
      } else if (!store.getters["UIState/isTradeAllowed"]) {
        gatingReason = "closed-economy-hard-lock";
        const before = items.length;
        const targetOrg = store.getters.currentOrgUID;
        // An unavailable monetary authority must not hide a server-approved
        // feed. Explicit organisation/profile scoping is still applied above.
        items = items.filter((item) =>
          isServerFeedPresentation(item) ||
          (targetOrg && item.organisation === targetOrg)
        );
        console.log(
          `[Economy] Closed-economy gating applied for ${targetOrg}: ${before} -> ${items.length}`
        );
      } else {
        // In global view with trades allowed, filter out items from orgs that don't allow trades
        const before = items.length;
        items = items.filter((item) => {
          const isNFTListing =
            item?.type === "nft" ||
            item?.mediaType === "nft" ||
            item?.isNFTListing === true;

          if (isNFTListing) {
            return true;
          }

          // Check for organization field
          if (!item.organisation) {
            return false; // Filter out items without organization field
          }

          // Use centralized filtering logic
          return canAccessItem(item);
        });
        console.log(
          `[Economy] Global gating applied (trades open) ${before} -> ${items.length}`
        );
      }

      // Apply ownership filters
      if (props.showItems === "currentUser") {
        items = items.filter((item) => item.owner === authUserUid.value);
      } else if (props.showItems === "otherUsers") {
        items = items.filter((item) => item.owner !== authUserUid.value);
      } else if (props.showItems === "profileUser") {
        // Already scoped above; keep for safety
        items = items.filter((item) => item.owner === props.profileUserUid);
      }

      // Apply type filtering
      if (filterType.value === "__none__") return [];

      // A blank initial preference and the complete set both show all types.
      const effectiveFilterType = isDefaultFilterState(filterType.value)
        ? DEFAULT_FILTER_TYPES
        : filterType.value;

      // Split the comma-separated filter types
      const filterTypes = effectiveFilterType.split(",");

      console.log("[Filtering] Using filter types:", filterTypes);

      items = items.filter((item) => {
        // For media items, we need to check the specific media type
        if (item.type === "media" || item.type === "multimedia") {
          // Determine the item's true content type once, so sub-filters
          // don't accidentally match items that belong to a different category.
          // Detect embed source (YouTube → video, SoundCloud → audio)
          const embedSrc = (
            item.embedUrl ||
            item.embedHtml ||
            ""
          ).toLowerCase();
          const isYouTubeEmbed =
            item.mediaType === "embed" &&
            /youtube\.com|youtu\.be/.test(embedSrc);
          const isSoundCloudEmbed =
            item.mediaType === "embed" && /soundcloud\.com/.test(embedSrc);

          const isVideo =
            item.mediaType === "video" || !!item.videoUrl || isYouTubeEmbed;
          const isAudio =
            item.mediaType === "audio" || !!item.audioUrl || isSoundCloudEmbed;
          const isDoc =
            item.mediaType === "documents" ||
            item.mediaType === "document" ||
            (Array.isArray(item.documents) && item.documents.length > 0);
          const isImage =
            !isVideo &&
            !isAudio &&
            !isDoc &&
            (item.mediaType === "image" ||
              (item.images && item.images.length > 0));
          const isText =
            !isVideo &&
            !isAudio &&
            !isDoc &&
            !isImage &&
            ["text", "post", "poster", "tweet", "embed"].includes(
              item.mediaType
            );

          // Document / PDF media — matched by the "docs" filter
          if (filterTypes.includes("docs") && isDoc) {
            return true;
          }
          // Check if one of our media filters is active
          if (filterTypes.includes("media_image") && isImage) {
            return true;
          }
          if (filterTypes.includes("media_video") && isVideo) {
            return true;
          }
          if (filterTypes.includes("media_audio") && isAudio) {
            return true;
          }
          if (filterTypes.includes("media_text") && isText) {
            return true;
          }
          return false;
        }

        // Docs special-case: match several heuristics (does NOT short-circuit
        // other type checks so products/services still pass through below)
        if (filterTypes.includes("docs")) {
          if (item.type === "doc" || item.type === "document") return true;

          const cat = (item.category || "").toLowerCase();
          const tags = Array.isArray(item.tags)
            ? item.tags.join(" ").toLowerCase()
            : "";
          const evt = (item.eventType || "").toLowerCase();
          if (
            cat === "docs" ||
            cat === "documents" ||
            tags.includes("doc") ||
            evt === "document"
          ) {
            return true;
          }
          // Don't return false here — fall through so other active filters
          // (product, service, etc.) still get a chance to match.
        }

        // NFT items — matched by the "nft" filter
        if (
          filterTypes.includes("nft") &&
          (item.type === "nft" || item.mediaType === "nft" || item.isNFTListing)
        ) {
          return true;
        }

        // For products, services, and any other type — check if their type
        // is in the active filter list
        return filterTypes.includes(item.type);
      });

      // Apply search filtering - improved to check multiple fields and normalize search query
      if (searchQuery.value) {
        const normalizedQuery = searchQuery.value.trim().toLowerCase();
        const before = items.length;
        items = items.filter((item) => {
          // Check title/name fields
          const titleMatch =
            (item.title?.toLowerCase() || "").includes(normalizedQuery) ||
            (item.itemName?.toLowerCase() || "").includes(normalizedQuery);

          // Check description fields
          const descriptionMatch =
            (item.description?.toLowerCase() || "").includes(normalizedQuery) ||
            (item.itemDescription?.toLowerCase() || "").includes(
              normalizedQuery
            );

          // Check category as well for more useful search results
          const categoryMatch = (item.category?.toLowerCase() || "").includes(
            normalizedQuery
          );

          return titleMatch || descriptionMatch || categoryMatch;
        });
        console.log(
          `[Search] '${searchQuery.value}' filtered: ${before} -> ${items.length}`
        );
      }

      // Apply category filtering - improved to check mapped categories
      if (selectedCategory.value) {
        const before = items.length;
        items = items.filter((item) => {
          // Check direct category match
          if (item.category === selectedCategory.value) {
            return true;
          }

          // Check if there's a category mapping for this item's category
          if (
            item.category &&
            categoryMapping[item.category] === selectedCategory.value
          ) {
            return true;
          }

          return false;
        });
        console.log(
          `[Category] ${selectedCategory.value} filtered: ${before} -> ${items.length}`
        );
      }

      // Apply location filtering if active - improved to keep items WITHOUT location data visible
      if (locationFilter.value.active && locationFilter.value.coordinates) {
        const before = items.length;
        items = items.filter((item) => {
          // Keep items without location data visible
          if (!item.location || !item.location.coordinates) {
            return true;
          }

          // Calculate distance between item and user
          const distance = calculateHaversineDistance(
            locationFilter.value.coordinates.lat,
            locationFilter.value.coordinates.lng,
            item.location.coordinates.lat,
            item.location.coordinates.lng
          );

          // Store the distance on the item for potential display
          item._distanceFromUser = distance;

          // Filter based on search radius
          return distance <= locationFilter.value.radius;
        });
        console.log(
          `[Location] radius ${locationFilter.value.radius}km filtered: ${before} -> ${items.length}`
        );
      }

      // Apply sorting based on the selected sort order
      const sorted = [...items].sort((a, b) => {
        // Extract timestamps if available
        const aTimestamp = a.createdAt?.toMillis
          ? a.createdAt.toMillis()
          : a.createdAt?.seconds
          ? a.createdAt.seconds * 1000
          : 0;
        const bTimestamp = b.createdAt?.toMillis
          ? b.createdAt.toMillis()
          : b.createdAt?.seconds
          ? b.createdAt.seconds * 1000
          : 0;

        // Extract prices for price sorting
        const aPrice = typeof a.price === "number" ? a.price : 0;
        const bPrice = typeof b.price === "number" ? b.price : 0;

        // Apply the appropriate sort
        switch (sortOrder.value) {
          case "timestamp_desc": // newest first
            return bTimestamp - aTimestamp;
          case "timestamp_asc": // oldest first
            return aTimestamp - bTimestamp;
          case "price_asc": // lowest price first
            return aPrice - bPrice;
          case "price_desc": // highest price first
            return bPrice - aPrice;
          default:
            return bTimestamp - aTimestamp; // default to newest first
        }
      });

      // Update filtered count for empty state handling
      const currentCount = sorted.length;

      // Check if we have no matching items and update the flag
      noMatchingItems.value = currentCount === 0;

      // Track significant changes in filtered results count
      const countChanged = Math.abs(currentCount - lastFilteredCount.value) > 5;
      lastFilteredCount.value = currentCount;

      hasInitialItems.value = currentCount > 0;
      return sorted.slice(0, itemsToLoad.value);
    });

    // Enhanced column distribution with forced repaint triggers
    const distributeItemsImmediate = (forceInitial = false) => {
      // Force browser repaint before layout changes
      document.body.offsetHeight;

      const items = filteredItems.value;
      const totalItems = items.length;

      console.log(
        `[LAYOUT] Distribute called {forceInitial: ${forceInitial}, items: ${totalItems}, isLoadingMoreItems: ${isLoadingMoreItems.value}, columnsBefore: ${columns.value.length}}`
      );

      const cols = distributeToColumns(items, numberOfColumns.value);
      if (cols && cols.length) {
        columns.value = cols;

        // Force browser repaint after layout changes
        requestAnimationFrame(() => {
          document.body.offsetHeight;
        });
      }

      hasInitialItems.value = totalItems > 0;

      console.log(
        `[LAYOUT] Distribute applied {columnsAfter: ${columns.value.length}}`
      );
    };

    // Create debounced version with frame-aligned timing for smooth performance
    const distributeItems = debounceDistribute(distributeItemsImmediate, 16);

    const handleResize = () => {
      const newColumnCount = getNumberOfColumns();
      if (newColumnCount !== numberOfColumns.value) {
        numberOfColumns.value = newColumnCount;
        // Simple redistribution on resize
        distributeItems();
      }
    };

    // Watch for filteredItems changes and distribute to columns
    // Preserve scroll position to prevent layout jump on filter toggle
    watch(
      filteredItems,
      (newItems) => {
        if (newItems && newItems.length >= 0) {
          const scrollY = window.scrollY;
          columns.value = distributeToColumns(newItems, numberOfColumns.value);
          // Restore scroll after the DOM settles
          nextTick(() => {
            window.scrollTo({ top: scrollY, behavior: "instant" });
          });
        }
      },
      { immediate: true }
    );

    // Watch for column count changes
    watch(numberOfColumns, () => {
      if (filteredItems.value) {
        columns.value = distributeToColumns(
          filteredItems.value,
          numberOfColumns.value
        );
      }
    });
    const windowWidth = ref(window.innerWidth);

    const updateWindowWidth = () => {
      windowWidth.value = window.innerWidth;
    };

    // Watchers - only watch for changes that require redistribution
    watch(
      () => [filteredItems.value.length, numberOfColumns.value],
      () => {
        distributeItems();
      }
    );

    // Debounced search: wait 1s after last keystroke before fetching
    let searchDebounceTimer = null;
    watch(
      () => searchQuery.value,
      (newQuery, oldQuery) => {
        // Clear any in-flight debounce
        if (searchDebounceTimer) {
          clearTimeout(searchDebounceTimer);
          searchDebounceTimer = null;
        }

        const prev = (oldQuery || "").trim();
        const next = (newQuery || "").trim();

        // If search cleared, allow the main filters watcher to reset immediately
        if (prev !== "" && next === "") {
          return;
        }

        // If we're in search (non-empty), debounce the fetch
        if (next !== "") {
          searchDebounceTimer = setTimeout(() => {
            console.log(
              `[SEARCH_MODE] Debounced fetch for query "${next}" (1s)`
            );
            // Only fetch MORE items if the current client-side filtered set
            // is too small. Do NOT reset pools — the client-side computed
            // search in filteredItems already works on the existing pool.
            const currentFiltered = filteredItems.value.length;
            if (currentFiltered < 10 && hasMoreTimelineData.value) {
              console.log(
                `[SEARCH_MODE] Only ${currentFiltered} client-side matches — fetching more from server`
              );
              requestTimelineFetch(false); // paginate, don't reset
            } else {
              console.log(
                `[SEARCH_MODE] Already have ${currentFiltered} client-side matches — skipping server fetch`
              );
            }
          }, 1000);
        }
      }
    );

    // When filters change, reset processed count to force fresh layout
    watch(
      () => [
        searchQuery.value,
        filterType.value,
        sortOrder.value,
        selectedCategory.value,
        locationFilter.value.active,
      ],
      (newFilters, oldFilters) => {
        if (isInitializing.value) {
          return;
        }
        // Store the previous filter state in a more structured way
        previousFilterState.value = {
          searchQuery: oldFilters?.[0] || "",
          filterType: oldFilters?.[1] || "",
          sortOrder: oldFilters?.[2] || "timestamp_desc",
          category: oldFilters?.[3] || "",
          locationActive: oldFilters?.[4] || false,
        };

        console.log(
          "[Filters] Changed from:",
          previousFilterState.value,
          "to:",
          {
            searchQuery: newFilters[0],
            filterType: newFilters[1],
            sortOrder: newFilters[2],
            category: newFilters[3],
            locationActive: newFilters[4],
          }
        );

        // Detect search transitions
        const prevSearch = previousFilterState.value.searchQuery || "";
        const nextSearch = newFilters[0] || "";
        const searchWasCleared = prevSearch !== "" && nextSearch === "";
        const searchBecameActive = prevSearch === "" && nextSearch !== "";

        if (searchWasCleared) {
          console.log("[SEARCH_MODE] Search cleared, reactivating pagination");
          hasMoreTimelineData.value = true;
          // Reset pagination cursor when leaving search to restart timeline
          lastTimelineDoc.value = null;
          // Reset processed pools to drop search-only set and load timeline again
          processedItems.value = [];
          processedItemIds.value.clear();
        }

        // Reset layout/display counters
        lastProcessedCount.value = 0;
        itemPositions.value.clear();
        noMatchingItems.value = false;
        itemsToLoad.value = 12;
        // Force a clean relayout so removed types disappear and we avoid duplicates
        columns.value = [];
        nextTick(() => {
          // Rebuild columns from the current filtered set
          distributeItems(true);
          lastTimelineRefreshAt.value = Date.now();
        });

        // Avoid clearing processed items on every keystroke or type toggle to prevent transient 0 results
        // Only clear item pools on major mode transitions if needed in the future

        // Trigger fetch if we have no items or when leaving search (entering search is handled by debounced watcher)
        if (processedItems.value.length === 0 || searchWasCleared) {
          // Kick off initial fetch; after it completes, run a scroll check to continue pagination
          Promise.resolve(requestTimelineFetch(true)).finally(() => {
            // Give the UI a moment to render items, then check if we need to fetch next page
            setTimeout(() => {
              try {
                handleScroll();
              } catch (e) {
                console.warn(
                  "[SEARCH_MODE] Post-clear scroll check failed:",
                  e
                );
              }
            }, 200);
          });
        }
      }
    );

    watch(
      () => sortOrder.value,
      () => {
        distributeItems();
      }
    );

    const preloadOrgTradePermissions = async () => {
      try {
        // Collect unique organization IDs from all items
        const uniqueOrgUIDs = new Set();

        // Timeline-only: derive organisations from processed timeline items
        if (Array.isArray(processedItems.value)) {
          processedItems.value.forEach((item) => {
            if (item && item.organisation) uniqueOrgUIDs.add(item.organisation);
          });
        }

        // Check trade permissions for all unique orgs
        // console.log(
        //   `[Economy] Checking trade permissions for ${uniqueOrgUIDs.size} organizations in MasonryMarketplace`
        // );

        for (const orgUID of uniqueOrgUIDs) {
          if (orgUID !== props.organisationUid) {
            // Skip current user's org
            const allowsTrades = await checkOrgAllowsTrades(orgUID);
            orgAllowsTradesMap.value[orgUID] = allowsTrades;
          }
        }

        // console.log(
        //   "[Economy] Organizations trade permissions:",
        //   orgAllowsTradesMap.value
        // );
      } catch (error) {
        console.error(
          "[Economy] Error preloading org trade permissions:",
          error
        );
      }
    };

    // Legacy end-of-data flags removed in timeline-only mode

    // Track consecutive empty fetches
    const emptyFetchCounter = ref(0);
    const MAX_EMPTY_FETCHES = 2; // After 2 empty fetches, consider all data exhausted

    // Track timeline pagination state
    const lastTimelineDoc = ref(null);
    const isUsingTimeline = ref(false);
    const hasMoreTimelineData = ref(true);

    // Track profile-specific pagination state
    const profileLastDoc = ref(null);
    const hasMoreProfileData = ref(true);
    const profileItemsProcessed = ref(new Set()); // Track processed item IDs to prevent duplicates

    // Track current filter state for fetch optimization
    const currentFilterState = reactive({
      searchQuery: "",
      filterType: "",
      chainFilter: ["0x1"],
      category: "",
      locationActive: false,
      sortOrder: "timestamp_desc",
    });

    // Check if filters have changed significantly enough to warrant a fresh fetch
    const haveFiltersChanged = () => {
      return (
        currentFilterState.searchQuery !== searchQuery.value ||
        currentFilterState.filterType !== filterType.value ||
        JSON.stringify(currentFilterState.chainFilter) !==
          JSON.stringify(chainFilter.value) ||
        currentFilterState.category !== selectedCategory.value ||
        currentFilterState.locationActive !== locationFilter.value.active ||
        currentFilterState.sortOrder !== sortOrder.value
      );
    };

    // Check if filters have been completely reset (all default values)
    const areFiltersReset = () => {
      return (
        searchQuery.value === "" &&
        isDefaultFilterState(filterType.value) &&
        Array.isArray(chainFilter.value) &&
        chainFilter.value.length === 1 &&
        chainFilter.value[0] === "0x1" &&
        selectedCategory.value === "" &&
        sortOrder.value === "timestamp_desc" &&
        !locationFilter.value.active
      );
    };

    // Update the stored filter state
    const updateFilterState = () => {
      currentFilterState.searchQuery = searchQuery.value;
      currentFilterState.filterType = filterType.value;
      currentFilterState.chainFilter = Array.isArray(chainFilter.value)
        ? chainFilter.value.slice()
        : ["0x1"];
      currentFilterState.category = selectedCategory.value;
      currentFilterState.locationActive = locationFilter.value.active;
      currentFilterState.sortOrder = sortOrder.value;

      // Log filter changes to help with debugging
      console.log("[Filters] Current state:", { ...currentFilterState });
    };

    // Track processed item IDs to prevent duplicates
    const processedItemIds = ref(new Set());
    // Track items currently being processed to avoid race duplicates
    const inFlightItemIds = ref(new Set());

    // Internal org mode state for FiltersComponent (must be declared before computed that uses it)
    // Start in org mode automatically if trades are NOT allowed (hard lock)
    const internalOrgMode = ref(economyRequiresOrgScope.value);
    // Treat org mode if either the prop says so or UI toggle is on (robust for MetaMask timing)
    const isOrgMode = computed(
      () =>
        props.showItems === "organisation" ||
        store.getters["UIState/showOwnOrganisationOnly"] ||
        internalOrgMode.value
    );

    // Determine if org toggle should be shown
    const shouldShowOrgToggle = computed(() => {
      // Get user organizations
      const hasOrganisations = (store.getters.userOrgIds || []).length > 0;

      // Check if trades are allowed
      const tradesAllowed = isTradeAllowed.value;

      // Show toggle if user has orgs AND trades are allowed
      return hasOrganisations && tradesAllowed;
    });

    // Handle org mode updates from FiltersComponent
    const handleOrgModeUpdate = (newOrgMode) => {
      console.log(
        "[MasonryMarketplace] Org mode update from filters:",
        newOrgMode
      );
      // Only set flag; a single consolidated watcher will perform resets & fetch.
      internalOrgMode.value = newOrgMode; // preserve capability: user-driven toggle
      store.dispatch("UIState/setShowOwnOrganisationOnly", newOrgMode); // preserve global state sync
    };

    const openOrganisationCreation = () =>
      router.push({ name: "organisation-new" });
    const openInvites = () => router.push({ name: "invites" });

    // Feed scope segmented toggle (lives here so it renders above the grid on all viewports)
    const activeOrganisationIds = computed(
      () => store.getters.userOrgIds || []
    );
    const hasActiveOrganisations = computed(
      () => activeOrganisationIds.value.length > 0
    );
    const hasMultipleOrganisations = computed(
      () => activeOrganisationIds.value.length > 1
    );
    const effectiveFeedOrgUID = computed(
      () =>
        (props.hideFeedToggle ? props.organisationUid : null) ||
        store.getters.selectedOrgUID ||
        props.organisationUid ||
        store.getters.currentOrgUID ||
        null
    );
    const loadedFeedOrgUID = ref(effectiveFeedOrgUID.value);
    const feedScopeOptions = computed(() => [
      { label: "Open Feed", id: "open" },
      {
        label: "Organisations",
        id: "org",
        disabled: !hasActiveOrganisations.value,
      },
      {
        label: "Follows",
        id: "follows",
        disabled: !isFollowingFeedFeatureEnabled,
      },
    ]);
    const feedScope = ref(
      props.showItems === "profileUser" || props.showItems === "organisation"
        ? "open"
        : internalOrgMode.value
        ? "org"
        : "open"
    );
    if (!hasActiveOrganisations.value && feedScope.value === "org") {
      feedScope.value = "open";
      internalOrgMode.value = false;
    }
    const followingEmptyState = ref("unknown");
    let followingStateRequestGeneration = 0;
    const emptyStateTitle = computed(() => {
      if (feedScope.value !== "follows") return "No matching items found";
      return followingEmptyState.value === "no-follows"
        ? "You aren't following anyone yet"
        : "No new content from your follows";
    });
    const emptyStateDescription = computed(() => {
      if (feedScope.value !== "follows") {
        return "Try adjusting your filters or search terms";
      }
      return followingEmptyState.value === "no-follows"
        ? "Follow a person or organisation to build this feed."
        : "The people and organisations you follow haven't published anything yet.";
    });
    const resolveFollowingEmptyState = async () => {
      if (isItemAuthorityReadsFeatureEnabled) {
        // The private item feed owns Following publication eligibility. Do not
        // reopen the legacy subscription list merely to refine an empty-state
        // sentence while that authority is selected.
        followingEmptyState.value = "unknown";
        return;
      }
      const expectedUserUID = authUserUid.value;
      const generation = ++followingStateRequestGeneration;
      try {
        const result = await listContentSubscriptions({ limit: 1 });
        if (
          authUserUid.value !== expectedUserUID ||
          generation !== followingStateRequestGeneration
        ) {
          return;
        }
        followingEmptyState.value = result.subscriptions.length
          ? "no-content"
          : "no-follows";
      } catch (_error) {
        if (
          authUserUid.value !== expectedUserUID ||
          generation !== followingStateRequestGeneration
        ) {
          return;
        }
        followingEmptyState.value = "unknown";
      }
    };

    let queuedTimelineFetch = null;
    let paginationLifecycleActive = false;
    let paginationHasMounted = false;
    let paginationWasDeactivated = false;
    let resumeTimelineFetch = null;
    let componentDisposed = false;
    const isTimelineRequestCurrent = (generation) =>
      isCurrentFeedRequest({
        generation,
        currentGeneration: timelineGeneration.value,
        lifecycleActive: paginationLifecycleActive,
        disposed: componentDisposed,
      });
    const requestTimelineFetch = (isInitialFetch = false) => {
      const requestKind = isInitialFetch ? "initial" : "next";
      if (componentDisposed) return Promise.resolve(false);
      if (paginationHasMounted && !paginationLifecycleActive) {
        if (requestKind === "initial" || !resumeTimelineFetch) {
          resumeTimelineFetch = requestKind;
        }
        return Promise.resolve(false);
      }
      if (
        !paginationLifecycleActive ||
        isLoadingMoreItems.value ||
        isInitializing.value
      ) {
        if (requestKind === "initial" || !queuedTimelineFetch) {
          queuedTimelineFetch = requestKind;
        }
        return Promise.resolve(false);
      }
      queuedTimelineFetch = null;
      return fetchMoreItems(isInitialFetch);
    };
    const flushQueuedTimelineFetch = () => {
      if (
        !queuedTimelineFetch ||
        componentDisposed ||
        !paginationLifecycleActive ||
        isLoadingMoreItems.value ||
        isInitializing.value
      ) {
        return;
      }
      const requestKind = queuedTimelineFetch;
      queuedTimelineFetch = null;
      nextTick(() => requestTimelineFetch(requestKind === "initial"));
    };

    watch(feedScope, (newScope, oldScope) => {
      if (economyRequiresOrgScope.value && newScope !== "org") {
        saveFeedCache(feedCacheKey(oldScope));
        feedScope.value = "org";
        toast.warn("Global marketplace disabled for this organisation", {
          autoClose: 2200,
          position: "bottom-center",
        });
        return;
      }
      if (newScope === "follows") {
        saveFeedCache(feedCacheKey(oldScope));
        followingEmptyState.value = "unknown";
        resolveFollowingEmptyState();
        resetTimelineState("following-feed-selected");
        if (restoreFeedCache(feedCacheKey("follows"))) return;
        requestTimelineFetch(true);
        return;
      }
      const wantOrg = newScope === "org";
      if (oldScope === "follows") {
        saveFeedCache(feedCacheKey("follows"));
        resetTimelineState("following-feed-left");
        if (wantOrg === internalOrgMode.value) {
          if (restoreFeedCache(feedCacheKey(newScope))) return;
          requestTimelineFetch(true);
          return;
        }
      }
      if (wantOrg === internalOrgMode.value) return;
      // Block switching to global when economy is closed
      if (!wantOrg && economyRequiresOrgScope.value) {
        feedScope.value = "org";
        toast.warn("Global marketplace disabled for this organisation", {
          autoClose: 2200,
          position: "bottom-center",
        });
        return;
      }
      handleOrgModeUpdate(wantOrg);
    });

    // Keep feedScope in sync when orgMode changes internally
    watch(internalOrgMode, (nv) => {
      if (economyRequiresOrgScope.value && nv) {
        feedScope.value = "org";
      } else if (feedScope.value !== "follows") {
        feedScope.value = nv ? "org" : "open";
      }
    });

    watch(hasActiveOrganisations, (hasOrganisations) => {
      if (!hasOrganisations && feedScope.value === "org") {
        feedScope.value = "open";
        internalOrgMode.value = false;
        store.dispatch("UIState/setShowOwnOrganisationOnly", false);
      }
    });

    const handleSelectedOrgChange = (orgId) => {
      if (!orgId || orgId === loadedFeedOrgUID.value) return;
      if (feedScope.value !== "org") {
        loadedFeedOrgUID.value = orgId;
        return;
      }
      saveFeedCache(feedCacheKey("org", loadedFeedOrgUID.value));
      loadedFeedOrgUID.value = orgId;
      resetTimelineState("selected-organisation-changed");
      if (restoreFeedCache(feedCacheKey("org", orgId))) return;
      nextTick(() => requestTimelineFetch(true));
    };
    const handleFollowingChanged = () => {
      feedScopeCache.delete(feedCacheKey("follows"));
      if (feedScope.value === "follows") {
        followingEmptyState.value = "unknown";
        resolveFollowingEmptyState();
        refreshTimeline({ reason: "following-changed" });
      }
    };

    // Centralized reset routine (do NOT remove any feature; only consolidating logic)
    function resetTimelineState(reason) {
      console.log("[MasonryMarketplace] ✅ Reset timeline state:", reason);
      timelineGeneration.value += 1;
      lastTimelineDoc.value = null;
      hasMoreTimelineData.value = true;
      processedItems.value = [];
      processedItemIds.value.clear();
      lastProcessedCount.value = 0;
      itemPositions.value.clear();
      columns.value = [];
      noMatchingItems.value = false;
      timelineError.value = null;
      emptyFetchCounter.value = 0;
      hasUserPaginationIntent.value = false;
      resumeTimelineFetch = null;
    }

    const revalidateFirstTimelinePage = (promise, generation) => {
      if (!promise) return;
      promise
        .then(async (freshResult) => {
          if (!isTimelineRequestCurrent(generation)) return;
          processedItems.value = [];
          processedItemIds.value.clear();
          lastProcessedCount.value = 0;
          itemPositions.value.clear();
          columns.value = [];
          lastTimelineDoc.value = freshResult.lastDoc || null;
          hasMoreTimelineData.value = !!freshResult.hasMore;
          await processItems(normalizeTimelineItems(freshResult.items));
          if (!isTimelineRequestCurrent(generation)) return;
          distributeItems(true);
        })
        .catch(() => {
          // Cached content remains usable when background revalidation fails.
        });
    };

    const fetchWishlistInBackground = () => {
      if (!isWishlistFeatureEnabled) return Promise.resolve();
      const finishWishlist = beginBootStep("wishlist");
      return fetchWishlistItems()
        .then(() => finishWishlist({ outcome: "ready" }))
        .catch((error) => {
          finishWishlist({ errorCode: error?.code || "wishlist-error" });
        });
    };

    // ── Feed scope cache (3-minute TTL) ───────────────────────────────
    // Caches processedItems, columns, lastTimelineDoc, hasMoreTimelineData
    // per actor and scope key so neither an organisation nor an account switch
    // can restore another context's content.
    const FEED_CACHE_TTL = 3 * 60 * 1000; // 3 minutes
    const feedScopeCache = new Map(); // key → { items, ids, columns, lastDoc, hasMore, noMatching, ts }

    function feedCacheKey(scope, orgId = loadedFeedOrgUID.value) {
      return buildFeedCacheKey({
        userUID: authUserUid.value,
        scope,
        orgId,
      });
    }

    function saveFeedCache(scopeKey) {
      if (!processedItems.value.length) return; // nothing to cache
      feedScopeCache.set(scopeKey, {
        items: [...processedItems.value],
        ids: new Set(processedItemIds.value),
        columns: columns.value.map((col) => [...col]),
        lastDoc: lastTimelineDoc.value,
        hasMore: hasMoreTimelineData.value,
        noMatching: noMatchingItems.value,
        lastProcessed: lastProcessedCount.value,
        positions: new Map(itemPositions.value),
        ts: Date.now(),
      });
      console.log(
        `[MasonryMarketplace] 💾 Cached feed scope "${scopeKey}" (${processedItems.value.length} items)`
      );
    }

    function restoreFeedCache(scopeKey) {
      const cached = feedScopeCache.get(scopeKey);
      if (!cached) return false;
      if (Date.now() - cached.ts > FEED_CACHE_TTL) {
        feedScopeCache.delete(scopeKey);
        console.log(`[MasonryMarketplace] 🗑️ Cache expired for "${scopeKey}"`);
        return false;
      }
      processedItems.value = [...cached.items];
      processedItemIds.value = new Set(cached.ids);
      columns.value = cached.columns.map((col) => [...col]);
      lastTimelineDoc.value = cached.lastDoc;
      hasMoreTimelineData.value = cached.hasMore;
      noMatchingItems.value = cached.noMatching;
      lastProcessedCount.value = cached.lastProcessed;
      itemPositions.value = new Map(cached.positions);
      timelineError.value = null;
      isInitialLoading.value = false;
      hasUserPaginationIntent.value = false;
      console.log(
        `[MasonryMarketplace] ♻️ Restored feed scope "${scopeKey}" from cache (${cached.items.length} items)`
      );
      return true;
    }

    // Consolidated watcher: handles any org-mode transitions (user toggle, store, prop sync)
    watch(
      () => isOrgMode.value,
      (nv, ov) => {
        if (nv === ov) return;
        console.log("[MasonryMarketplace] (Consolidated) isOrgMode changed", {
          from: ov,
          to: nv,
        });
        // Save current feed to cache before switching
        const leavingKey = feedCacheKey(ov ? "org" : "open");
        saveFeedCache(leavingKey);

        resetTimelineState("org-mode-switch");

        // Try to restore from cache for the incoming scope. Reset first so
        // pending responses from the previous scope cannot replace it.
        const enteringKey = feedCacheKey(nv ? "org" : "open");
        if (restoreFeedCache(enteringKey)) {
          return; // restored from cache, skip fetch
        }

        // Defer fetch to ensure effectiveOrgUid derived from updated reactive state
        requestTimelineFetch(true);
      }
    );

    // Enforce org mode whenever economy becomes closed after being open
    watch(economyRequiresOrgScope, (requiresOrgScope) => {
      if (requiresOrgScope) {
        if (feedScope.value !== "org") {
          feedScope.value = "org";
        }
        if (!internalOrgMode.value) {
          console.log(
            "[MasonryMarketplace] Economy closed -> forcing org mode true"
          );
          internalOrgMode.value = true; // triggers consolidated watcher indirectly
          store.dispatch("UIState/setShowOwnOrganisationOnly", true);
        }
      }
    });

    // Slim showItems watcher: only sync internal flag; keep capability for external routing without duplicate fetches
    watch(
      () => props.showItems,
      (newMode, oldMode) => {
        if (newMode === oldMode) return;
        const wantOrg = newMode === "organisation";
        console.log("[MasonryMarketplace] showItems changed (sync only)", {
          from: oldMode,
          to: newMode,
        });
        if (wantOrg !== internalOrgMode.value) {
          internalOrgMode.value = wantOrg; // watcher above will reset & fetch
        }
      }
    );

    // HARDCODED TIMELINE MODE - Force timeline-only approach to fix infinite loops
    const fetchMarketplaceWithDeadline = (options) =>
      withUiDeadline(
        privateAwareFeedSource.fetchPage({
          ...options,
          actorUid: authUserUid.value,
          publicOrganisationView: props.publicOrganisationView,
        }),
        {
          phase: "timeline",
          // The timeline reads two functions and may cold-start both.
          timeoutMs: 30000,
        }
      );

    const fetchMoreItems = async (isInitialFetch = false) => {
      const requestGeneration = timelineGeneration.value;
      if (
        isLoadingMoreItems.value ||
        !isTimelineRequestCurrent(requestGeneration)
      ) {
        return false;
      }

      // Compute search/filtered mode locally to avoid hoist issues
      const inSearchMode = (searchQuery.value || "").trim() !== "";
      const inFilteredMode = (() => {
        // Intentional __none__ is filtered; blank/full-set are defaults.
        const hasSearch = inSearchMode;
        const hasNonDefaultType = !isDefaultFilterState(filterType.value);
        const hasCategory = selectedCategory.value !== "";
        const hasLocation = !!locationFilter.value?.active;
        const hasNonDefaultSort = sortOrder.value !== "timestamp_desc";
        return (
          hasSearch ||
          hasNonDefaultType ||
          hasCategory ||
          hasLocation ||
          hasNonDefaultSort
        );
      })();

      // Clear any previous error when starting a new fetch
      if (isInitialFetch) timelineError.value = null;
      isLoadingMoreItems.value = true;

      try {
        console.log(
          `[TIMELINE_ONLY] fetchMoreItems called - Initial: ${isInitialFetch}, Filtered: ${inFilteredMode}`
        );

        // Build current filter state - ALWAYS use timeline
        const currentFilters = {
          searchQuery: searchQuery.value,
          filterType: filterType.value,
          sortOrder: sortOrder.value,
          selectedCategory: selectedCategory.value,
          locationFilter: locationFilter.value,
        };

        // SEARCH MODE (initial fetch only): reset pools then fall through to normal path
        if (inSearchMode && isInitialFetch) {
          console.log("[SEARCH_MODE] Initial search fetch — resetting pools");
          // Fully reset pools so search results start fresh
          processedItemIds.value.clear();
          processedItems.value = [];
          lastTimelineDoc.value = null;
          hasMoreTimelineData.value = true;
          // Fall through to NORMAL MODE below which handles both initial and paginated fetches
        }

        // PROFILE MODE: Use timeline with user filtering for pagination (ordering: createdAt desc, __name__ desc)
        if (props.showItems === "profileUser") {
          if (isItemAuthorityReadsFeatureEnabled) {
            timelineError.value =
              "Profile items are unavailable until their private presentation is implemented.";
            hasMoreProfileData.value = false;
            return false;
          }
          if (!hasMoreProfileData.value) {
            console.log("[Profile] No more data available, stopping fetch");
            isLoadingMoreItems.value = false;
            return;
          }

          try {
            const timelineResult = await fetchMarketplaceWithDeadline({
              filters: currentFilters,
              batchSize: 12,
              lastDoc: profileLastDoc.value,
              organisationUid: props.organisationUid,
              scopeOrganisation: isOrgMode.value,
              profileUserUid: props.profileUserUid,
            });

            if (!isTimelineRequestCurrent(requestGeneration)) return;

            if (timelineResult.fromTimeline) {
              console.log(
                `[TIMELINE_ONLY] Profile fetch: ${timelineResult.items.length} items`
              );

              if (timelineResult.items.length === 0) {
                hasMoreProfileData.value = false;
                profileLastDoc.value = null;
                isLoadingMoreItems.value = false;
                return;
              }

              const normalizedItems = normalizeTimelineItems(
                timelineResult.items
              );
              // Basic ordering validation (createdAt should be non-increasing)
              let orderingOk = true;
              for (let i = 1; i < normalizedItems.length; i++) {
                const prev =
                  normalizedItems[i - 1].createdAt?.toMillis?.() ||
                  normalizedItems[i - 1].createdAt;
                const curr =
                  normalizedItems[i].createdAt?.toMillis?.() ||
                  normalizedItems[i].createdAt;
                if (prev < curr) {
                  orderingOk = false;
                  break;
                }
              }
              if (!orderingOk) {
                console.warn(
                  "[TIMELINE_ONLY][Ordering] Profile batch not strictly descending by createdAt"
                );
              }
              await processItems(normalizedItems);
              if (!isTimelineRequestCurrent(requestGeneration)) return;

              profileLastDoc.value = timelineResult.hasMore
                ? timelineResult.lastDoc
                : null;
              hasMoreProfileData.value = Boolean(
                timelineResult.hasMore && timelineResult.lastDoc
              );

              distributeItems(false);
              return;
            }
          } catch (error) {
            if (!isTimelineRequestCurrent(requestGeneration)) return;
            console.warn(`[TIMELINE_ONLY] Profile error:`, error);
            // Fallback: fetch a general page and client-filter by owner to avoid total failure (e.g., missing index)
            try {
              const fallback = await fetchMarketplaceWithDeadline({
                filters: currentFilters,
                batchSize: 12,
                lastDoc: null,
                organisationUid: null,
                scopeOrganisation: false,
                profileUserUid: null,
              });
              if (!isTimelineRequestCurrent(requestGeneration)) return;
              if (fallback.fromTimeline && fallback.items?.length) {
                const normalized = normalizeTimelineItems(fallback.items);
                const ownerItems = normalized.filter(
                  (it) => it.owner === props.profileUserUid
                );
                await processItems(ownerItems);
                if (!isTimelineRequestCurrent(requestGeneration)) return;
                hasMoreProfileData.value = false; // cannot paginate reliably on fallback
                profileLastDoc.value = null;
                distributeItems(false);
                return;
              }
            } catch (e2) {
              if (!isTimelineRequestCurrent(requestGeneration)) return;
              console.warn("[TIMELINE_ONLY] Profile fallback failed:", e2);
            }
            if (!isTimelineRequestCurrent(requestGeneration)) return;
            timelineError.value =
              error?.message || "Failed to load profile items.";
            hasMoreProfileData.value = false;
            isLoadingMoreItems.value = false;
            return;
          }
        }

        // NORMAL MODE: ALWAYS use timeline (no complex sorting fallback)
        console.log(
          `[TIMELINE_ONLY] Using timeline fetch - filters:`,
          currentFilters
        );

        try {
          // Determine effective organisation scoping (force null when not in org mode to prevent accidental scoping)
          const effectiveOrgUid = isOrgMode.value
            ? effectiveFeedOrgUID.value
            : null;
          console.log(
            "[TimelineScope] Effective org UID:",
            effectiveOrgUid,
            "Mode:",
            isOrgMode.value
              ? "ORG"
              : props.showItems === "profileUser"
              ? "PROFILE"
              : "GLOBAL"
          );
          const timelineResult = await fetchMarketplaceWithDeadline({
            filters: currentFilters,
            batchSize: 12,
            lastDoc: isInitialFetch ? null : lastTimelineDoc.value,
            organisationUid: effectiveOrgUid,
            scopeOrganisation: !!effectiveOrgUid,
            feedScope: feedScope.value,
            profileUserUid:
              props.showItems === "profileUser" ? props.profileUserUid : null,
          });

          if (!isTimelineRequestCurrent(requestGeneration)) return;

          if (timelineResult.fromTimeline) {
            console.log(
              `[TIMELINE_ONLY] Timeline fetch successful: ${timelineResult.items.length} items`
            );

            const pageState = resolveTimelinePageState({
              itemCount: timelineResult.items.length,
              nextCursor: timelineResult.lastDoc,
              sourceHasMore: timelineResult.hasMore,
              previousCursor: lastTimelineDoc.value,
              feedScope: feedScope.value,
              emptyPageCount: emptyFetchCounter.value,
            });
            lastTimelineDoc.value = pageState.nextCursor;
            hasMoreTimelineData.value = pageState.hasMore;
            emptyFetchCounter.value = pageState.emptyPageCount;

            // Following scans are bounded. A sparse page can legitimately be
            // empty while its opaque cursor still points to older content.
            if (timelineResult.items.length === 0) {
              if (pageState.continueAfterEmpty) {
                console.log(
                  "[TIMELINE_ONLY] Continuing bounded sparse Following scan"
                );
                requestTimelineFetch(false);
                return;
              }
              console.log("[TIMELINE_ONLY] Reached end of timeline data");
              hasMoreTimelineData.value = false;
              lastTimelineDoc.value = null;
              isLoadingMoreItems.value = false;
              return;
            }
            emptyFetchCounter.value = 0;

            // Process items with duplicate detection
            const normalizedItems = normalizeTimelineItems(
              timelineResult.items
            );

            // Filter out duplicates
            const newItems = normalizedItems.filter(
              (item) => !processedItemIds.value.has(item.id)
            );

            console.log(
              `[TIMELINE_ONLY] Processing ${newItems.length} new items (${
                normalizedItems.length - newItems.length
              } duplicates filtered out)`
            );

            if (newItems.length === 0) {
              console.log(
                "[TIMELINE_ONLY] No new items after duplicate filtering, stopping"
              );
              hasMoreTimelineData.value = false;
              isLoadingMoreItems.value = false;
              return;
            }

            const finishUsefulRender = isInitialFetch
              ? beginBootStep("useful-render", { scope: feedScope.value })
              : null;
            await processItems(newItems);
            if (!isTimelineRequestCurrent(requestGeneration)) return;

            // Pagination continuity check (log createdAt range for diagnostics)
            try {
              const times = newItems
                .map((it) => it.createdAt?.toMillis?.() || it.createdAt)
                .filter((t) => typeof t === "number")
                .sort((a, b) => b - a);
              if (times.length) {
                console.log(
                  `[TIMELINE_ONLY][Range] New batch createdAt range: ${
                    times[times.length - 1]
                  } -> ${times[0]}`
                );
              }
            } catch (eRange) {
              console.warn(
                "[TIMELINE_ONLY][Range] Failed to compute createdAt range",
                eRange
              );
            }
            distributeItems(isInitialFetch);
            await nextTick();
            await afterBrowserFrame();
            lastTimelineRefreshAt.value = Date.now();
            finishUsefulRender?.({ count: newItems.length, source: "rendered-state" });
            if (isInitialFetch) {
              revalidateFirstTimelinePage(
                timelineResult.revalidatePromise,
                requestGeneration
              );
            }

            console.log(
              `[TIMELINE_ONLY] Timeline fetch completed successfully`
            );
            return;
          }
        } catch (timelineErrorCaught) {
          if (!isTimelineRequestCurrent(requestGeneration)) return;
          console.error(
            `[TIMELINE_ONLY] Timeline fetch failed:`,
            timelineErrorCaught
          );
          // NO FALLBACK - just fail gracefully
          hasMoreTimelineData.value = false;
          timelineError.value =
            timelineErrorCaught?.message || "Failed to load marketplace items.";
          isLoadingMoreItems.value = false;
          return;
        }

        // If we reach here, timeline failed completely
        console.error("[TIMELINE_ONLY] Timeline fetch failed completely");
        hasMoreTimelineData.value = false;
        if (!timelineError.value) {
          timelineError.value = "Failed to load marketplace items.";
        }
      } catch (error) {
        if (!isTimelineRequestCurrent(requestGeneration)) return;
        console.error("[TIMELINE_ONLY] Error in fetchMoreItems:", error);
        timelineError.value =
          error?.message || "Unexpected error while loading items.";
      } finally {
        isLoadingMoreItems.value = false;
        flushQueuedTimelineFetch();
      }
    };

    // Add computed property to detect profile mode
    const isProfileMode = computed(() => props.showItems === "profileUser");

    onMounted(async () => {
      // Reset all state variables for a fresh start
      lastProcessedCount.value = 0;
      itemPositions.value.clear();
      columns.value = [];

      // Set initial loading state to true
      isInitialLoading.value = true;
      isLoadingMoreItems.value = true;

      try {
        if (isItemAuthorityReadsFeatureEnabled && isProfileMode.value) {
          timelineError.value =
            "Profile items are unavailable until their private presentation is implemented.";
          hasMoreProfileData.value = false;
          hasMoreTimelineData.value = false;
          return;
        }
        // PROFILE MODE: Use enhanced timeline approach with user filtering
        if (isProfileMode.value && props.profileUserUid) {
          console.log(
            "[MasonryMarketplace] 👤 PROFILE MODE detected - using enhanced timeline approach"
          );

          // Mark organization UID as loaded immediately if it's available
          if (props.organisationUid) {
            console.log(
              "[MasonryMarketplace] Setting orgUID as loaded for profile:",
              props.organisationUid
            );
            isOrgUIDLoaded.value = true;
          }

          // Mark all data types as loaded immediately since we're using timeline
          isOrgUIDLoaded.value = true;

          console.log(
            "[MasonryMarketplace] Profile mode - all data types marked as loaded"
          );

          // Load essential data for profile mode
          if (!isItemAuthorityReadsFeatureEnabled) {
            preloadOrgTradePermissions().catch(() => {});
            if (isWishlistFeatureEnabled && authUserUid.value) {
              fetchWishlistInBackground();
            }
          }

          // Fetch directly from timeline with user filtering
          const profileGeneration = timelineGeneration.value;
          try {
            const timelineResult = await fetchMarketplaceWithDeadline({
              filters: {}, // No filters needed for profile mode
              batchSize: itemsToLoad.value,
              lastDoc: null,
              organisationUid: props.organisationUid,
              scopeOrganisation: props.showItems === "organisation",
              feedScope: feedScope.value,
              profileUserUid: props.profileUserUid, // This enables profile mode filtering
            });

            if (!isTimelineRequestCurrent(profileGeneration)) return;

            if (timelineResult.fromTimeline) {
              console.log(
                `[MasonryMarketplace] ✅ Profile timeline fetch successful: ${timelineResult.items.length} items`
              );

              // Update pagination state for profile mode (use profile-specific cursors)
              profileLastDoc.value = timelineResult.lastDoc;
              hasMoreProfileData.value = timelineResult.hasMore;

              // Normalize timeline items for compatibility
              const normalizedItems = normalizeTimelineItems(
                timelineResult.items
              );

              console.log(
                `[MasonryMarketplace] 🔄 Processing ${normalizedItems.length} profile timeline items`
              );

              // Process timeline items through the same pipeline as normal items
              await processItems(normalizedItems);
              if (!isTimelineRequestCurrent(profileGeneration)) return;

              // Force layout distribution
              distributeItems(true);
              lastTimelineRefreshAt.value = Date.now();

              console.log(
                "[MasonryMarketplace] 📊 Profile mode timeline initialization completed"
              );
            } else {
              console.warn(
                "[MasonryMarketplace] Profile timeline fetch failed, attempting fallback"
              );
              // Fallback: global fetch then client-filter by owner
              try {
                const fallback = await fetchMarketplaceWithDeadline({
                  filters: {},
                  batchSize: itemsToLoad.value,
                  lastDoc: null,
                  organisationUid: null,
                  scopeOrganisation: false,
                  profileUserUid: null,
                });
                if (!isTimelineRequestCurrent(profileGeneration)) return;
                if (fallback.fromTimeline && fallback.items?.length) {
                  const normalized = normalizeTimelineItems(fallback.items);
                  const ownerItems = normalized.filter(
                    (it) => it.owner === props.profileUserUid
                  );
                  await processItems(ownerItems);
                  if (!isTimelineRequestCurrent(profileGeneration)) return;
                  distributeItems(true);
                }
              } catch (e2) {
                if (!isTimelineRequestCurrent(profileGeneration)) return;
                console.warn(
                  "[MasonryMarketplace] Profile fallback failed:",
                  e2
                );
              }
            }
          } catch (timelineError) {
            if (!isTimelineRequestCurrent(profileGeneration)) return;
            console.error(
              "[MasonryMarketplace] Profile timeline fetch error:",
              timelineError
            );
            // Attempt the same fallback on error
            try {
              const fallback = await fetchMarketplaceWithDeadline({
                filters: {},
                batchSize: itemsToLoad.value,
                lastDoc: null,
                organisationUid: null,
                scopeOrganisation: false,
                profileUserUid: null,
              });
              if (!isTimelineRequestCurrent(profileGeneration)) return;
              if (fallback.fromTimeline && fallback.items?.length) {
                const normalized = normalizeTimelineItems(fallback.items);
                const ownerItems = normalized.filter(
                  (it) => it.owner === props.profileUserUid
                );
                await processItems(ownerItems);
                if (!isTimelineRequestCurrent(profileGeneration)) return;
                distributeItems(true);
              }
            } catch (e3) {
              if (!isTimelineRequestCurrent(profileGeneration)) return;
              console.warn(
                "[MasonryMarketplace] Profile fallback (error path) failed:",
                e3
              );
            }
          }

          // Set up minimal event listeners for profile mode
          if (!isTimelineRequestCurrent(profileGeneration)) return;
          window.removeEventListener("resize", handleResize);
          window.addEventListener("resize", handleResize);

          window.removeEventListener("resize", updateWindowWidth);
          window.addEventListener("resize", updateWindowWidth);

          return; // Exit early for profile mode
        }

        // NORMAL MODE: Continue with existing timeline/store logic
        console.log(
          "[MasonryMarketplace] Normal mode - proceeding with timeline/store fetching"
        );

        // Mark organization UID as loaded immediately if it's available
        if (props.organisationUid) {
          console.log(
            "[MasonryMarketplace] Setting orgUID as loaded immediately:",
            props.organisationUid
          );
          isOrgUIDLoaded.value = true;
        }

        // Enrichment must not hold the first timeline query.
        if (!isItemAuthorityReadsFeatureEnabled) {
          preloadOrgTradePermissions().catch(() => {});
          if (isWishlistFeatureEnabled && authUserUid.value) {
            fetchWishlistInBackground();
          }
        }

        // Check if we should use timeline optimization immediately
        const currentFilters = {
          searchQuery: searchQuery.value,
          filterType: filterType.value,
          sortOrder: sortOrder.value,
          selectedCategory: selectedCategory.value,
          locationFilter: locationFilter.value,
        };

        const useTimelineOptimization = shouldUseTimeline(currentFilters);
        console.log(
          `[MasonryMarketplace] Timeline optimization available: ${useTimelineOptimization}`
        );

        if (useTimelineOptimization) {
          console.log(
            "[MasonryMarketplace] 🚀 Using TIMELINE-FIRST approach - bypassing props"
          );

          // Mark all data types as loaded immediately since we're using timeline
          isOrgUIDLoaded.value = true;

          // Fetch directly from timeline
          const finishTimeline = beginBootStep("timeline", {
            scope: props.showItems,
          });
          const initialGeneration = timelineGeneration.value;
          try {
            const timelineResult = await fetchMarketplaceWithDeadline({
              filters: currentFilters,
              batchSize: itemsToLoad.value,
              lastDoc: null,
              organisationUid: props.organisationUid,
              scopeOrganisation: props.showItems === "organisation",
              feedScope: feedScope.value,
              profileUserUid:
                props.showItems === "profileUser" ? props.profileUserUid : null,
              fallbackFetch: null,
            });

            if (!isTimelineRequestCurrent(initialGeneration)) return;

            if (timelineResult.fromTimeline) {
              console.log(
                `[MasonryMarketplace] ✅ Timeline fetch successful: ${timelineResult.items.length} items`
              );

              // Initialize pagination state for normal mode so scroll can load next pages
              lastTimelineDoc.value = timelineResult.lastDoc || null;
              hasMoreTimelineData.value = !!timelineResult.hasMore;
              console.log(
                `[MasonryMarketplace] 📍 Init cursor set - hasMore: ${hasMoreTimelineData.value}, lastDoc:`,
                lastTimelineDoc.value
              );

              // Normalize timeline items for compatibility
              const normalizedItems = normalizeTimelineItems(
                timelineResult.items
              );

              console.log(
                `[MasonryMarketplace] 🔄 Processing ${normalizedItems.length} timeline items through price calculation pipeline`
              );

              // CRITICAL FIX: Process timeline items through the same pipeline as props items
              // This ensures fetchConvertedPrice is called for timeline services
              const finishUsefulRender = beginBootStep("useful-render", {
                scope: feedScope.value,
              });
              await processItems(normalizedItems);
              if (!isTimelineRequestCurrent(initialGeneration)) return;

              // Force layout distribution
              distributeItems(true);
              await nextTick();
              await afterBrowserFrame();
              finishUsefulRender({
                count: normalizedItems.length,
                source: "rendered-state",
              });
              revalidateFirstTimelinePage(
                timelineResult.revalidatePromise,
                initialGeneration
              );
              finishTimeline({ count: normalizedItems.length });

              console.log(
                `[MasonryMarketplace] 📊 Timeline optimization completed with price calculations`
              );
            } else {
              console.log("[MasonryMarketplace] Timeline fetch failed");
              hasMoreTimelineData.value = false;
              timelineError.value =
                timelineError.value || "Failed to load marketplace items.";
            }
          } catch (error) {
            if (!isTimelineRequestCurrent(initialGeneration)) return;
            console.warn(
              `[MasonryMarketplace] Timeline fetch error:`,
              error
            );
            hasMoreTimelineData.value = false;
            timelineError.value =
              error?.message || "Failed to load marketplace items.";
            finishTimeline({
              errorCode: error?.code || "timeline-error",
            });
          }
        } else {
          // In strict timeline-only mode, always use timeline (fetchMoreItems handles filters)
          await fetchMoreItems(true);
        }

        // Set up event listeners
        if (componentDisposed || !paginationLifecycleActive) return;
        console.log("[Scroll] Pagination listener is lifecycle-owned");

        window.removeEventListener("resize", handleResize);
        window.addEventListener("resize", handleResize);

        window.removeEventListener("resize", updateWindowWidth);
        window.addEventListener("resize", updateWindowWidth);
        window.addEventListener(
          "marketplace:item-updated",
          handleExternalItemUpdated
        );
        // When a brand-new media post is created, full-refresh the grid
        const onNewMedia = () => {
          try {
            // Clear existing items and dedup tracking so the refetch is clean
            processedItems.value = [];
            processedItemIds.value.clear();
            lastTimelineDoc.value = null;
            hasMoreTimelineData.value = true;
            lastProcessedCount.value = 0;
            // Re-fetch from the beginning
            requestTimelineFetch(true);
          } catch (e) {
            console.warn(
              "[Masonry] Failed to react to marketplace:new-media",
              e
            );
          }
        };
        window.addEventListener("marketplace:new-media", onNewMedia);
        // Keep reference to remove on unmount
        newMediaHandlerRef.value = onNewMedia;

        // When an NFT is delisted from NFTGallery, remove its card from the grid
        const onNFTDelisted = (evt) => {
          try {
            const { timelineDocId } = evt?.detail || {};
            if (!timelineDocId) return;
            const idx = processedItems.value.findIndex(
              (it) =>
                it &&
                (it._timelineId === timelineDocId || it.id === timelineDocId)
            );
            if (idx !== -1) {
              processedItems.value.splice(idx, 1);
              forceRedistribute.value = true;
              nextTick(() => distributeItems());
            }
          } catch (e) {
            console.warn(
              "[Masonry] Failed to react to marketplace:nft-delisted",
              e
            );
          }
        };
        window.addEventListener("marketplace:nft-delisted", onNFTDelisted);
        nftDelistedHandlerRef.value = onNFTDelisted;

        // Listen for open-mint-modal event from PostComposer/SellingAction
        const handleOpenMintModal = async (event) => {
          try {
            const {
              mode,
              itemId,
              collection,
              draft,
              composerRef,
              ownerId,
              organisationId,
            } = event.detail || {};
            console.log("[Masonry] marketplace:open-mint-modal received", {
              mode,
              itemId,
              collection,
              hasDraft: !!draft,
            });

            // DRAFT MODE: New media from PostComposer that hasn't been saved yet
            // Don't save now - defer until user confirms in mint modal
            if (mode === "draft" && draft) {
              console.log(
                "[Masonry] Draft mode - opening mint modal with draft preview (no save yet)"
              );

              if (!composerRef) {
                console.warn(
                  "[Masonry] No composerRef provided for draft mode"
                );
                window.$toast?.error?.(
                  "Cannot mint: composer reference missing"
                );
                return;
              }

              // Check if this is a multi-image post (2+ images = batch mint)
              const imageCount = draft.images?.length || 0;
              if (imageCount >= 2) {
                console.log(
                  "[Masonry] Draft has multiple images, opening BatchMintModal",
                  { imageCount }
                );

                // Store draft info for batch mint
                pendingDraftMint.value = {
                  composerRef,
                  ownerId,
                  organisationId,
                  draft,
                };

                // Create a sourceItem-like object for BatchMintModal
                pendingBatchMintItem.value = {
                  images: draft.images,
                  itemName: draft.title,
                  title: draft.title,
                  caption: draft.caption,
                  _isDraft: true,
                  _composerRef: composerRef,
                  _ownerId: ownerId,
                  _organisationId: organisationId,
                };
                showBatchMintModal.value = true;
                return;
              }

              // Single image - use SimpleMintModal
              // Store draft info for later save (when user confirms in SimpleMintModal)
              pendingDraftMint.value = {
                composerRef,
                ownerId,
                organisationId,
                draft,
              };

              // Open the SimpleMintModal with draft preview
              // Note: pendingMint remains null, we use pendingDraftMint for draft mode
              showMintModal.value = true;

              console.log(
                "[Masonry] Draft mode - SimpleMintModal opened with preview:",
                {
                  title: draft.title,
                  hasPreviewImage: !!draft.previewImage,
                  imagesCount: draft.images?.length || 0,
                }
              );
              return;
            }

            // EXISTING ITEM MODE: Item already saved, just open mint modal
            if (!itemId) {
              console.warn("[Masonry] open-mint-modal: no itemId provided");
              return;
            }
            // First refresh the items to get the newly saved one
            await requestTimelineFetch(true);
            // Find the item in processedItems
            const item = processedItems.value.find((i) => i.id === itemId);
            if (item) {
              console.log("[Masonry] Found item, starting mint:", item);
              startMint(item);
            } else {
              // Try to fetch the item directly from Firestore
              const itemDoc = await getDoc(
                doc(null, collection || "images", itemId)
              );
              if (itemDoc.exists()) {
                const itemData = { id: itemDoc.id, ...itemDoc.data() };
                console.log(
                  "[Masonry] Fetched item directly, starting mint:",
                  itemData
                );
                startMint(itemData);
              } else {
                console.warn("[Masonry] Could not find item to mint:", itemId);
                window.$toast?.warning?.(
                  "Could not find item to mint. Please try again."
                );
              }
            }
          } catch (e) {
            console.error("[Masonry] Failed to handle open-mint-modal:", e);
            window.$toast?.error?.(
              "Failed to open mint modal. Please try again."
            );
          }
        };
        window.addEventListener(
          "marketplace:open-mint-modal",
          handleOpenMintModal
        );
        // Store reference for cleanup
        openMintModalHandlerRef.value = handleOpenMintModal;

        // Listen for batch mint modal open event
        const handleOpenBatchMint = () => {
          console.log("[Masonry] marketplace:open-batch-mint received");
          showBatchMintModal.value = true;
        };
        window.addEventListener(
          "marketplace:open-batch-mint",
          handleOpenBatchMint
        );
        // Store reference for cleanup in onBeforeUnmount
        window._batchMintHandler = handleOpenBatchMint;
      } catch (error) {
        if (componentDisposed || !paginationLifecycleActive) return;
        console.error("Error during initialization:", error);
        // Ensure org UID flag doesn't block anything
        isOrgUIDLoaded.value = true;
      } finally {
        isInitialLoading.value = false;
        isLoadingMoreItems.value = false;
        isInitializing.value = false;
        flushQueuedTimelineFetch();
      }
    });

    // Allow user to retry timeline fetch after an error
    const retryTimelineFetch = () => {
      // Reset state and try initial fetch again
      timelineError.value = null;
      hasMoreTimelineData.value = true;
      lastTimelineDoc.value = null;
      processedItems.value = [];
      processedItemIds.value.clear();
      itemsToLoad.value = 12;
      noMatchingItems.value = false;
      lastProcessedCount.value = 0;
      itemPositions.value.clear();
      columns.value = [];
      requestTimelineFetch(true);
    };

    const refreshTimeline = ({ reason = "external-refresh" } = {}) => {
      timelineError.value = null;
      feedScopeCache.delete(feedCacheKey(feedScope.value));
      resetTimelineState(reason);
      itemsToLoad.value = 12;
      hasUserPaginationIntent.value = false;
      return requestTimelineFetch(true);
    };

    expose({ refreshTimeline });

    // initializeWithProps removed (timeline-only)

    // Legacy watchers removed (timeline-only)

    // Products watcher removed

    // Services watcher removed

    // Media watcher removed

    // Prefer consolidated watchers; this flag disables legacy duplicate watchers below
    const useConsolidatedWatchers = true;

    // The selected organisation is global. When it changes from the picker,
    // Dashboard, or Wallet, keep one org-scoped cache and one request
    // generation per organisation.
    watch(
      effectiveFeedOrgUID,
      (newValue, oldValue) => {
        if (!newValue || newValue === oldValue) return;
        isOrgUIDLoaded.value = true;
        handleSelectedOrgChange(newValue);
      },
      { immediate: false }
    );

    // Watch for org mode toggles from either props or UI state
    watch(
      () => isOrgMode.value,
      (nowOrg, wasOrg) => {
        if (useConsolidatedWatchers) return; // handled by consolidated watcher above
        if (nowOrg === wasOrg) return;
        console.log("[MasonryMarketplace] isOrgMode changed", {
          from: wasOrg,
          to: nowOrg,
          orgUid: props.organisationUid || store.getters.currentOrgUID,
        });
        // Reset pagination and pools on mode switch
        lastTimelineDoc.value = null;
        hasMoreTimelineData.value = true;
        processedItems.value = [];
        processedItemIds.value.clear();
        requestTimelineFetch(true).catch((e) =>
          console.warn(
            "[MasonryMarketplace] Fetch after org-mode toggle failed:",
            e
          )
        );
      },
      { immediate: false }
    );

    // Watch for showItems changes to enter/leave org mode
    watch(
      () => props.showItems,
      (newMode, oldMode) => {
        if (useConsolidatedWatchers) return; // sync-only watcher above will handle
        console.log("[MasonryMarketplace] showItems changed", {
          from: oldMode,
          to: newMode,
          orgUid: props.organisationUid,
        });
        if (newMode === "organisation" && props.organisationUid) {
          console.log(
            "[MasonryMarketplace] Entering organisation mode - fetching org timeline"
          );
          lastTimelineDoc.value = null;
          hasMoreTimelineData.value = true;
          processedItems.value = [];
          processedItemIds.value.clear();
          requestTimelineFetch(true).catch((e) =>
            console.warn("[MasonryMarketplace] Org fetch failed:", e)
          );
          return;
        }
        if (oldMode === "organisation" && newMode !== "organisation") {
          console.log(
            "[MasonryMarketplace] Leaving organisation mode → switching to global feed"
          );
          // Clear org-scoped pagination and fetch a fresh global page
          lastTimelineDoc.value = null;
          hasMoreTimelineData.value = true;
          processedItems.value = [];
          processedItemIds.value.clear();
          requestTimelineFetch(true).catch((e) =>
            console.warn(
              "[MasonryMarketplace] Global fetch failed after leaving org:",
              e
            )
          );
        }
      },
      { immediate: false }
    );

    // Watch for changes in economy restrictions
    watch(
      () => store.getters["UIState/isTradeAllowed"],
      async (newVal) => {
        console.log(`[Economy] Trade allowed state changed to: ${newVal}`);
        // Re-check permissions when trade restrictions change
        if (!isItemAuthorityReadsFeatureEnabled) {
          await preloadOrgTradePermissions();
        }
        // Note: We don't need to call updateFilteredItems directly
        // The distributeItems function will be called by the filteredItems computed
        distributeItems();
      }
    );

    watch(
      authUserUid,
      (newUid, oldUid) => {
        if (newUid === oldUid) return;

        feedScopeCache.clear();
        loadedFeedOrgUID.value = effectiveFeedOrgUID.value;
        followingEmptyState.value = "unknown";
        followingStateRequestGeneration += 1;
        wishlistRequestGeneration += 1;
        Object.keys(isWishlisted).forEach((key) => {
          delete isWishlisted[key];
        });
        resetTimelineState("authenticated-user-changed");
        isInitialLoading.value = true;

        if (
          newUid &&
          isWishlistFeatureEnabled &&
          !isItemAuthorityReadsFeatureEnabled
        ) {
          console.log("User UID changed, fetching wishlist items for:", newUid);
          fetchWishlistItems().catch(() => {});
        }

        if (feedScope.value !== "org" || effectiveFeedOrgUID.value) {
          nextTick(() => requestTimelineFetch(true));
        }
      },
      { flush: "sync" }
    );

    // Watch for profile user changes and reset profile pagination state
    watch(
      () => props.profileUserUid,
      (newUserUid, oldUserUid) => {
        if (newUserUid !== oldUserUid && props.showItems === "profileUser") {
          console.log("[Profile] User changed, resetting pagination state");
          hasMoreProfileData.value = true;
          profileLastDoc.value = null;
          processedItems.value = [];
          lastProcessedCount.value = 0;
          itemPositions.value.clear();

          // Reset other pagination states
          lastTimelineDoc.value = null;
          hasMoreTimelineData.value = true;
          emptyFetchCounter.value = 0;

          // Reset layout
          columns.value = [];
          itemsToLoad.value = 12;
          // Trigger a fresh profile fetch
          requestTimelineFetch(true).catch((e) =>
            console.warn("[Profile] Initial fetch after user change failed:", e)
          );
        }
      }
    );

    onBeforeUnmount(() => {
      componentDisposed = true;
      paginationLifecycleActive = false;
      queuedTimelineFetch = null;
      resumeTimelineFetch = null;
      timelineGeneration.value += 1;
      followingStateRequestGeneration += 1;
      wishlistRequestGeneration += 1;
      feedScopeCache.clear();
      clearCompactDeleteConfirmation();
      marketplaceItemDeletion.clear();
      window.removeEventListener("resize", handleResize);
      detachPaginationScrollListener();
      window.removeEventListener("resize", updateWindowWidth);
      window.removeEventListener(
        "marketplace:item-updated",
        handleExternalItemUpdated
      );
      if (newMediaHandlerRef.value) {
        window.removeEventListener(
          "marketplace:new-media",
          newMediaHandlerRef.value
        );
        newMediaHandlerRef.value = null;
      }
      if (nftDelistedHandlerRef.value) {
        window.removeEventListener(
          "marketplace:nft-delisted",
          nftDelistedHandlerRef.value
        );
        nftDelistedHandlerRef.value = null;
      }
      if (openMintModalHandlerRef.value) {
        window.removeEventListener(
          "marketplace:open-mint-modal",
          openMintModalHandlerRef.value
        );
        openMintModalHandlerRef.value = null;
      }
      if (window._batchMintHandler) {
        window.removeEventListener(
          "marketplace:open-batch-mint",
          window._batchMintHandler
        );
        window._batchMintHandler = null;
      }
      window.removeEventListener(
        "social:following-changed",
        handleFollowingChanged
      );
      window.removeEventListener("pageshow", handlePageResume);
      document.removeEventListener("visibilitychange", handleVisibilityResume);

      // Clear any pending search debounce
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = null;
      }

      // Clean up other references to prevent memory leaks
      columns.value = [];
      processedItems.value = [];
      Object.keys(convertedPrices).forEach(
        (key) => delete convertedPrices[key]
      );
      Object.keys(isWishlisted).forEach((key) => delete isWishlisted[key]);
    });

    // Close overlay + clear flag when navigating away (keep-alive deactivation)
    onDeactivated(() => {
      deactivatePaginationLifecycle();
      if (slideshowOverlayVisible.value) {
        slideshowOverlayVisible.value = false;
        slideshowNavigationItems.value = [];
        store.dispatch("UIState/setLocalOverlayActive", false);
      }
    });

    // Auto-open overlay from URL ?item=<id> on mount and on route changes
    let autoOpenOverlayGeneration = 0;
    const autoOpenOverlayFromUrl = async (
      itemId,
      itemOrgId = null,
      generation = ++autoOpenOverlayGeneration
    ) => {
      if (!itemId) return;
      // If overlay is already showing this item, skip
      // Authority placements route by item and organisation; legacy media
      // posts and NFTs in the same feed route by their own id.
      const matchesRoute = (item) =>
        isItemAuthorityReadsFeatureEnabled && itemOrgId
          ? item?._authorityPresentation === true &&
            item.itemId === itemId &&
            item.organisation === itemOrgId
          : item?.id === itemId;
      if (slideshowOverlayVisible.value && matchesRoute(slideshowItem.value))
        return;

      // Try to find the item in already-loaded items first
      const allItems = processedItems.value || [];
      const found = allItems.find(matchesRoute);
      if (found) {
        captureSlideshowNavigationContext(found);
        slideshowItem.value = found;
        slideshowOverlayVisible.value = true;
        store.dispatch("UIState/setLocalOverlayActive", true);
        return;
      }

      // Not in local data — resolve the exact private-authority placement.
      try {
        const fetched = await fetchItemById(itemId, itemOrgId);
        if (
          generation !== autoOpenOverlayGeneration ||
          route.query.item !== itemId ||
          (route.query.itemOrg || null) !== itemOrgId
        ) {
          return;
        }
        if (fetched) {
          slideshowNavigationItems.value = [];
          slideshowItem.value = fetched;
          slideshowOverlayVisible.value = true;
          store.dispatch("UIState/setLocalOverlayActive", true);
        }
      } catch (e) {
        console.warn(
          "[MasonryMarketplace] Failed to auto-open overlay for",
          itemId,
          e
        );
      }
    };

    // Watch route query for changes to ?item=
    watch(
      () => [route.query.item, route.query.itemOrg || null],
      ([newItemId, newItemOrgId]) => {
        const generation = ++autoOpenOverlayGeneration;
        if (
          newItemId &&
          !slideshowOverlayVisible.value
        ) {
          autoOpenOverlayFromUrl(newItemId, newItemOrgId, generation);
        } else if (!newItemId && slideshowOverlayVisible.value) {
          // URL lost ?item (e.g. browser back) — close overlay
          slideshowOverlayVisible.value = false;
          slideshowNavigationItems.value = [];
          store.dispatch("UIState/setLocalOverlayActive", false);
        }
      }
    );

    // Check URL on mount (after items may have loaded)
    // Use nextTick so initial item load has a chance to populate processedItems
    const refreshStaleTimeline = () => {
      if (
        componentDisposed ||
        !paginationLifecycleActive ||
        document.visibilityState === "hidden" ||
        Date.now() - lastTimelineRefreshAt.value < FEED_CACHE_TTL ||
        !processedItems.value.length
      ) {
        return;
      }
      const generation = timelineGeneration.value;
      const effectiveOrgUid = isOrgMode.value
        ? effectiveFeedOrgUID.value
        : null;
      revalidateFirstTimelinePage(
        fetchMarketplaceWithDeadline({
          filters: {
            searchQuery: searchQuery.value,
            filterType: filterType.value,
            sortOrder: sortOrder.value,
            selectedCategory: selectedCategory.value,
            locationFilter: locationFilter.value,
          },
          batchSize: itemsToLoad.value,
          lastDoc: null,
          organisationUid: effectiveOrgUid,
          scopeOrganisation: !!effectiveOrgUid,
          feedScope: feedScope.value,
          preferCache: false,
        }),
        generation
      );
    };
    const handlePageResume = () => refreshStaleTimeline();
    const handleVisibilityResume = () => {
      if (document.visibilityState === "visible") refreshStaleTimeline();
    };

    onMounted(() => {
      window.addEventListener(
        "social:following-changed",
        handleFollowingChanged
      );
      window.addEventListener("pageshow", handlePageResume);
      document.addEventListener("visibilitychange", handleVisibilityResume);
      nextTick(() => {
        const itemId = route.query.item;
        if (itemId) {
          const generation = ++autoOpenOverlayGeneration;
          autoOpenOverlayFromUrl(
            itemId,
            route.query.itemOrg || null,
            generation
          );
        }
      });
    });

    const showOtherUserPrice = computed(() => windowWidth.value > 590);

    const openOverlay = () => {
      console.log("Opening overlay...");
      currentStep.value = "initial";
      showOverlay.value = true;
      store.dispatch("toggleNavbarVisibility", false);
    };

    const lastComposerHydrationKey = ref(null);
    const hydrateComposerIfReady = async () => {
      if (!showOverlay.value) return;
      if (!postComposerRef.value) return;
      if (!currentProduct.value || currentProduct.value.type !== "media")
        return;
      if (lastComposerHydrationKey.value === composerKey.value) return;

      await nextTick();
      try {
        postComposerRef.value?.loadFromItem?.(currentProduct.value);
        lastComposerHydrationKey.value = composerKey.value;
      } catch (e) {
        console.warn("[Masonry] Failed to hydrate PostComposer", e);
      }
    };

    watch(
      [
        () => showOverlay.value,
        () => currentProduct.value?.id,
        () => composerKey.value,
        () => postComposerRef.value,
      ],
      () => {
        hydrateComposerIfReady();
      }
    );

    const goToNextStep = async () => {
      if (!addFormRef.value.handleFormValidation()) {
        return;
      }
      if (currentProduct.value.type === "product") {
        if (currentStep.value === "initial") {
          currentStep.value = "preview";
        }
      } else if (currentProduct.value.type === "service") {
        // For service form, handle each step transition
        console.log(
          `[MasonryMarketplace] Processing next step for service form, current step: ${currentStep.value}`
        );

        if (currentStep.value === "initial") {
          // Validate the form before proceeding
          if (addFormRef.value.handleFormValidation()) {
            currentStep.value = "availability";
            console.log("[MasonryMarketplace] Moving to availability step");
          }
        } else if (currentStep.value === "availability") {
          // If no days are selected, skip to preview
          if (
            addFormRef.value.selectedDays &&
            addFormRef.value.selectedDays.filter((day) => day).length === 0
          ) {
            if (addFormRef.value.handleFormValidation()) {
              await addFormRef.value.gatherServiceDataForPreview();
              currentStep.value = "preview";
              console.log(
                "[MasonryMarketplace] No days selected, moving to preview step"
              );
            }
          } else {
            // If days are selected, move to customization
            if (addFormRef.value.handleFormValidation()) {
              currentStep.value = "customization";
              console.log("[MasonryMarketplace] Moving to customization step");
            }
          }
        } else if (currentStep.value === "customization") {
          // From customization, move to preview
          if (addFormRef.value.handleFormValidation()) {
            await addFormRef.value.gatherServiceDataForPreview();
            console.log(
              "[MasonryMarketplace] Gathered service data for preview"
            );
            currentStep.value = "preview";
          }
        }
      }
    };

    const goToPreviousStep = () => {
      console.log("Going to previous step...");

      // Delete step always goes back to initial for all form types
      if (currentStep.value === "delete") {
        currentStep.value = "initial";
        return;
      }

      // Handle specific form types
      if (currentProduct.value.type === "product") {
        if (currentStep.value === "preview") {
          currentStep.value = "initial";
        }
      } else if (currentProduct.value.type === "service") {
        if (currentStep.value === "customization") {
          currentStep.value = "availability";
        } else if (currentStep.value === "availability") {
          currentStep.value = "initial";
        } else if (currentStep.value === "preview") {
          currentStep.value = "availability";
        }
      } else if (currentProduct.value.type === "media") {
        if (currentStep.value === "preview") {
          currentStep.value = "initial";
          // Ensure PostComposer switches back to its internal edit mode
          try {
            postComposerRef.value?.backToEdit?.();
          } catch {}
        }
      }
    };

    const closeOverlay = () => {
      console.log("Closing overlay...");
      showOverlay.value = false;
      currentStep.value = "initial";
      store.dispatch("clearProduct");
      store.dispatch("toggleNavbarVisibility", true);
      currentProduct.value = null;
    };

    const toggleLoginModal = () => {
      store.dispatch("toggleLoginModal", true);
    };

    const fetchConvertedPrice = async (item) => {
      // OPTIMIZATION: Skip price calculation for media items early in the pipeline
      // Media items (images, videos, audio) typically don't have prices and don't need conversion
      if (item.type === "media" || item.mediaType) {
        // Only process media items that explicitly have a price > 0
        if (!item.price || item.price === 0) {
          console.log(
            `[PRICE_OPTIMIZATION] ⚡ Early skip for media item ${item.id} - no price needed`
          );
          // Set to null immediately without further processing
          convertedPrices[item.id] = { convertedPrice: null, coinSymbol: null };
          return;
        }
        // If media item has a price, log it as unusual but continue processing
        console.log(
          `[PRICE_OPTIMIZATION] 💰 Media item ${item.id} has price ${item.price} - processing`
        );
      }

      // OPTIMIZATION: Skip items that already have valid converted prices
      if (
        convertedPrices[item.id] &&
        typeof convertedPrices[item.id].convertedPrice === "number" &&
        !isNaN(convertedPrices[item.id].convertedPrice)
      ) {
        console.log(
          `[PRICE_OPTIMIZATION] ✅ Price already calculated for ${item.type} ${
            item.id
          }: ${convertedPrices[item.id].convertedPrice} ${
            convertedPrices[item.id].coinSymbol
          }`
        );
        return; // Skip if we already have a valid price
      }

      // Fetch buyer and seller UIDs
      const buyerOrgUID =
        props.organisationUid || store.getters.currentUserOrgUID; // Use organization UID, not user UID
      const sellerOrgUID = item.itemOwner || item.organisation; // Ensure at least one UID is set

      // Enhanced debug logs to track UID values and item structure (only for items that need pricing)
      console.log(`[SERVICE_PRICE_DEBUG] 🔍 Processing item ${item.id}:`, {
        type: item.type,
        price: item.price,
        priceType: typeof item.price,
        organisation: item.organisation,
        buyerOrgUID,
        sellerOrgUID,
        fromTimeline: item._fromTimeline,
        coinSymbol: item.coinSymbol,
        isOwner: item.owner === authUserUid.value,
        currentConvertedPrice: convertedPrices[item.id],
      });

      // Skip if price is missing or invalid (for products/services)
      if (
        item.price === undefined ||
        item.price === null ||
        isNaN(item.price) ||
        item.price === 0
      ) {
        console.log(
          `[SERVICE_PRICE_DEBUG] ❌ Skipping item ${item.id} - invalid price:`,
          {
            price: item.price,
            priceType: typeof item.price,
            isNaN: isNaN(item.price),
          }
        );
        convertedPrices[item.id] = { convertedPrice: null, coinSymbol: null };
        return;
      }

      // Check if both UIDs are available before proceeding
      if (!buyerOrgUID || !sellerOrgUID) {
        console.error(
          `[SERVICE_PRICE_DEBUG] 🚫 Organization UIDs missing for item ${item.id}:`,
          {
            buyerOrgUID,
            sellerOrgUID,
            itemOrganisation: item.organisation,
            itemOwner: item.itemOwner,
          }
        );
        convertedPrices[item.id] = { convertedPrice: null, coinSymbol: null };
        return; // Exit the function early if UIDs are missing
      }

      // Set loading state for price calculation
      favouritesLoading[`price_${item.id}`] = true;

      try {
        // Fetch the converted price from the utility function
        const { convertedPrice, coinSymbol } = await getConvertedPrice(
          item,
          sellerOrgUID,
          item.price
        );

        // Check if the converted price is valid
        if (
          !isNaN(convertedPrice) &&
          convertedPrice !== null &&
          convertedPrice !== undefined
        ) {
          // Store the converted price in the reactive object
          convertedPrices[item.id] = { convertedPrice, coinSymbol };

          // Force UI update by triggering reactivity
          nextTick(() => {
            distributeItems(); // Re-distribute items after fetching price
          });
        } else {
          console.warn(
            `[SERVICE_PRICE_DEBUG] ⚠️ Invalid price calculated for item ${item.id}:`,
            {
              convertedPrice,
              coinSymbol,
              isNaN: isNaN(convertedPrice),
            }
          );
          convertedPrices[item.id] = { convertedPrice: null, coinSymbol: null };
        }
      } catch (error) {
        // Handle errors during the price conversion process
        console.error(
          `[SERVICE_PRICE_DEBUG] 💥 Failed to fetch converted price for item ${item.id}:`,
          error
        );
        convertedPrices[item.id] = { convertedPrice: null, coinSymbol: null };
      } finally {
        // Clear loading state
        favouritesLoading[`price_${item.id}`] = false;
      }
    };

    const refreshPage = () => {
      location.reload();
    };
    // Holder for dynamically added event handler
    const newMediaHandlerRef = ref(null);
    // Holder for open-mint-modal event handler
    const openMintModalHandlerRef = ref(null);
    // Holder for nft-delisted event handler
    const nftDelistedHandlerRef = ref(null);

    // React to external edits (e.g., ProductView) and merge into local state
    const handleExternalItemUpdated = (evt) => {
      try {
        const payload = evt && evt.detail;
        if (
          !payload ||
          !processedItems.value ||
          processedItems.value.length === 0
        )
          return;

        const targetKey = payload.originalPath;
        const idx = processedItems.value.findIndex((it) => {
          if (!it) return false;
          if (it._originalPath) return it._originalPath === targetKey;
          const fallbackKey = `${it.type || "unknown"}/${it.id}`;
          return fallbackKey === targetKey;
        });
        if (idx === -1) return;

        const prev = processedItems.value[idx] || {};
        const next = { ...prev };
        if (typeof payload.itemName === "string") {
          next.itemName = payload.itemName;
          next.title = payload.itemName;
        }
        if (typeof payload.itemDescription === "string") {
          next.itemDescription = payload.itemDescription;
        }
        if (typeof payload.description === "string") {
          next.description = payload.description;
        } else if (typeof payload.itemDescription === "string") {
          // Fallback for callers that don't send description separately (e.g., ProductView)
          next.description = payload.itemDescription;
        }
        if (typeof payload.caption === "string") {
          next.caption = payload.caption;
        }
        if (Array.isArray(payload.imageCaptions)) {
          next.imageCaptions = payload.imageCaptions;
        }
        if (Array.isArray(payload.images)) {
          next.images = payload.images;
          if (payload.images[0]) {
            next.thumbnail = payload.images[0];
          } else if (!payload.images.length) {
            next.thumbnail = "";
          }
        }
        if (Array.isArray(payload.mediaVariants)) {
          next.mediaVariants = payload.mediaVariants;
        }
        if (
          payload.variants &&
          typeof payload.variants === "object" &&
          !Array.isArray(payload.variants)
        ) {
          next.variants = payload.variants;
        }
        if (Array.isArray(payload.mediaThumbnails)) {
          next.mediaThumbnails = payload.mediaThumbnails;
        }
        if (typeof payload.thumbnailIndex === "number") {
          next.thumbnailIndex = payload.thumbnailIndex;
        }
        if (typeof payload.mediaType === "string") {
          next.mediaType = payload.mediaType;
        }
        if (typeof payload.mediaUrl === "string") {
          next.mediaUrl = payload.mediaUrl;
        }
        if (typeof payload.audioUrl === "string") {
          next.audioUrl = payload.audioUrl;
        }
        if (Array.isArray(payload.documentUrls)) {
          next.documentUrls = payload.documentUrls;
        }
        if (Array.isArray(payload.documentMetadata)) {
          next.documentMetadata = payload.documentMetadata;
        }
        // Embed-specific fields (YouTube, SoundCloud)
        if (typeof payload.embedHtml === "string") {
          next.embedHtml = payload.embedHtml;
        }
        if (typeof payload.embedUrl === "string") {
          next.embedUrl = payload.embedUrl;
        }
        if (
          typeof payload.embedProvider === "string" ||
          payload.embedProvider === null
        ) {
          next.embedProvider = payload.embedProvider;
        }
        if (typeof payload.embedIsVertical === "boolean") {
          next.embedIsVertical = payload.embedIsVertical;
        }
        if (typeof payload.thumbnailUrl === "string") {
          next.thumbnailUrl = payload.thumbnailUrl;
        }

        processedItems.value[idx] = next;
        forceRedistribute.value = true;
        nextTick(() => distributeItems());
      } catch (e) {
        console.warn("[Masonry] Failed to merge external item update:", e);
      }
    };

    const processItems = async (items) => {
      // Filter out invalid items (must have an ID)
      const validItems = items.filter((item) => item && item.id);

      // Filter out items we already processed using the global tracking set
      const newItems = validItems.filter(
        (item) =>
          !processedItemIds.value.has(item.id) &&
          !inFlightItemIds.value.has(item.id)
      );

      // Add new item IDs to the tracking set
      newItems.forEach((item) => {
        processedItemIds.value.add(item.id);
        inFlightItemIds.value.add(item.id);
      });

      // Log the types of items being processed to help debug
      const itemTypeCounts = {
        product: newItems.filter((item) => item.type === "product").length,
        service: newItems.filter((item) => item.type === "service").length,
        media: newItems.filter(
          (item) => item.type === "media" || item.mediaType
        ).length,
      };
      console.log(
        `[Processing] New items by type: ${JSON.stringify(itemTypeCounts)}`
      );

      if (newItems.length === 0) {
        console.log(
          "[Processing] No new items to process after duplicate filtering"
        );
        return;
      }

      // Timeline cards do not render ownerData. Append immediately, then enrich
      // prices without keeping the initial skeleton visible.
      const newProcessedItems = newItems.map((item) => ({ ...item }));
      const pricedItems = [];
      newItems.forEach((item) => {
        if (item._authorityPresentation) {
          // The server-fixed atomic listing is already the complete display
          // authority. Never call legacy Wallet/exchange readers to enrich it.
          convertedPrices[item.id] = {
            convertedPrice: null,
            coinSymbol: item.currencySymbol || item.currencyCode || null,
          };
        } else if (
          (item.type === "media" || item.mediaType) &&
          !(item.price > 0)
        ) {
          convertedPrices[item.id] = { convertedPrice: null, coinSymbol: null };
        } else {
          pricedItems.push(item);
        }
      });

      // Merge and dedupe by stable key (originalPath or type/id), keep newest
      const combined = [...processedItems.value, ...newProcessedItems];
      const byKey = new Map();
      for (const it of combined) {
        if (!it) continue;
        const key = it._originalPath || `${it.type || "unknown"}/${it.id}`;
        const existing = byKey.get(key);
        if (!existing) {
          byKey.set(key, it);
        } else {
          const eTs = existing?.createdAt?.toMillis
            ? existing.createdAt.toMillis()
            : existing?.createdAt?.seconds
            ? existing.createdAt.seconds * 1000
            : 0;
          const nTs = it?.createdAt?.toMillis
            ? it.createdAt.toMillis()
            : it?.createdAt?.seconds
            ? it.createdAt.seconds * 1000
            : 0;
          if (nTs > eTs) byKey.set(key, it);
        }
      }
      processedItems.value = Array.from(byKey.values()).sort((a, b) => {
        const aTimestamp = a?.createdAt?.toMillis
          ? a.createdAt.toMillis()
          : a?.createdAt?.seconds
          ? a.createdAt.seconds * 1000
          : 0;

        const bTimestamp = b?.createdAt?.toMillis
          ? b.createdAt.toMillis()
          : b?.createdAt?.seconds
          ? b.createdAt.seconds * 1000
          : 0;

        // Sort by timestamp descending (newest first)
        return bTimestamp - aTimestamp;
      });

      // Clear in-flight markers for processed batch
      newItems.forEach((item) => inFlightItemIds.value.delete(item.id));

      // A timeline page waits only briefly for signed item photos. Cards that
      // arrived without theirs get them here once the signing completes.
      newItems
        .filter(
          (item) =>
            item._authorityPresentation === true &&
            !(Array.isArray(item.images) && item.images.length > 0) &&
            Array.isArray(item.mediaAssetIds) &&
            item.mediaAssetIds.length > 0
        )
        .forEach((item) => {
          itemMediaUrlResolver
            .resolveImageUrls(item.mediaAssetIds, { limit: 4 })
            .then((images) => {
              if (images.length === 0) return;
              processedItems.value = processedItems.value.map((current) =>
                current.id === item.id &&
                !(Array.isArray(current.images) && current.images.length > 0)
                  ? Object.freeze({ ...current, images, image: images[0] })
                  : current
              );
            })
            .catch(() => {});
        });

      if (pricedItems.length) {
        const finishPrices = beginBootStep("prices", {
          count: pricedItems.length,
        });
        Promise.allSettled(
          pricedItems.map((item) => fetchConvertedPrice(item))
        ).then(() => finishPrices({ count: pricedItems.length }));
      }

      console.log(
        `[Processing] Finished processing ${newProcessedItems.length} new items. Total: ${processedItems.value.length}`
      );
    };

    // Removed props-based allItems and related helpers in timeline-only mode

    const handlePriceClicked = async (eventData) => {
      console.log("EVENT DATA - priceclicked");
      console.log(eventData);
      if (!eventData.product || !eventData.product.owner) {
        console.error("Product data is missing in eventData:", eventData);
        return;
      }

      // Extract all values from eventData
      const product = eventData.product;
      const productPrice =
        eventData.productPrice !== undefined
          ? eventData.productPrice
          : product.price;
      const convertedPrice = eventData.convertedPrice;
      const sellerCoinSymbol = eventData.sellerCoinSymbol || "SellerCoinSymbol";
      const buyerCoinSymbol = eventData.buyerCoinSymbol || "BuyerCoinSymbol";

      console.log("SellerCoinSymbol in handlePriceClicked:", eventData);
      console.log("EXACT VALUES BEING USED:", {
        productPrice, // Original price in SELLER's coins
        convertedPrice, // Converted price in BUYER's coins
      });

      // Create the payload with explicit values to ensure no data is lost
      const actionPayload = {
        product,
        // Original price in SELLER's coins - what the seller will receive
        productPrice: productPrice || 0,
        // Converted price in BUYER's coins - what the buyer will pay
        convertedPrice: convertedPrice || 0,
        buyerCoinSymbol: buyerCoinSymbol || "",
        sellerCoinSymbol: sellerCoinSymbol || "",
      };

      // First fetch the other user's data
      await store.dispatch("UIState/fetchOtherUserData", actionPayload);

      // Then check if the other organization allows trades
      // This is important to do here before opening messages, so the status is ready
      if (product && product.owner) {
        console.log(
          "Checking cross-org trade status for product owner:",
          product.owner
        );
        const ownerData = {
          uid: product.owner,
        };
        await store.dispatch("UIState/checkAndSetOtherOrgTrades", ownerData);
      }

      // Finally open messages and show interactive message
      await store.dispatch("UIState/setMessagesOpen", true);
      await store.dispatch("UIState/setMessagesMaximised", true);
      await store.dispatch("UIState/setShowInteractiveMessage", true);
    };

    // Legacy function name kept for compatibility - now opens overlay instead of navigating
    const redirectToProductView = async (item) => {
      console.log("Opening overlay for item:", item);

      // For P5.js sketches, open in a new tab
      if (item.type === "multimedia" && item.p5SketchUrl) {
        window.open(item.p5SketchUrl, "_blank");
        return;
      }

      // Open overlay for all item types
      if (item.id) {
        openImageSlideshow(item);
      } else {
        console.error("Missing item ID in redirectToProductView", item);
      }
    };

    const handleAuthorityPurchaseSettled = () => {
      refreshTimeline({ reason: "authority-purchase-settled" });
    };

    const selectMarketplaceCard = (item) => {
      openImageSlideshow(item);
    };

    // Open image slideshow overlay instead of navigating to ProductView
    const openImageSlideshow = (item) => {
      // For P5.js sketches, still open in a new tab
      if (item.type === "multimedia" && item.p5SketchUrl) {
        window.open(item.p5SketchUrl, "_blank");
        return;
      }

      captureSlideshowNavigationContext(item);
      slideshowItem.value = item;
      slideshowOverlayVisible.value = true;

      store.dispatch("UIState/setLocalOverlayActive", true);
      openItemOverlayRoute(
        router,
        route,
        item.itemId || item.id,
        item._authorityPresentation ? item.organisation : null
      ).catch(() => {});
      nextTick(() => prefetchNextSlideshowItem());
    };

    // Close overlay and clean up URL + Vuex flag
    const closeLocalOverlay = (closeData) => {
      const closingItem = slideshowItem.value;
      const isEmbed =
        closingItem &&
        (closingItem.mediaType === "embed" || closingItem.embedHtml);
      const isAudio =
        closingItem &&
        closingItem.mediaType === "audio" &&
        !!closingItem.audioUrl;
      const wasPlaying =
        closeData?.mediaIsPlaying ??
        closeData?.embedIsPlaying ??
        closeData?.audioIsPlaying ??
        false;
      const currentTime =
        closeData?.mediaCurrentTime ??
        (isAudio ? closeData?.audioCurrentTime : closeData?.embedCurrentTime) ??
        0;

      if ((isEmbed || isAudio) && wasPlaying) {
        const activeFpItem = store.getters["UIState/floatingPlayerItem"];
        const fpHasDifferentItem =
          activeFpItem && activeFpItem.id !== closingItem?.id;
        if (!fpHasDifferentItem) {
          store.dispatch("UIState/playInFloatingPlayer", {
            item: closingItem,
            startTime: Math.floor(currentTime),
          });
          console.log(
            "[FloatingPlayer] dispatched playInFloatingPlayer with startTime:",
            Math.floor(currentTime)
          );
        }
      }

      slideshowOverlayVisible.value = false;
      slideshowNavigationItems.value = [];
      store.dispatch("UIState/setLocalOverlayActive", false);
      closeItemOverlayRoute(router, route);
      console.log("[FloatingPlayer] overlay closed");
    };

    // Handle edit from slideshow overlay
    const handleSlideshowEdit = (item) => {
      closeLocalOverlay();
      editItem(item);
    };

    // Handle delete from slideshow overlay
    const handleSlideshowDelete = (item) => {
      closeLocalOverlay();
      currentProduct.value = item;
      confirmDelete();
    };

    // Handle mint from slideshow overlay
    const handleSlideshowMint = (item) => {
      closeLocalOverlay();
      // For multi-image posts, open BatchMintModal
      if (isMultiImageItem(item)) {
        startBatchMintFromPost(item);
      } else {
        // Single image: use regular mint flow
        startMint(item);
      }
    };

    // Handle favourites update from slideshow overlay
    const handleSlideshowFavouritesUpdated = async () => {
      if (!isWishlistFeatureEnabled) return;
      // Refresh the isWishlisted state for the current item from Firestore
      if (slideshowItem.value?.id && authUserUid.value) {
        try {
          const wishlistDocRef = doc(
            null,
            "users",
            authUserUid.value,
            "Wishlist",
            slideshowItem.value.id
          );
          const wishlistDoc = await getDoc(wishlistDocRef);
          isWishlisted[slideshowItem.value.id] = wishlistDoc.exists();
        } catch (error) {
          console.error("Error checking wishlist status:", error);
        }
      }
    };

    const slideshowHasMoreSourceItems = computed(() =>
      props.showItems === "profileUser"
        ? hasMoreProfileData.value
        : hasMoreTimelineData.value
    );
    function captureSlideshowNavigationContext(item) {
      const renderedItems = restoreRoundRobinFeedOrder(columns.value);
      slideshowNavigationItems.value = renderedItems.some(
        (renderedItem) => renderedItem?.id === item?.id
      )
        ? renderedItems
        : [];
    }
    const syncSlideshowNavigationContext = () => {
      const renderedItems = restoreRoundRobinFeedOrder(columns.value);
      slideshowNavigationItems.value = mergeFeedNavigationContext({
        currentItems: slideshowNavigationItems.value,
        visibleItems: renderedItems,
        currentId: slideshowItem.value?.id,
      });
    };
    watch(
      [slideshowOverlayVisible, () => slideshowItem.value?.id, columns],
      ([visible, currentId]) => {
        if (!visible || !currentId) return;
        syncSlideshowNavigationContext();
        preloadAdjacentOverlayItems({
          items: slideshowNavigationItems.value,
          currentId,
        });
      },
      { flush: "post" }
    );
    const slideshowCurrentItemIndex = computed(() =>
      slideshowNavigationItems.value.findIndex(
        (item) => item?.id === slideshowItem.value?.id
      )
    );
    const canNavigateSlideshowPrevious = computed(() =>
      Boolean(slideshowCurrentItemIndex.value > 0)
    );
    const canNavigateSlideshowNext = computed(() =>
      Boolean(
        slideshowCurrentItemIndex.value >= 0 &&
          (slideshowCurrentItemIndex.value <
            slideshowNavigationItems.value.length - 1 ||
            itemsToLoad.value < processedItems.value.length ||
            slideshowHasMoreSourceItems.value)
      )
    );

    const setSlideshowItem = (item) => {
      if (!item?.id) return;
      slideshowItem.value = item;
      replaceItemOverlayRoute(
        router,
        route,
        item.itemId || item.id,
        item._authorityPresentation ? item.organisation : null
      ).catch(() => {});
    };

    let slideshowPaginationPromise = null;
    const loadMoreSlideshowItems = async () => {
      if (slideshowPaginationPromise) return slideshowPaginationPromise;
      slideshowPaginationPromise = (async () => {
        isLoadingSlideshowNextItem.value = true;
        markUserPaginationIntent();
        try {
          for (let attempt = 0; attempt < 3; attempt += 1) {
            if (
              findAdjacentFeedItem(
                slideshowNavigationItems.value,
                slideshowItem.value?.id,
                "next"
              )
            )
              return true;

            if (itemsToLoad.value < processedItems.value.length) {
              itemsToLoad.value = Math.min(
                itemsToLoad.value + 12,
                processedItems.value.length
              );
              await nextTick();
              distributeItems(false);
              syncSlideshowNavigationContext();
              continue;
            }

            if (!slideshowHasMoreSourceItems.value) return false;
            const previousCount = processedItems.value.length;
            await requestTimelineFetch(false);
            await nextTick();
            syncSlideshowNavigationContext();
            if (
              processedItems.value.length === previousCount &&
              !slideshowHasMoreSourceItems.value
            )
              return false;
          }
          return Boolean(
            findAdjacentFeedItem(
              slideshowNavigationItems.value,
              slideshowItem.value?.id,
              "next"
            )
          );
        } finally {
          isLoadingSlideshowNextItem.value = false;
          slideshowPaginationPromise = null;
        }
      })();
      return slideshowPaginationPromise;
    };

    const prefetchNextSlideshowItem = () => {
      if (
        !slideshowOverlayVisible.value ||
        !slideshowItem.value ||
        findAdjacentFeedItem(
          slideshowNavigationItems.value,
          slideshowItem.value.id,
          "next"
        ) ||
        !canNavigateSlideshowNext.value
      )
        return;
      loadMoreSlideshowItems();
    };

    // Item-to-item navigation follows the exact visible feed order. Only a
    // vertical gesture reaches these handlers; horizontal gestures stay
    // inside the current post's media slideshow.
    let slideshowNextNavigationPromise = null;
    const handleSlideshowNext = () => {
      if (!slideshowItem.value) return Promise.resolve(false);
      let nextItem = findAdjacentFeedItem(
        slideshowNavigationItems.value,
        slideshowItem.value.id,
        "next"
      );
      if (nextItem) {
        setSlideshowItem(nextItem);
        nextTick(() => prefetchNextSlideshowItem());
        return Promise.resolve(true);
      }

      if (!canNavigateSlideshowNext.value) return Promise.resolve(false);
      if (slideshowNextNavigationPromise) return slideshowNextNavigationPromise;

      const requestedItemId = slideshowItem.value.id;
      slideshowNextNavigationPromise = (async () => {
        try {
          await loadMoreSlideshowItems();
          if (slideshowItem.value?.id !== requestedItemId) return false;
          nextItem = findAdjacentFeedItem(
            slideshowNavigationItems.value,
            requestedItemId,
            "next"
          );
          if (!nextItem) return false;
          setSlideshowItem(nextItem);
          nextTick(() => prefetchNextSlideshowItem());
          return true;
        } finally {
          slideshowNextNavigationPromise = null;
        }
      })();
      return slideshowNextNavigationPromise;
    };

    const handleSlideshowPrev = () => {
      if (!slideshowItem.value) return;
      const previousItem = findAdjacentFeedItem(
        slideshowNavigationItems.value,
        slideshowItem.value.id,
        "prev"
      );
      if (previousItem) setSlideshowItem(previousItem);
    };

    const handleProductAdded = async (newProduct) => {
      console.log("handleProductAdded", newProduct);
      toast.success(
        newProduct?._created ? "Product created" : "Product edited successfully"
      );

      // Optimistic update: immediately reflect changes in the grid
      try {
        if (newProduct && newProduct._created && newProduct.id) {
          // Newly created product: inject at top
          const normalized = {
            ...newProduct,
            title: newProduct.itemName || newProduct.title || "",
            description:
              newProduct.itemDescription || newProduct.description || "",
          };
          processedItems.value = [normalized, ...processedItems.value];
          forceRedistribute.value = true;
          nextTick(() => distributeItems());
        } else if (currentProduct.value && currentProduct.value.id) {
          const idx = processedItems.value.findIndex(
            (it) => it && it.id === currentProduct.value.id
          );
          if (idx !== -1) {
            const prev = processedItems.value[idx] || {};
            const optimistic = {
              ...prev,
              itemName:
                newProduct?.itemName || prev.itemName || prev.title || "",
              title: newProduct?.itemName || prev.title || "",
              itemDescription:
                newProduct?.itemDescription ||
                newProduct?.description ||
                prev.itemDescription ||
                prev.description ||
                "",
              description:
                newProduct?.itemDescription ||
                newProduct?.description ||
                prev.description ||
                "",
              images:
                Array.isArray(newProduct?.images) && newProduct.images.length
                  ? newProduct.images
                  : Array.isArray(prev.images)
                  ? prev.images
                  : prev.mediaUrl
                  ? [prev.mediaUrl]
                  : [],
              thumbnailIndex:
                newProduct?.thumbnailIndex ?? prev.thumbnailIndex ?? 0,
            };
            optimistic.thumbnail =
              optimistic.images && optimistic.images.length
                ? optimistic.images[0]
                : prev.thumbnail || "";
            processedItems.value[idx] = optimistic;
            forceRedistribute.value = true;
            nextTick(() => distributeItems());
          }
        }
      } catch (e) {
        console.warn(
          "[Optimistic] Failed to apply immediate product update",
          e
        );
      }

      // Immediately clear loading state and notify other views optimistically to avoid flicker
      isSaveLoading.value = false;

      try {
        if (currentProduct.value && currentProduct.value.id) {
          // Find the just-updated item to broadcast its current state
          const idx = processedItems.value.findIndex(
            (it) => it && it.id === currentProduct.value.id
          );
          if (idx !== -1) {
            const optimistic = processedItems.value[idx];
            try {
              window.dispatchEvent(
                new CustomEvent("marketplace:item-updated", {
                  detail: {
                    originalPath: `products/${optimistic.id}`,
                    itemName: optimistic.itemName,
                    itemDescription: optimistic.itemDescription,
                    images: optimistic.images || [],
                    thumbnailIndex:
                      typeof optimistic.thumbnailIndex === "number"
                        ? optimistic.thumbnailIndex
                        : 0,
                  },
                })
              );
            } catch (e) {
              console.warn("[Masonry] Failed to dispatch optimistic update", e);
            }
          }
        }
      } catch {}

      // Close the overlay immediately; perform Firestore refresh in background to merge any server-side changes
      closeOverlay();

      if (currentProduct.value && currentProduct.value.id) {
        setTimeout(async () => {
          try {
            const productRef = doc(null, "products", currentProduct.value.id);
            const snap = await getDoc(productRef);
            if (snap.exists()) {
              const updated = { id: snap.id, ...snap.data(), type: "product" };
              // Ensure both legacy and canonical fields are populated for UI
              updated.title = updated.itemName || updated.title || "";
              updated.description =
                updated.itemDescription || updated.description || "";
              updated.itemName = updated.itemName || updated.title || "";
              updated.itemDescription =
                updated.itemDescription || updated.description || "";
              // Normalize images
              if (
                !Array.isArray(updated.images) ||
                updated.images.length === 0
              ) {
                updated.images = updated.mediaUrl ? [updated.mediaUrl] : [];
              }
              updated.thumbnail =
                updated.images && updated.images.length > 0
                  ? updated.images[0]
                  : updated.thumbnail || "";

              const idx = processedItems.value.findIndex(
                (it) => it && it.id === updated.id
              );
              if (idx !== -1) {
                const prev = processedItems.value[idx] || {};
                processedItems.value[idx] = {
                  ...prev,
                  ...updated,
                };
                nextTick(() => distributeItems());

                // Broadcast a global update event so other views can sync with the final server state
                try {
                  window.dispatchEvent(
                    new CustomEvent("marketplace:item-updated", {
                      detail: {
                        originalPath: `products/${updated.id}`,
                        itemName: updated.itemName,
                        itemDescription: updated.itemDescription,
                        images: updated.images || [],
                        thumbnailIndex: updated.thumbnailIndex || 0,
                      },
                    })
                  );
                } catch (e) {
                  console.warn("[Masonry] Failed to dispatch item-updated", e);
                }
              }
            }
          } catch (e) {
            console.warn("[Edit] Background refresh of product failed", e);
          }
        }, 0);
      }
    };

    const handleServiceAdded = async (newService) => {
      console.log("handleServiceAdded", newService);
      toast.success(
        newService?._created ? "Service created" : "Service edited successfully"
      );

      // Optimistic update
      try {
        if (newService && newService._created && newService.id) {
          const normalized = {
            ...newService,
            title: newService.itemName || newService.title || "",
            description:
              newService.itemDescription || newService.description || "",
          };
          processedItems.value = [normalized, ...processedItems.value];
          forceRedistribute.value = true;
          nextTick(() => distributeItems());
        } else if (currentProduct.value && currentProduct.value.id) {
          const idx = processedItems.value.findIndex(
            (it) => it && it.id === currentProduct.value.id
          );
          if (idx !== -1) {
            const prev = processedItems.value[idx] || {};
            const optimistic = {
              ...prev,
              itemName:
                newService?.itemName || prev.itemName || prev.title || "",
              title: newService?.itemName || prev.title || "",
              itemDescription:
                newService?.itemDescription ||
                newService?.description ||
                prev.itemDescription ||
                prev.description ||
                "",
              description:
                newService?.itemDescription ||
                newService?.description ||
                prev.description ||
                "",
              images:
                Array.isArray(newService?.images) && newService.images.length
                  ? newService.images
                  : Array.isArray(prev.images)
                  ? prev.images
                  : prev.mediaUrl
                  ? [prev.mediaUrl]
                  : [],
              thumbnailIndex:
                newService?.thumbnailIndex ?? prev.thumbnailIndex ?? 0,
            };
            optimistic.thumbnail =
              optimistic.images && optimistic.images.length
                ? optimistic.images[0]
                : prev.thumbnail || "";
            processedItems.value[idx] = optimistic;
            forceRedistribute.value = true;
            nextTick(() => distributeItems());
          }
        }
      } catch (e) {
        console.warn(
          "[Optimistic] Failed to apply immediate service update",
          e
        );
      }

      isSaveLoading.value = false;
      try {
        if (currentProduct.value && currentProduct.value.id) {
          const serviceRef = doc(null, "services", currentProduct.value.id);
          const snap = await getDoc(serviceRef);
          if (snap.exists()) {
            const updated = { id: snap.id, ...snap.data(), type: "service" };
            // Ensure both legacy and canonical fields are populated for UI
            updated.title = updated.itemName || updated.title || "";
            updated.description =
              updated.itemDescription || updated.description || "";
            updated.itemName = updated.itemName || updated.title || "";
            updated.itemDescription =
              updated.itemDescription || updated.description || "";
            // Normalize images
            if (!Array.isArray(updated.images) || updated.images.length === 0) {
              updated.images = updated.mediaUrl ? [updated.mediaUrl] : [];
            }
            updated.thumbnail =
              updated.images && updated.images.length > 0
                ? updated.images[0]
                : updated.thumbnail || "";
            const idx = processedItems.value.findIndex(
              (it) => it && it.id === updated.id
            );
            if (idx !== -1) {
              const prev = processedItems.value[idx] || {};
              processedItems.value[idx] = {
                ...prev,
                ...updated,
              };
              nextTick(() => distributeItems());

              // Broadcast a global update event so other views can sync
              try {
                window.dispatchEvent(
                  new CustomEvent("marketplace:item-updated", {
                    detail: {
                      originalPath: `services/${updated.id}`,
                      itemName: updated.itemName,
                      itemDescription: updated.itemDescription,
                      images: updated.images || [],
                      thumbnailIndex: updated.thumbnailIndex || 0,
                    },
                  })
                );
              } catch (e) {
                console.warn("[Masonry] Failed to dispatch item-updated", e);
              }
            }
          }
        }
      } catch (e) {
        console.warn(
          "[Edit] Failed to refresh edited service from Firestore",
          e
        );
      } finally {
        closeOverlay();
      }
    };

    const handleMediaAdded = async (newMedia) => {
      console.log("handleMediaAdded", newMedia);
      isSaveLoading.value = true;

      try {
        // Newly created media (optimistic inject)
        if (newMedia && newMedia._created && newMedia.id) {
          const normalized = {
            ...newMedia,
            caption: newMedia.caption || newMedia.description || "",
            description: newMedia.caption || newMedia.description || "",
          };
          processedItems.value = [normalized, ...processedItems.value];
          forceRedistribute.value = true;
          nextTick(() => distributeItems());
        }

        // Determine which collection to use: prefer originalPath/sourceCollection from the form to avoid switching
        let collectionName = "images"; // Default to images
        if (
          newMedia &&
          typeof newMedia.originalPath === "string" &&
          newMedia.originalPath.includes("/")
        ) {
          collectionName = newMedia.originalPath.split("/")[0];
        } else if (
          newMedia &&
          typeof newMedia.sourceCollection === "string" &&
          newMedia.sourceCollection
        ) {
          collectionName = newMedia.sourceCollection;
        } else if (newMedia.mediaType === "video") {
          collectionName = "videos";
        } else if (newMedia.mediaType === "audio") {
          collectionName = "audios";
        }

        // If we have a file, upload it to storage
        if (newMedia.file && !newMedia.file.isExistingFile) {
          console.log("Uploading new media file:", newMedia.file);
          await store.dispatch("uploadMediaFile", {
            file: newMedia.file,
            type: newMedia.mediaType,
            owner: newMedia.owner,
            organisation: newMedia.organisation,
          });
        }

        // If we have an ID, update the existing record; otherwise, create a new one
        if (newMedia.id) {
          console.log("Updating existing media with ID:", newMedia.id);

          // Handle files to upload and existing URLs more carefully
          const existingUrls = newMedia.existingUrls || [];
          let filesToProcess = [];

          // If we have files property, these are new files to upload
          if (
            newMedia.files &&
            Array.isArray(newMedia.files) &&
            newMedia.files.length > 0
          ) {
            filesToProcess = newMedia.files;
          }

          // Upload files if needed (one at a time for now)
          const uploadedUrls = [];
          for (const file of filesToProcess) {
            if (file instanceof File) {
              try {
                console.log("Uploading file:", file.name);
                const uploadResult = await store.dispatch("uploadMediaFile", {
                  file,
                  type: newMedia.mediaType,
                  owner: newMedia.owner,
                  organisation: newMedia.organisation,
                });
                uploadedUrls.push(uploadResult);
              } catch (uploadErr) {
                console.error("Error uploading file:", uploadErr);
              }
            }
          }

          console.log("Uploaded new URLs:", uploadedUrls);
          console.log("Existing URLs to preserve:", existingUrls);

          // Combine all URLs
          let allImages = [...existingUrls, ...uploadedUrls].filter(Boolean);

          // IMPORTANT: If user removed all media, honor the deletion — don't rehydrate from Firestore
          // Keep allImages as [] to persist removal
          console.log("Combined images for update:", allImages);

          try {
            const computedMediaType =
              allImages.length === 0
                ? newMedia.embedHtml
                  ? "embed"
                  : "poster"
                : newMedia.mediaType;
            await store.dispatch("mediaActions/updateMediaItem", {
              id: newMedia.id,
              collection: collectionName,
              data: {
                itemName: newMedia.itemName,
                caption: newMedia.caption,
                type: "media",
                // If no images remain, convert to poster (text-only)
                mediaType: computedMediaType,
                organisation: newMedia.organisation,
                owner: newMedia.owner,
                thumbnailIndex: newMedia.thumbnailIndex || 0,
                // If updatedAt isn't already there, add it
                updatedAt: serverTimestamp(),
              },
              // Pass existingUrls as a separate parameter so they're properly handled
              existingUrls: allImages,
            });
          } catch (err) {
            console.log(
              "Failed with namespaced action, trying non-namespaced:",
              err
            );
            // Fall back to directly calling the action in the store
            const computedMediaType =
              allImages.length === 0
                ? newMedia.embedHtml
                  ? "embed"
                  : "poster"
                : newMedia.mediaType;
            await store.dispatch("updateMedia", {
              id: newMedia.id,
              collection: collectionName,
              data: {
                itemName: newMedia.itemName,
                caption: newMedia.caption,
                type: "media",
                mediaType: computedMediaType,
                organisation: newMedia.organisation,
                owner: newMedia.owner,
                thumbnailIndex: newMedia.thumbnailIndex || 0,
                updatedAt: serverTimestamp(),
              },
              existingUrls: allImages,
            });
          }
          toast.success("Media updated successfully");
          // Merge into local grid immediately
          const idToUpdate = newMedia.id || currentProduct.value?.id;
          const idx = processedItems.value.findIndex(
            (it) => it && it.id === idToUpdate
          );
          if (idx !== -1) {
            const prev = processedItems.value[idx] || {};
            const prevImages = Array.isArray(prev.images) ? prev.images : [];
            const imagesChanged =
              (allImages && allImages.length) !== prevImages.length ||
              (allImages || []).some((u, i) => u !== prevImages[i]);
            // IMPORTANT: honor deletions - do NOT fall back to prev images
            const nextImages = allImages || [];
            const computedMediaType =
              nextImages.length === 0
                ? "poster"
                : newMedia.mediaType || prev.mediaType || "image";
            const merged = {
              ...prev,
              itemName: newMedia.itemName || prev.itemName || prev.title || "",
              title: newMedia.itemName || prev.title || "",
              itemDescription:
                newMedia.caption ||
                prev.itemDescription ||
                prev.description ||
                "",
              description: newMedia.caption || prev.description || "",
              images: nextImages,
              thumbnailIndex:
                newMedia.thumbnailIndex ?? prev.thumbnailIndex ?? 0,
              type: "media",
              mediaType: computedMediaType,
            };
            // Clamp thumbnailIndex into images range
            if (
              typeof merged.thumbnailIndex === "number" &&
              (merged.thumbnailIndex < 0 ||
                merged.thumbnailIndex >= (merged.images?.length || 0))
            ) {
              merged.thumbnailIndex = 0;
            }
            merged.thumbnail =
              merged.images && merged.images.length
                ? merged.images[0]
                : prev.thumbnail || "";
            // If the images changed, clear stale variants so srcset doesn't override the new src
            if (imagesChanged) {
              delete merged.variants;
              delete merged.imageVariants;
            }
            processedItems.value[idx] = merged;
            forceRedistribute.value = true;
            nextTick(() => distributeItems());
            // Broadcast update event
            try {
              const originalPath = `${collectionName}/${idToUpdate}`;
              window.dispatchEvent(
                new CustomEvent("marketplace:item-updated", {
                  detail: {
                    originalPath,
                    itemName: merged.itemName,
                    itemDescription: merged.itemDescription,
                    images: merged.images,
                    thumbnailIndex: merged.thumbnailIndex || 0,
                  },
                })
              );
            } catch (e) {
              console.warn("[Masonry] Failed to dispatch item-updated", e);
            }
          }
        } else {
          console.log("Adding new media item");

          // Handle files to upload and existing URLs
          const existingUrls = newMedia.existingUrls || [];
          let filesToProcess = [];

          // If we have files property, these are new files to upload
          if (
            newMedia.files &&
            Array.isArray(newMedia.files) &&
            newMedia.files.length > 0
          ) {
            filesToProcess = newMedia.files;
          }

          // Upload files if needed (one at a time for now)
          const uploadedUrls = [];
          for (const file of filesToProcess) {
            if (file instanceof File) {
              try {
                console.log("Uploading file:", file.name);
                const uploadResult = await store.dispatch("uploadMediaFile", {
                  file,
                  type: newMedia.mediaType,
                  owner: newMedia.owner,
                  organisation: newMedia.organisation,
                });
                uploadedUrls.push(uploadResult);
              } catch (uploadErr) {
                console.error("Error uploading file:", uploadErr);
              }
            }
          }

          console.log("Uploaded new URLs:", uploadedUrls);
          console.log("Existing URLs to preserve:", existingUrls);

          // Combine all URLs
          const allImages = [...existingUrls, ...uploadedUrls].filter(Boolean);
          console.log("Combined images for create:", allImages);

          try {
            const addPayload = {
              itemName: newMedia.itemName,
              caption: newMedia.caption,
              // Use the combined images array
              images: allImages,
              type: "media",
              mediaType: newMedia.mediaType,
              owner: newMedia.owner,
              organisation: newMedia.organisation,
              thumbnailIndex: newMedia.thumbnailIndex || 0,
            };
            const addResult = await store.dispatch(
              "mediaActions/addMediaPost",
              addPayload
            );
            // Optimistically inject the new item at the top with real ID
            if (addResult && addResult.id) {
              try {
                // Fetch the created doc to obtain server timestamps for proper ordering
                const createdSnap = await getDoc(
                  doc(null, collectionName, addResult.id)
                );
                const createdData = createdSnap.exists()
                  ? createdSnap.data()
                  : {};
                const optimisticItem = {
                  id: addResult.id,
                  type: "media",
                  mediaType: newMedia.mediaType || "image",
                  itemName: newMedia.itemName || "",
                  title: newMedia.itemName || "",
                  itemDescription: newMedia.caption || "",
                  description: newMedia.caption || "",
                  images: allImages && allImages.length ? allImages : [],
                  thumbnailIndex: newMedia.thumbnailIndex || 0,
                  organisation: newMedia.organisation,
                  owner: newMedia.owner,
                  createdAt: createdData.createdAt ||
                    createdData.created_at || {
                      seconds: Math.floor(Date.now() / 1000),
                    },
                  _originalPath: `${collectionName}/${addResult.id}`,
                };
                // Track ID to avoid duplication when timeline fetch returns it
                if (!processedItemIds.value.has(optimisticItem.id)) {
                  processedItemIds.value.add(optimisticItem.id);
                }
                processedItems.value = [
                  optimisticItem,
                  ...processedItems.value,
                ];
                forceRedistribute.value = true;
                nextTick(() => distributeItems(true));

                // Notify other views and trigger a timeline refresh listener
                try {
                  window.dispatchEvent(
                    new CustomEvent("marketplace:new-media", {
                      detail: {
                        originalPath: `${collectionName}/${addResult.id}`,
                        id: addResult.id,
                        mediaType: optimisticItem.mediaType,
                      },
                    })
                  );
                } catch (evtErr) {
                  console.warn(
                    "[Masonry] Failed to dispatch marketplace:new-media",
                    evtErr
                  );
                }
              } catch (optimisticErr) {
                console.warn(
                  "[Masonry] Failed optimistic insert for new media",
                  optimisticErr
                );
              }
            }
          } catch (err) {
            console.log(
              "Failed with namespaced action, trying non-namespaced:",
              err
            );
            // Fall back to directly calling the action in the store
            const addResult = await store.dispatch("addMedia", {
              itemName: newMedia.itemName,
              caption: newMedia.caption,
              images: allImages,
              type: "media",
              mediaType: newMedia.mediaType,
              owner: newMedia.owner,
              organisation: newMedia.organisation,
              thumbnailIndex: newMedia.thumbnailIndex || 0,
            });
            // Best-effort optimistic insert even in fallback path (no guarantee of returned id)
            if (addResult && addResult.id) {
              try {
                const optimisticItem = {
                  id: addResult.id,
                  type: "media",
                  mediaType: newMedia.mediaType || "image",
                  itemName: newMedia.itemName || "",
                  title: newMedia.itemName || "",
                  itemDescription: newMedia.caption || "",
                  description: newMedia.caption || "",
                  images: allImages && allImages.length ? allImages : [],
                  thumbnailIndex: newMedia.thumbnailIndex || 0,
                  organisation: newMedia.organisation,
                  owner: newMedia.owner,
                  _originalPath: `${collectionName}/${addResult.id}`,
                };
                if (!processedItemIds.value.has(optimisticItem.id)) {
                  processedItemIds.value.add(optimisticItem.id);
                }
                processedItems.value = [
                  optimisticItem,
                  ...processedItems.value,
                ];
                forceRedistribute.value = true;
                nextTick(() => distributeItems(true));
                try {
                  window.dispatchEvent(
                    new CustomEvent("marketplace:new-media", {
                      detail: {
                        originalPath: `${collectionName}/${addResult.id}`,
                        id: addResult.id,
                        mediaType: optimisticItem.mediaType,
                      },
                    })
                  );
                } catch {}
              } catch {}
            }
          }
          toast.success("Media added successfully");
          // Merge the newly added media into grid immediately
          const idToUpdate = newMedia.id || currentProduct.value?.id;
          const idx = processedItems.value.findIndex(
            (it) => it && it.id === idToUpdate
          );
          if (idx !== -1) {
            const prev = processedItems.value[idx] || {};
            const prevImages = Array.isArray(prev.images) ? prev.images : [];
            const imagesChanged =
              (allImages && allImages.length) !== prevImages.length ||
              (allImages || []).some((u, i) => u !== prevImages[i]);
            const merged = {
              ...prev,
              itemName: newMedia.itemName || prev.itemName || prev.title || "",
              title: newMedia.itemName || prev.title || "",
              itemDescription:
                newMedia.caption ||
                prev.itemDescription ||
                prev.description ||
                "",
              description: newMedia.caption || prev.description || "",
              images:
                allImages && allImages.length ? allImages : prev.images || [],
              thumbnailIndex:
                newMedia.thumbnailIndex ?? prev.thumbnailIndex ?? 0,
              type: "media",
              mediaType: newMedia.mediaType || prev.mediaType || "image",
            };
            if (
              typeof merged.thumbnailIndex === "number" &&
              (merged.thumbnailIndex < 0 ||
                merged.thumbnailIndex >= (merged.images?.length || 0))
            ) {
              merged.thumbnailIndex = 0;
            }
            merged.thumbnail =
              merged.images && merged.images.length
                ? merged.images[0]
                : prev.thumbnail || "";
            if (imagesChanged) {
              delete merged.variants;
              delete merged.imageVariants;
            }
            processedItems.value[idx] = merged;
            forceRedistribute.value = true;
            nextTick(() => distributeItems());
            // Broadcast update event
            try {
              const originalPath = `${collectionName}/${idToUpdate}`;
              window.dispatchEvent(
                new CustomEvent("marketplace:item-updated", {
                  detail: {
                    originalPath,
                    itemName: merged.itemName,
                    itemDescription: merged.itemDescription,
                    images: merged.images,
                    thumbnailIndex: merged.thumbnailIndex || 0,
                  },
                })
              );
            } catch (e) {
              console.warn("[Masonry] Failed to dispatch item-updated", e);
            }
          }
        }

        // Timeline-only: no local media refresh here; timeline fetch will bring new items
      } catch (error) {
        console.error("Error saving media:", error);
        toast.error("Error saving media: " + error.message);
      } finally {
        isSaveLoading.value = false;
        emit("itemEdited");
        closeOverlay();
      }
    };

    const getMediaEditContext = (item) => {
      const originalPath = String(
        item?._editOriginalPath ||
          item?._originalPath ||
          item?.originalPath ||
          ""
      );
      const pathCollection = originalPath.includes("/")
        ? originalPath.split("/")[0]
        : "";
      const mediaType = String(item?.mediaType || "").toLowerCase();
      const legacyCollections = ["images", "videos", "audios", "documents"];
      let collection = legacyCollections.includes(pathCollection)
        ? pathCollection
        : "";

      if (!collection) {
        if (mediaType === "video") collection = "videos";
        else if (mediaType === "audio") collection = "audios";
        else collection = "images";
      }

      const resolvedOriginalPath =
        originalPath || (item?.id ? `${collection}/${item.id}` : "");

      return { collection, originalPath: resolvedOriginalPath };
    };

    const handleComposerNext = () => {
      currentStep.value = "preview";
    };

    const handleComposerSubmit = async () => {
      if (!postComposerRef.value) {
        toast.error("Composer isn't ready yet. Please try again.");
        return;
      }

      isSaveLoading.value = true;
      try {
        const item = currentProduct.value || {};
        const { collection, originalPath } = getMediaEditContext(item);
        const owner = item.owner || authUserUid.value;
        const organisation =
          item.organisation ||
          props.organisationUid ||
          store.getters.currentOrgUID;

        if (item && item.id) {
          await postComposerRef.value.saveEdit({
            id: item.id,
            collection,
            originalPath,
            owner,
            organisation,
          });
        } else {
          await postComposerRef.value.save({ owner, organisation });
        }

        currentStep.value = "initial";
        emit("itemEdited");
        closeOverlay();
      } catch (error) {
        console.error("Error saving media from PostComposer:", error);
        toast.error(error?.message || "Failed to save media");
      } finally {
        isSaveLoading.value = false;
      }
    };

    const handleStepUpdate = (stepData) => {
      console.log("[MasonryMarketplace] handleStepUpdate received:", stepData);

      if (stepData && stepData.step) {
        // Validate the step is appropriate for the current form
        if (
          stepData.form === "service" &&
          [
            "initial",
            "availability",
            "customization",
            "preview",
            "delete",
          ].includes(stepData.step)
        ) {
          console.log(
            `[MasonryMarketplace] Updating to service step: ${stepData.step}`
          );
          currentStep.value = stepData.step;
        } else if (
          stepData.form === "media" &&
          ["initial", "preview", "delete"].includes(stepData.step)
        ) {
          console.log(
            `[MasonryMarketplace] Updating to media step: ${stepData.step}`
          );
          currentStep.value = stepData.step;
        } else if (
          stepData.form === "product" &&
          ["initial", "preview", "delete"].includes(stepData.step)
        ) {
          console.log(
            `[MasonryMarketplace] Updating to product step: ${stepData.step}`
          );
          currentStep.value = stepData.step;
        } else {
          console.log(`[MasonryMarketplace] Setting step to: ${stepData.step}`);
          currentStep.value = stepData.step;
        }
      }
    };

    const handleTitleUpdate = (newTitle) => {
      dynamicTitle.value = newTitle;
    };

    const handleDataUpdate = (data) => {
      // Update current product data with real-time changes for reactive UI
      if (currentProduct.value) {
        // Update the currentProduct object
        currentProduct.value.itemName = data.itemName;
        currentProduct.value.caption = data.caption;

        // Handle image updates
        if (data.images !== undefined) {
          currentProduct.value.images = data.images;
          currentProduct.value.selectedFiles = data.images;
        }

        if (data.previewUrl !== undefined) {
          currentProduct.value.mediaUrl = data.previewUrl;
          currentProduct.value.previewUrl = data.previewUrl;
        }

        if (data.thumbnailIndex !== undefined) {
          currentProduct.value.thumbnailIndex = data.thumbnailIndex;
        }

        // Update the item in processedItems array for immediate UI reflection
        const itemIndex = processedItems.value.findIndex(
          (item) => item.id === currentProduct.value.id
        );
        if (itemIndex !== -1) {
          const updatedItem = { ...processedItems.value[itemIndex] };
          updatedItem.itemName = data.itemName;
          updatedItem.caption = data.caption;
          // Don't set description to avoid duplicate captions in ImageCard
          // ImageCard will show caption in the appropriate place based on media type

          // Update image-related fields
          if (data.images !== undefined) {
            updatedItem.images = data.images;
            updatedItem.selectedFiles = data.images;
          }

          if (data.previewUrl !== undefined) {
            updatedItem.mediaUrl = data.previewUrl;
            updatedItem.previewUrl = data.previewUrl;
          }

          if (data.thumbnailIndex !== undefined) {
            updatedItem.thumbnailIndex = data.thumbnailIndex;
          }

          // Replace the item in the array to trigger reactivity
          processedItems.value.splice(itemIndex, 1, updatedItem);
        }
      }
    };

    const validateForm = async () => {
      console.log(
        "[MasonryMarketplace] validateForm called, currentStep:",
        currentStep.value
      );

      if (!addFormRef.value) {
        console.log("addFormRef is null");
        return;
      }

      // Call the form validation and get the result
      const isValid = await addFormRef.value.handleFormValidation();

      if (isValid) {
        // If validation passes, manually proceed to the next step based on current form type
        if (currentProduct.value.type === "service") {
          if (currentStep.value === "initial") {
            currentStep.value = "availability";
          } else if (currentStep.value === "availability") {
            // Check if days are selected
            if (
              addFormRef.value.selectedDays &&
              addFormRef.value.selectedDays.filter((day) => day).length === 0
            ) {
              // If no days selected, skip to preview
              await addFormRef.value.gatherServiceDataForPreview();
              currentStep.value = "preview";
            } else {
              // If days are selected, go to customization
              currentStep.value = "customization";
            }
          } else if (currentStep.value === "customization") {
            await addFormRef.value.gatherServiceDataForPreview();
            currentStep.value = "preview";
          }
        } else if (currentProduct.value.type === "product") {
          // For product, go straight to preview
          currentStep.value = "preview";
        } else if (currentProduct.value.type === "media") {
          // For media, go straight to preview
          currentStep.value = "preview";
        }
      }
    };

    const saveItem = async () => {
      isSaveLoading.value = true;
      try {
        if (!addFormRef.value) {
          console.error("Form reference is not correctly set.");
          toast.error("Form isn't ready yet. Please try again.");
          return;
        }

        if (currentProduct.value.type === "product") {
          await addFormRef.value.addOrUpdateProduct();
          emit("itemEdited");
        } else if (currentProduct.value.type === "service") {
          await addFormRef.value.addOrUpdateService();
          emit("itemEdited");
        } else if (currentProduct.value.type === "media") {
          await addFormRef.value.handleSubmit();
          emit("itemEdited");
        } else {
          console.warn("Unknown item type for save:", currentProduct.value);
          toast.error("Unsupported item type.");
        }
      } catch (error) {
        console.error("Error in saveItem:", error);
        toast.error(error?.message || "Failed to save changes");
      } finally {
        isSaveLoading.value = false;
      }
    };

    const clearCompactDeleteConfirmation = () => {
      if (compactDeleteConfirmTimer) {
        clearTimeout(compactDeleteConfirmTimer);
        compactDeleteConfirmTimer = null;
      }
      compactDeleteConfirmItemId.value = null;
    };

    const handleCompactDeleteMenuClick = async (item, close) => {
      if (!item || !item.id) return;

      if (compactDeleteConfirmItemId.value === item.id) {
        clearCompactDeleteConfirmation();
        currentProduct.value = item;
        await confirmDelete();
        if (typeof close === "function") close();
        return;
      }

      if (compactDeleteConfirmTimer) {
        clearTimeout(compactDeleteConfirmTimer);
      }
      compactDeleteConfirmItemId.value = item.id;
      compactDeleteConfirmTimer = setTimeout(() => {
        if (compactDeleteConfirmItemId.value === item.id) {
          compactDeleteConfirmItemId.value = null;
        }
        compactDeleteConfirmTimer = null;
      }, 3000);
    };

    const clearCompactUnlistConfirmation = () => {
      if (compactUnlistConfirmTimer) {
        clearTimeout(compactUnlistConfirmTimer);
        compactUnlistConfirmTimer = null;
      }
      compactUnlistConfirmItemId.value = null;
    };

    const handleCompactUnlistMenuClick = async (item, close) => {
      if (!item || !item.id) return;

      if (compactUnlistConfirmItemId.value === item.id) {
        clearCompactUnlistConfirmation();
        await executeUnlistNFT(item);
        if (typeof close === "function") close();
        return;
      }

      if (compactUnlistConfirmTimer) {
        clearTimeout(compactUnlistConfirmTimer);
      }
      compactUnlistConfirmItemId.value = item.id;
      compactUnlistConfirmTimer = setTimeout(() => {
        if (compactUnlistConfirmItemId.value === item.id) {
          compactUnlistConfirmItemId.value = null;
        }
        compactUnlistConfirmTimer = null;
      }, 3000);
    };

    const executeUnlistNFT = async (item) => {
      const nftId =
        item.nftId ||
        `nft-${item.chainId}-${item.contractAddress}-${item.tokenId}`;
      try {
        // Look up the listing tracking doc
        const listingSnap = await getDoc(doc(null, "nftListings", nftId));
        if (!listingSnap.exists()) {
          toast.error("Listing not found");
          return;
        }
        const listingData = listingSnap.data();

        // Mark listing as delisted
        await setDoc(
          doc(null, "nftListings", nftId),
          {
            listingStatus: "delisted",
            lastUpdated: serverTimestamp(),
          },
          { merge: true }
        );

        // Handle the itemsTimeline entry based on listing mode
        const timelineDocId = listingData.timelineDocId;
        if (timelineDocId) {
          if (listingData.listingMode === "in_place") {
            // Non-destructive: clear listing fields but keep the original post
            await setDoc(
              doc(null, "itemsTimeline", timelineDocId),
              {
                isNFTListing: false,
                listingStatus: "delisted",
                listingPrice: null,
                currency: null,
                coinSymbol: null,
                requiresWalletPurchase: false,
                lastUpdated: serverTimestamp(),
              },
              { merge: true }
            );
          } else {
            // NFTGallery-origin listing: delete the timeline entry entirely
            await deleteDoc(doc(null, "itemsTimeline", timelineDocId));
          }
        }

        // Optimistically remove card from grid
        const idx = processedItems.value.findIndex(
          (it) => it && it.id === item.id
        );
        if (idx !== -1) {
          processedItems.value.splice(idx, 1);
          forceRedistribute.value = true;
          nextTick(() => distributeItems());
        }

        toast.success("NFT unlisted from marketplace");
      } catch (error) {
        console.error("[MasonryMarketplace] Error unlisting NFT:", error);
        toast.error("Failed to unlist NFT");
      }
    };

    const confirmDelete = async () => {
      const itemToDelete = currentProduct.value;
      const itemDisplayId = itemToDelete?.id || itemToDelete?.itemId;
      const itemKind = itemToDelete?.type || itemToDelete?.kind;
      const deletionActorUid = authUserUid.value;
      try {
        isSaveLoading.value = true; // Start loading indicator

        if (!itemToDelete || !itemDisplayId) {
          throw new Error("No item ID found.");
        }

        const deletion = await marketplaceItemDeletion.deleteItem({
          item: itemToDelete,
          legacyDelete: () =>
            store.dispatch("mediaActions/deleteItem", {
              id: itemDisplayId,
              type: itemToDelete.type,
              mediaType: itemToDelete.mediaType,
              // Legacy Storage cleanup remains isolated from private item commands.
              images: itemToDelete.images || [],
              originalPath:
                itemToDelete.originalPath ||
                (itemToDelete.type
                  ? `${
                      itemToDelete.type === "media"
                        ? itemToDelete.mediaType || "images"
                        : itemToDelete.type + "s"
                    }/${itemDisplayId}`
                  : undefined),
            }),
        });

        // A response started by one account must not mutate another account's UI.
        if (authUserUid.value !== deletionActorUid) return;

        const removedIndex = processedItems.value.findIndex(
          (item) =>
            item &&
            (item.id === itemDisplayId ||
              (itemToDelete.itemId && item.itemId === itemToDelete.itemId))
        );
        if (removedIndex !== -1) {
          processedItems.value.splice(removedIndex, 1);
          forceRedistribute.value = true;
          nextTick(() => distributeItems());
        }

        const deletionPending =
          deletion.mode === "private-authority" &&
          deletion.result?.outcome === "deletion-pending";
        toast.success(
          deletionPending
            ? "Item deletion started."
            : `${
                itemKind === "media" ? "Media" : itemKind || "Item"
              } deleted successfully.`
        );

        // Broadcast deletion event so other views can sync
        try {
          const originalPath =
            itemToDelete.originalPath ||
            (itemToDelete.type && itemDisplayId
              ? `${
                  itemToDelete.type === "media"
                    ? itemToDelete.mediaType || "images"
                    : itemToDelete.type + "s"
                }/${itemDisplayId}`
              : undefined);
          window.dispatchEvent(
            new CustomEvent("marketplace:item-deleted", {
              detail: { originalPath, id: itemDisplayId },
            })
          );
        } catch (e) {
          console.warn("[Masonry] Failed to dispatch item-deleted", e);
        }

        emit("itemEdited");
        closeOverlay();
      } catch (error) {
        toast.error(
          `Failed to delete ${itemKind || "item"}: ${
            error?.message || "Unknown error"
          }`
        );
        console.error(`Deletion Error:`, error);
      } finally {
        isSaveLoading.value = false; // End loading indicator
      }
    };

    /**
     * Show burn confirmation step
     */
    const confirmBurn = () => {
      if (!currentProduct.value) {
        console.warn("[Masonry] confirmBurn called but no currentProduct");
        return;
      }
      currentStep.value = "burn";
      showOverlay.value = true;
    };

    /**
     * Execute the burn transaction
     */
    const executeBurn = async () => {
      if (!currentProduct.value) {
        toast.error("No item selected to burn");
        return;
      }

      const item = currentProduct.value;
      console.log("[Masonry] executeBurn called for item:", item.id);

      isSaveLoading.value = true;
      try {
        const result = await burnNFT(item);

        if (result.success) {
          // Show appropriate message based on burn method
          let methodMsg;
          if (result.method === "already-burned") {
            methodMsg =
              "NFT was already burned. Removing from your collection...";
          } else if (result.method === "transfer-to-burn-address") {
            methodMsg = "NFT sent to burn address!";
          } else {
            methodMsg = "NFT burned successfully!";
          }

          // Show transaction hash if available (not for already-burned)
          if (result.txHash) {
            toast.success(`${methodMsg} Tx: ${result.txHash?.slice(0, 10)}...`);
          } else {
            toast.success(methodMsg);
          }

          // Remove from local list (optimistic)
          const removedIndex = processedItems.value.findIndex(
            (it) => it && it.id === item.id
          );
          if (removedIndex !== -1) {
            processedItems.value.splice(removedIndex, 1);
            forceRedistribute.value = true;
            nextTick(() => distributeItems());
          }

          // Delete item from Firestore using the same action as confirmDelete
          try {
            await store.dispatch("mediaActions/deleteItem", {
              id: item.id,
              type: item.type,
              mediaType: item.mediaType,
              // Pass all available images for deletion from storage
              images: item.images || [],
              originalPath:
                item.originalPath ||
                (item.type && item.id
                  ? `${
                      item.type === "media"
                        ? item.mediaType || "images"
                        : item.type + "s"
                    }/${item.id}`
                  : undefined),
            });
            console.log("[Masonry] Item deleted from Firestore after burn");
          } catch (deleteError) {
            console.error(
              "[Masonry] Failed to delete item from Firestore after burn:",
              deleteError
            );
            // Don't fail the whole operation - the on-chain burn succeeded
            toast.warning(
              "NFT burned but failed to remove from database. It may still appear temporarily."
            );
          }

          // Broadcast event for other views
          try {
            window.dispatchEvent(
              new CustomEvent("marketplace:nft-burned", {
                detail: {
                  id: item.id,
                  txHash: result.txHash,
                  tokenId: result.tokenId,
                },
              })
            );
            // Also dispatch item-deleted for consistency with delete flow
            window.dispatchEvent(
              new CustomEvent("marketplace:item-deleted", {
                detail: {
                  originalPath:
                    item.originalPath ||
                    `${
                      item.type === "media"
                        ? item.mediaType || "images"
                        : item.type + "s"
                    }/${item.id}`,
                  id: item.id,
                },
              })
            );
          } catch (e) {
            console.warn("[Masonry] Failed to dispatch nft-burned event", e);
          }

          emit("itemEdited");
          closeOverlay();
        } else {
          toast.error(`Burn failed: ${result.error}`);
        }
      } catch (error) {
        console.error("[Masonry] Burn error:", error);
        toast.error(`Burn failed: ${error.message || "Unknown error"}`);
      } finally {
        isSaveLoading.value = false;
      }
    };

    /**
     * Handle burn NFT with window.confirm dialog (no overlay)
     * Used from the dropdown menu for quick burn action
     */
    const handleBurnNFT = async (item) => {
      if (!item) {
        console.warn("[Masonry] handleBurnNFT called but no item");
        return;
      }

      const itemName =
        item.itemName || item.title || item.name || `Token #${item.tokenId}`;

      // Confirm with user - burning is irreversible
      const confirmed = window.confirm(
        `⚠️ BURN NFT - THIS IS IRREVERSIBLE!\n\nYou are about to permanently destroy:\n${itemName}\n\nThis action cannot be undone. The NFT will be sent to the burn address and lost forever.\n\nAre you sure you want to continue?`
      );

      if (!confirmed) {
        console.log("[Masonry] User cancelled burn");
        return;
      }

      // Set currentProduct and call executeBurn
      currentProduct.value = item;
      await executeBurn();
    };

    // Debounce implementation for scroll handling
    const debounce = (fn, delay) => {
      let timer;
      const debounced = function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
      debounced.cancel = () => {
        clearTimeout(timer);
        timer = null;
      };
      return debounced;
    };

    // FIXED SCROLL HANDLING - Allows pagination for both search and normal modes
    const hasUserPaginationIntent = ref(false);
    const markUserPaginationIntent = () => {
      hasUserPaginationIntent.value = true;
    };
    const showManualLoadMore = computed(
      () =>
        !hasUserPaginationIntent.value &&
        !isInitialLoading.value &&
        !isLoadingMoreItems.value &&
        hasMoreTimelineData.value &&
        processedItems.value.length > 0
    );
    const loadMoreFromUserAction = () => {
      markUserPaginationIntent();
      requestTimelineFetch(false);
    };

    const handleScroll = debounce(() => {
      if (
        !canHandleFeedDocumentScroll({
          disposed: componentDisposed,
          lifecycleActive: paginationLifecycleActive,
          overlayActive: slideshowOverlayVisible.value,
        })
      )
        return;
      // Wallet view mounts its own gallery-specific infinite scroll; skip the
      // marketplace scroll pagination there to avoid competing listeners.
      if (
        route.name === "wallet" ||
        String(route.path || "").startsWith("/wallet")
      ) {
        return;
      }
      if (isInitializing.value) {
        return;
      }
      // Skip if already loading
      if (isLoadingMoreItems.value) {
        console.log("[SCROLL] Already loading, skipping");
        return;
      }

      // PROFILE MODE: Check profile-specific end-of-data
      if (props.showItems === "profileUser" && !hasMoreProfileData.value) {
        console.log("[SCROLL] No more profile data, skipping");
        return;
      }

      // TIMELINE MODE: Check timeline end-of-data
      if (!hasMoreTimelineData.value) {
        console.log("[SCROLL] No more timeline data, skipping");
        return;
      }

      const { scrollTop, viewportHeight, scrollHeight } =
        readDocumentScrollMetrics(window, document);

      if (!hasUserPaginationIntent.value) {
        if (scrollTop <= 16) return;
        hasUserPaginationIntent.value = true;
      }

      // Check if we're near the bottom (more generous 500px threshold)
      if (
        isNearDocumentBottom({ scrollTop, viewportHeight, scrollHeight }, 500)
      ) {
        console.log("[SCROLL] Near bottom - fetching more items");

        const currentDisplayed = filteredItems.value.length;
        const totalProcessed = processedItems.value.length;

        // Allow pagination for normal browsing.
        // When itemsToLoad already covers all processedItems, the gap between
        // currentDisplayed and totalProcessed is due to filtering, not display
        // limiting — so we must fetch more from the server instead.
        if (
          currentDisplayed < totalProcessed &&
          itemsToLoad.value < totalProcessed
        ) {
          // We have more processed items to show, just increase display limit
          const newItemsToLoad = Math.min(
            itemsToLoad.value + 12,
            totalProcessed + 12
          );
          if (newItemsToLoad > itemsToLoad.value) {
            itemsToLoad.value = newItemsToLoad;
            console.log(
              `[SCROLL] Increased display limit to ${newItemsToLoad} items`
            );

            // Force update the layout immediately
            nextTick(() => {
              distributeItems(false);
            });
          }
        } else {
          // Need to fetch more data from server
          console.log("[SCROLL] Fetching more items from server");
          requestTimelineFetch(false);
        }
      }
    }, 300);

    function attachPaginationScrollListener() {
      if (componentDisposed || !paginationLifecycleActive) return;
      handleScroll.cancel();
      window.removeEventListener("scroll", handleScroll);
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    function detachPaginationScrollListener() {
      window.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    }

    function deactivatePaginationLifecycle() {
      if (componentDisposed) return;
      if (queuedTimelineFetch) {
        resumeTimelineFetch = queuedTimelineFetch;
      } else if (isLoadingMoreItems.value) {
        resumeTimelineFetch = processedItems.value.length ? "next" : "initial";
      }
      paginationLifecycleActive = false;
      paginationWasDeactivated = true;
      queuedTimelineFetch = null;
      timelineGeneration.value += 1;
      detachPaginationScrollListener();
    }

    function activatePaginationLifecycle() {
      if (componentDisposed) return;
      const activationAction = resolveFeedActivationAction({
        resumeRequest: resumeTimelineFetch,
        wasDeactivated: paginationWasDeactivated,
        itemCount: processedItems.value.length,
        hasPaginationIntent: hasUserPaginationIntent.value,
        hasMore: hasMoreTimelineData.value,
      });
      paginationLifecycleActive = true;
      paginationWasDeactivated = false;
      resumeTimelineFetch = null;
      attachPaginationScrollListener();
      if (activationAction === "initial" || activationAction === "next") {
        requestTimelineFetch(activationAction === "initial");
      } else if (activationAction === "check-bottom") {
        nextTick(handleScroll);
      }
    }

    // Register independently from asynchronous feed boot. A stale first page
    // must never skip pagination setup, and keep-alive routes must reattach it.
    onMounted(() => {
      paginationHasMounted = true;
      activatePaginationLifecycle();
    });
    onActivated(activatePaginationLifecycle);

    // Track loading state for favourites operations
    const favouritesLoading = reactive({});

    // Function to fetch the user's wishlist items from Firestore
    const fetchWishlistItems = async () => {
      if (!isWishlistFeatureEnabled) return;
      const userUID = authUserUid.value;
      if (!userUID) {
        console.log("Cannot fetch wishlist: No user is logged in");
        return;
      }
      const generation = ++wishlistRequestGeneration;

      try {
        console.log("Fetching wishlist items for user:", userUID);
        const wishlistCollectionRef = collection(
          null,
          "users",
          userUID,
          "Wishlist"
        );
        const wishlistSnapshot = await getDocs(wishlistCollectionRef);
        if (
          generation !== wishlistRequestGeneration ||
          authUserUid.value !== userUID
        ) {
          return;
        }

        // Clear existing wishlist state
        Object.keys(isWishlisted).forEach((key) => {
          delete isWishlisted[key];
        });

        // Populate wishlist state with items from Firestore
        wishlistSnapshot.forEach((doc) => {
          const itemId = doc.id;
          isWishlisted[itemId] = true;
        });

        console.log("Wishlist state after fetching:", isWishlisted);
      } catch (error) {
        console.error("Error fetching wishlist items:", error);
      }
    };

    // Create a computed property to track wishlist status for each item
    const getIsWishlisted = (itemId) => {
      return computed(() => isWishlisted[itemId] || false);
    };

    const toggleWishlist = (item) => {
      console.log("Toggle wishlist for item:", item.id);
      console.log("Current user UID:", authUserUid.value);
      console.log(
        "Current wishlist state for this item:",
        isWishlisted[item.id]
      );

      // Set loading state for this item
      favouritesLoading[item.id] = true;

      if (isWishlisted[item.id]) {
        removeFromWishlist(item);
      } else {
        addToWishlist(item);
      }
    };

    // Copy item link to clipboard
    const copyItemLink = async (item) => {
      const itemId = item?.id;
      if (!itemId) return;
      const url = `${window.location.origin}/?item=${itemId}`;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.style.position = "fixed";
          ta.style.left = "-999999px";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        toast.success("Link copied to clipboard", { autoClose: 3000 });
      } catch (err) {
        console.error("Copy failed:", err);
        toast.error("Failed to copy link", { autoClose: 3000 });
      }
    };

    // Share item — builds a pending share payload and opens Messages panel
    const shareItem = async (item) => {
      // Document items store doc file URLs (PDFs etc.) in images[] — skip those
      const isDoc = item.mediaType === "documents";
      // For YouTube embeds without a stored thumbnail, derive from embedUrl/embedHtml
      let derivedThumbnail = isDoc
        ? ""
        : item.images?.[item.thumbnailIndex || 0] ||
          item.thumbnailUrl ||
          item.thumbnail ||
          item.posterUrl ||
          "";
      if (!derivedThumbnail && item.mediaType === "embed") {
        const src = item.embedUrl || item.embedHtml || "";
        const ytMatch = src.match(
          /(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{11})/
        );
        if (ytMatch)
          derivedThumbnail = `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
      }
      // Extract document name from metadata (same logic as ImageCard.documentDisplayName)
      let docName = item.documentName || item.fileName || "";
      if (
        !docName &&
        isDoc &&
        Array.isArray(item.documentMetadata) &&
        item.documentMetadata[0]?.name
      ) {
        docName = item.documentMetadata[0].name;
      }
      const pendingShare = {
        id: item.id || "",
        itemName: item.itemName || item.title || "",
        description: item.description || item.caption || "",
        mediaType: item.mediaType || "",
        type: item.type || "",
        thumbnail: derivedThumbnail,
        embedProvider: item.embedProvider || null,
        embedIsVertical: item.embedIsVertical || false,
        owner: item.owner || "",
        originalPath: item.originalPath || null,
        // Commerce fields
        price: item.price || null,
        coinSymbol: item.coinSymbol || "",
        // Document fields
        documentName: docName,
      };
      await store.dispatch("UIState/setPendingShareItem", pendingShare);
      await store.dispatch("UIState/setMessagesMinimised", false);
      await store.dispatch("UIState/setMessagesOpen", true);
    };

    const addToWishlist = async (item) => {
      if (!authUserUid.value) {
        console.log("Cannot add to wishlist: No user is logged in");
        favouritesLoading[item.id] = false;
        toast.error("You must be logged in to add items to favourites.", {
          autoClose: 3000,
        });
        return;
      }

      console.log("Adding item to wishlist:", item.id);
      console.log("For user:", authUserUid.value);

      try {
        // Update local state immediately for responsive UI using Vue.set equivalent
        // This ensures the change is reactive
        const newIsWishlisted = { ...isWishlisted };
        newIsWishlisted[item.id] = true;
        Object.keys(newIsWishlisted).forEach((key) => {
          isWishlisted[key] = newIsWishlisted[key];
        });

        // Force component update by triggering a state change
        distributeItems();

        // Create a clean version of the item to store in Firestore
        // This avoids issues with non-serializable properties
        const cleanItem = {
          id: item.id,
          itemName: item.itemName || item.title || "",
          itemDescription: item.itemDescription || item.caption || "",
          price: item.price || 0,
          images: Array.isArray(item.images) ? [...item.images] : [],
          thumbnailIndex: item.thumbnailIndex || 0,
          mediaUrl: item.mediaUrl || "",
          type: item.type || "",
          mediaType: item.mediaType || "",
          organisation: item.organisation || "",
          owner: item.owner || "",
          coinSymbol: item.coinSymbol || "",
          coinName: item.coinName || "",
          category: item.category || "",
          condition: item.condition || "",
          createdAt: serverTimestamp(),
        };

        const wishlistDocRef = doc(
          null,
          "users",
          authUserUid.value,
          "Wishlist",
          item.id
        );

        console.log("Wishlist document reference:", wishlistDocRef.path);
        console.log("Item data to save:", cleanItem);

        await setDoc(wishlistDocRef, cleanItem);
        console.log("Successfully added item to wishlist in Firestore");

        emit("favouritesUpdated");
        toast.success("Added to favourites.", { autoClose: 3000 });
      } catch (error) {
        // Revert local state on error using Vue.set equivalent
        const newIsWishlisted = { ...isWishlisted };
        newIsWishlisted[item.id] = false;
        Object.keys(newIsWishlisted).forEach((key) => {
          isWishlisted[key] = newIsWishlisted[key];
        });

        // Force component update
        distributeItems();

        console.error("Error adding to wishlist: ", error);
        console.error("Error details:", error.code, error.message);
        toast.error(`Error adding to wishlist: ${error.message}`, {
          autoClose: 3000,
        });
      } finally {
        // Clear loading state
        favouritesLoading[item.id] = false;
      }
    };

    const removeFromWishlist = async (item) => {
      if (!authUserUid.value) {
        console.log("Cannot remove from wishlist: No user is logged in");
        favouritesLoading[item.id] = false;
        toast.error("You must be logged in to remove items from favourites.", {
          autoClose: 3000,
        });
        return;
      }

      console.log("Removing item from wishlist:", item.id);
      console.log("For user:", authUserUid.value);

      try {
        // Update local state immediately for responsive UI using Vue.set equivalent
        const newIsWishlisted = { ...isWishlisted };
        newIsWishlisted[item.id] = false;
        Object.keys(newIsWishlisted).forEach((key) => {
          isWishlisted[key] = newIsWishlisted[key];
        });

        // Force component update
        distributeItems();

        const wishlistDocRef = doc(
          null,
          "users",
          authUserUid.value,
          "Wishlist",
          item.id
        );

        console.log(
          "Wishlist document reference to delete:",
          wishlistDocRef.path
        );

        await deleteDoc(wishlistDocRef);
        console.log("Successfully removed item from wishlist in Firestore");

        emit("favouritesUpdated");
        toast.success("Removed from favourites.", { autoClose: 3000 });
      } catch (error) {
        // Revert local state on error using Vue.set equivalent
        const newIsWishlisted = { ...isWishlisted };
        newIsWishlisted[item.id] = true;
        Object.keys(newIsWishlisted).forEach((key) => {
          isWishlisted[key] = newIsWishlisted[key];
        });

        // Force component update
        distributeItems();

        console.error("Error removing from wishlist: ", error);
        console.error("Error details:", error.code, error.message);
        toast.error(`Error removing from wishlist: ${error.message}`, {
          autoClose: 3000,
        });
      } finally {
        // Clear loading state
        favouritesLoading[item.id] = false;
      }
    };

    // Add a watcher to ensure reactivity of the wishlist state
    watch(
      isWishlisted,
      (newVal) => {
        // console.log("Wishlist state updated:", newVal);
      },
      { deep: true }
    );

    const editItem = async (item) => {
      isFetching.value = true;
      console.log("Editing item:", item);
      try {
        if (item.type === "service") {
          await store.dispatch("fetchService", item.id);
          currentProduct.value = { ...item, type: "service" };
          // Set appropriate title for service edit
          dynamicTitle.value = "Edit Service Details";
        } else if (item.type === "product") {
          // Fetch full product doc to ensure we have complete images array
          try {
            const snap = await getDoc(doc(null, "products", item.id));
            const data = snap.exists() ? snap.data() : {};
            const product = { id: item.id, ...data };

            // Normalize images from canonical and legacy fields
            const union = new Set();
            if (Array.isArray(product.images)) {
              for (const u of product.images)
                if (typeof u === "string") union.add(u);
            }
            if (typeof product.mediaUrl === "string")
              union.add(product.mediaUrl);
            if (Array.isArray(product.additionalImages)) {
              for (const u of product.additionalImages)
                if (typeof u === "string") union.add(u);
            }
            product.images = Array.from(union);
            // Ensure thumbnailIndex is valid
            const tIdx = Math.min(
              Number(product.thumbnailIndex) || 0,
              Math.max(0, (product.images?.length || 1) - 1)
            );
            product.thumbnailIndex = tIdx;
            // Backfill mediaUrl for previews/carousels
            product.mediaUrl = product.images[tIdx] || product.mediaUrl || "";
            // Title/description normalization
            product.itemName = product.itemName || product.title || "";
            product.itemDescription =
              product.itemDescription || product.description || "";

            currentProduct.value = { ...item, ...product, type: "product" };
          } catch (e) {
            console.warn(
              "Failed to fetch full product doc, falling back to provided item:",
              e
            );
            const fallback = { ...item };
            if (
              !Array.isArray(fallback.images) ||
              fallback.images.length === 0
            ) {
              fallback.images = fallback.mediaUrl ? [fallback.mediaUrl] : [];
            }
            currentProduct.value = { ...fallback, type: "product" };
          }
          // Set appropriate title for product edit and remount form to avoid stale state
          dynamicTitle.value = "Edit Product Details";
          formKey.value = Date.now();
        } else if (
          item.mediaType === "image" ||
          item.mediaType === "video" ||
          item.mediaType === "audio" ||
          item.mediaType === "documents" ||
          item.mediaType === "embed" ||
          item.type === "image" ||
          item.type === "media"
        ) {
          // For media items like images, videos, or audio
          console.log("Setting media item for editing:", item);

          const mediaItem = { ...item };
          // Normalize images/urls from the provided item
          const union = new Set();
          if (Array.isArray(mediaItem.images)) {
            for (const u of mediaItem.images)
              if (typeof u === "string") union.add(u);
          }
          if (typeof mediaItem.mediaUrl === "string")
            union.add(mediaItem.mediaUrl);
          if (Array.isArray(mediaItem.additionalImages)) {
            for (const u of mediaItem.additionalImages)
              if (typeof u === "string") union.add(u);
          }
          mediaItem.images = Array.from(union).filter(
            (u) => typeof u === "string" && !u.startsWith("blob:")
          );

          // Primary mediaUrl based on thumbnailIndex when images exist
          // For video/audio posts, mediaUrl holds the media file URL — don't overwrite.
          if (
            mediaItem.mediaType !== "documents" &&
            mediaItem.mediaType !== "video" &&
            mediaItem.mediaType !== "audio"
          ) {
            const tIdx = Math.min(
              Number(mediaItem.thumbnailIndex) || 0,
              Math.max(0, (mediaItem.images?.length || 1) - 1)
            );
            mediaItem.mediaUrl =
              mediaItem.images[tIdx] || mediaItem.mediaUrl || "";
            mediaItem.thumbnailIndex = tIdx;
          }

          // Titles/descriptions: prefer explicit fields from item
          mediaItem.itemName = mediaItem.itemName || mediaItem.title || "";
          mediaItem.caption =
            mediaItem.caption ||
            mediaItem.itemDescription ||
            mediaItem.description ||
            "";

          // Map textual media types to 'text' for the composer
          // NOTE: Embed posts keep their mediaType — loadFromItem detects embed data
          if (
            mediaItem.mediaType === "poster" ||
            mediaItem.mediaType === "tweet" ||
            mediaItem.mediaType === "post"
          ) {
            mediaItem.mediaType = "text";
          }
          // Embed posts: keep mediaType as "embed" so loadFromItem correctly
          // hydrates poster.value with embedHtml/embedUrl and sets selectedType="embed"

          const editContext = getMediaEditContext(mediaItem);
          currentProduct.value = {
            ...mediaItem,
            type: "media",
            _editCollection: editContext.collection,
            _editOriginalPath: editContext.originalPath,
          };

          // Set appropriate title for media edit
          dynamicTitle.value = "Edit Media Post";
          // Bump key so PostComposer remounts cleanly (prevents stale state)
          composerKey.value = Date.now();
        }
        openOverlay();
      } catch (error) {
        console.error(`Failed to fetch ${item.type || item.mediaType}:`, error);
      } finally {
        isFetching.value = false;
      }
    };

    // Deep-link edit via query param was removed per UX simplification.

    // Check if any filters are currently active (compare against true defaults)
    const hasActiveFilters = computed(() => {
      const nonDefaultChains = !(
        Array.isArray(chainFilter.value) &&
        chainFilter.value.length === 1 &&
        chainFilter.value[0] === "0x1"
      );
      const active =
        searchQuery.value.trim() !== "" ||
        !isDefaultFilterState(filterType.value) ||
        nonDefaultChains ||
        selectedCategory.value !== "" ||
        locationFilter.value.active ||
        sortOrder.value !== "timestamp_desc";
      if (active) {
        console.log("[FiltersState] Active filters detected", {
          search: searchQuery.value,
          filterType: filterType.value,
          chainFilter: chainFilter.value,
          selectedCategory: selectedCategory.value,
          locationActive: locationFilter.value.active,
          sortOrder: sortOrder.value,
          DEFAULT_FILTER_TYPES,
        });
      }
      return active;
    });

    // Determine if we're in search mode (text search active — pagination still works)
    const isSearchMode = computed(() => {
      return searchQuery.value.trim() !== "";
    });

    // Determine if we're in filtered mode (search/filters active)
    const isFilteredMode = computed(() => {
      const hasSearch = searchQuery.value.trim() !== "";
      const hasNonDefaultFilters = !isDefaultFilterState(filterType.value);
      const hasCategory = selectedCategory.value !== "";
      const hasLocation = locationFilter.value.active;
      const hasNonDefaultSort = sortOrder.value !== "timestamp_desc";

      return (
        hasSearch ||
        hasNonDefaultFilters ||
        hasCategory ||
        hasLocation ||
        hasNonDefaultSort
      );
    });

    // Track search results count for smart loading indicator management
    const searchResultsCount = ref(0);
    const searchHasMoreResults = ref(true);

    // Reset all filters to default values
    const resetFilters = () => {
      console.log("Resetting all filters");
      searchQuery.value = "";
      // Reset to default filter types list (all item kinds including docs)
      filterType.value = ""; // Back to default (show all, icons inactive)
      // Reset chains to default (ETH-only)
      chainFilter.value = ["0x1"];
      selectedCategory.value = "";
      sortOrder.value = "timestamp_desc"; // Reset to default sort

      // Reset location filter
      locationFilter.value = {
        active: false,
        coordinates: null,
        radius: 10,
        cityName: "",
      };

      // Reset and update filter state
      updateFilterState();

      // Reset items to load count to avoid confusion
      itemsToLoad.value = 12;

      // Reset position tracking and item distribution
      lastProcessedCount.value = 0;
      itemPositions.value.clear();
      processedItemIds.value.clear(); // Clear duplicate tracking

      // Reset empty state flags
      noMatchingItems.value = false;

      // Reset pagination state
      hasMoreTimelineData.value = true;
      lastTimelineDoc.value = null;

      // Force a clean relayout to ensure proper display
      nextTick(() => {
        distributeItems(true); // true = initial layout to force complete rebuild

        // After layout is rebuilt, fetch more if needed
        requestTimelineFetch();
      });
    };

    return {
      navbarVisible,
      hasInitialItems,
      searchQuery,
      filterType,
      chainFilter,
      sortOrder,
      itemsToLoad,
      filteredItems,
      handleScroll,
      showManualLoadMore,
      loadMoreFromUserAction,
      toggleWishlist,
      isWishlisted,
      isWishlistFeatureEnabled,
      isItemOwnedByCurrentUser,
      resolveClapPlacement,
      handlePriceClicked,
      editItem,
      shareItem,
      copyItemLink,
      redirectToProductView,
      selectMarketplaceCard,
      handleAuthorityPurchaseSettled,
      openImageSlideshow,
      closeLocalOverlay,
      handleSlideshowEdit,
      handleSlideshowDelete,
      handleSlideshowMint,
      handleSlideshowFavouritesUpdated,
      handleSlideshowNext,
      handleSlideshowPrev,
      canNavigateSlideshowPrevious,
      canNavigateSlideshowNext,
      isLoadingSlideshowNextItem,
      slideshowOverlayVisible,
      slideshowItem,
      authUserUid,
      convertedPrices,
      formatNumberTo4Digits,
      isFetching,
      currentProduct,
      showOverlay,
      currentStep,
      dynamicTitle,
      openOverlay,
      closeOverlay,
      shouldShowBackArrow,
      isMediaEdit,
      isCurrentProductMinted,
      goToPreviousStep,
      goToNextStep,
      isSaveLoading,
      canPreview,
      addFormRef,
      postComposerRef,
      composerKey,
      user,
      userUnitsOrg,
      handleProductAdded,
      handleServiceAdded,
      handleMediaAdded,
      handleComposerNext,
      handleComposerSubmit,
      handleStepUpdate,
      handleTitleUpdate,
      handleDataUpdate,
      validateForm,
      saveItem,
      compactDeleteConfirmItemId,
      handleCompactDeleteMenuClick,
      compactUnlistConfirmItemId,
      handleCompactUnlistMenuClick,
      confirmDelete,
      confirmBurn,
      executeBurn,
      handleBurnNFT,
      overlaySubtitle,
      burnNFT,
      toggleLoginModal,
      isLoadingMoreItems,
      refreshPage,
      columns, // Exposed for template
      selectedCategory,
      categories,
      updateCategoryNames,
      showOtherUserPrice,
      categoryMapping,
      favouritesLoading, // Add favouritesLoading to the return statement
      isTradeAllowed, // Add the computed property for economy restrictions
      locationFilter, // Add location filter state
      handleLocationFilterUpdate, // Add location filter handler
      isInitialLoading, // Add initial loading state
      getNumberOfColumns,
      // New properties for improved filtering
      noMatchingItems,
      resetFilters,
      hasActiveFilters,
      isOrgUIDLoaded, // Expose the organization UID loaded state
      fetchConvertedPrice,
      processedItemIds, // Expose duplicate tracking
      timelineError,
      retryTimelineFetch,
      formKey,
      // title helpers
      truncateTitleForBand,
      getDisplayTitle,
      hasRealTitle,
      hasImages,
      // org mode functionality
      internalOrgMode,
      shouldShowOrgToggle,
      handleOrgModeUpdate,
      openOrganisationCreation,
      openInvites,
      feedScope,
      feedScopeOptions,
      hasActiveOrganisations,
      hasMultipleOrganisations,
      handleSelectedOrgChange,
      isOrgContextFeatureEnabled,
      emptyStateTitle,
      emptyStateDescription,
      startMint,
      canMintItem,
      isItemMinted,
      mintingStatus,
      hasMintFeeConfigured,
      mintFeeEthDisplay,
      showMintModal,
      pendingMint,
      mintModalTitle,
      mintModalListingTitle,
      mintModalFeeText,
      mintModalCreatorWallet,
      mintModalTreasuryAddress,
      mintModalMetadata,
      mintModalPreviewImage,
      mintModalAcknowledged,
      mintModalLoading,
      handleMintModalClose,
      handleMintModalConfirm,
      handleSimpleMintConfirm,
      handleBatchMintSuccess,
      showBatchMintModal,
      closeBatchMintModal,
      pendingBatchMintItem,
      startBatchMintFromPost,
      isMultiImageItem,
      getItemImageCount,
      batchMintSelectedItems,
      isItemSelectableForBatchMint,
      handleBatchMintSelectionChange,
      updateMintModalAcknowledged,
      mintModalRoyaltyBps,
      mintModalDefaultRoyaltyBps,
      mintModalMaxRoyaltyBps,
      updateMintModalRoyalty,
      updateMintModalSelectedChain,
    };
  },
};
</script>

<style scoped>
/* Feed scope toggle — sits in scrollable content area above the masonry grid */
.feed-scope-toggle {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  flex-wrap: wrap;
  margin: var(--space-4) 0 var(--space-3) 0;
}

.feed-organisation-zero-state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
  margin: 0 0 var(--space-5);
  padding: var(--space-5);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
}

.feed-organisation-zero-state h2,
.feed-organisation-zero-state p {
  margin: 0;
}

.feed-organisation-zero-state p {
  margin-top: var(--space-2);
  color: var(--color-text-tertiary);
}

.feed-organisation-zero-state__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-3);
}

.manual-load-more {
  display: block;
  margin: var(--space-4) auto;
}
@media (max-width: 768px) {
  .feed-scope-toggle {
    margin: 0 0 var(--space-4);
  }

  .feed-organisation-zero-state {
    align-items: flex-start;
    flex-direction: column;
  }

  .feed-organisation-zero-state__actions {
    justify-content: flex-start;
  }
}

/* Masonry item title row - contains title and optional batch mint checkbox */
.masonry-item-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-2);
  width: 100%; /* Force width constraint */
  max-width: 100%; /* Prevent overflow */
}

/* Masonry item title - 24pt equivalent */
.masonry-item-title {
  font-size: var(--font-size-heading);
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  white-space: normal;
}

/* Batch mint checkbox next to title */
.batch-mint-checkbox {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  flex-shrink: 0;
}
.batch-mint-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--color-control-success-accent);
}
.batch-mint-checkbox .checkbox-label {
  font-size: var(--font-size-label);
  color: var(--color-feedback-success-foreground);
  font-weight: 500;
}

/* Price and stock on same row in ImageCard footer */
.info-footer-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: var(--space-4);
}

.price-inline {
  flex-shrink: 0;
}

.marketplace-state-action.unit-button {
  height: var(--control-height-default);
  padding: 0 var(--space-6);
  border-color: var(--color-border-control);
  font-weight: var(--font-weight-normal);
}

.marketplace-state-action.unit-button:not(:disabled):hover,
.marketplace-price-button.unit-button:not(:disabled):hover {
  background: transparent;
  transform: translateY(var(--motion-pressed-offset));
}

.marketplace-price-button.unit-button {
  min-width: var(--control-height-dense);
  min-height: var(--control-height-dense);
  padding: 0 var(--space-6);
  border-color: var(--color-border-control);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-normal);
}

.price-inline :deep(.marketplace-price-control) {
  padding: 0 var(--space-6);
  border: 1px solid var(--color-border-control);
  border-radius: var(--radius-pill);
  background: transparent;
  box-shadow: none;
}

.stock-inline {
  flex-shrink: 0;
}

.masonry-grid-container {
  min-height: calc(100vh - (var(--control-height-default) + var(--space-7)));
  margin-bottom: calc((var(--space-8) * 3 + var(--space-5)) * -1);
  /* CSS Rendering Pipeline Optimizations */
  will-change: scroll-position;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

.masonry-marketplace {
  width: 100%;
  padding-bottom: calc(var(--control-height-compact) * 2);
}

.filters {
  display: flex;
  margin-bottom: var(--space-4);
  gap: var(--space-4);
}

.search-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-container input {
  width: 100%;
  padding: var(--space-3);
  padding-left: calc(var(--control-height-compact) * 0.9);
  border-radius: var(--radius-sm);
}

.search-container .search-icon {
  position: absolute;
  left: var(--space-3);
  pointer-events: none; /* Allow clicks to pass through to the input */
  display: flex;
  align-items: center;
  opacity: 0.6;
}

.masonry-grid {
  display: flex;
  gap: var(--space-4);
}

.masonry-column {
  flex: 1;
  /* Ensure column allows shrinking below content size to maintains equal widths */
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  /* CSS Layout Containment for Performance */
  contain: layout style;
}

.masonry-item {
  background: var(--color-surface-canvas);
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative; /* Ensure the item details are positioned correctly */
  /* IMPORTANT: overflow must stay visible so that action-menu dropdowns
     (e.g. Dropdown.vue) are not clipped on short-height posts.
     Do NOT add overflow:hidden here. */
  overflow: visible;
  /* CSS Rendering Pipeline Optimizations */
  will-change: transform;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

figure.masonry-item {
  margin: 0;
  margin-bottom: var(--space-3);
}

.masonry-item img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-border-subtle);
}

/* Alpha-capable media: remove backgrounds from parent card and img */
.masonry-item:has(.transparent-media) {
  background: transparent;
}
.masonry-item:has(.transparent-media) img {
  background: transparent;
}

.no-image {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-border-subtle);
  border-radius: var(--radius-sm);
  overflow: hidden;
}
.grayedOut,
.soldout,
.service-label {
  color: var(--color-text-muted);
}

.editButton .gray {
  transition: color var(--motion-standard) ease;
  color: var(--color-text-muted);
}

.editButton:hover .gray {
  color: var(--color-text-secondary) !important;
}

.image-container {
  position: relative;
  margin-bottom: var(--space-3);
}

.top-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 30%; /* Adjust the height for more gradient */
  background: linear-gradient(
    to bottom,
    var(--color-surface-canvas) 10%,
    transparent
  );
  z-index: 1;
}

.item-details {
  top: 0px;
  bottom: 0;
  width: 100%;
  background: var(--color-surface-canvas);
  padding-bottom: var(--space-2);
  box-sizing: border-box;
  z-index: 2;
}

.row {
  display: flex;
  justify-content: space-between;
}

.productActions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.stockWarningColor {
  color: var(--color-feedback-danger-foreground);
}

.soldout {
  color: var(--color-text-muted);
}

.actionsRow {
  align-items: center;
  width: 100%;
}

.pointer {
  cursor: pointer;
}

.price-container {
  flex: 1;
  min-width: 0;
}

.invisible-spacer {
  width: 1px;
  height: 1px;
  flex: 1;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  flex: 2;
}

.innerSticker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--control-height-dense);
  height: var(--control-height-dense);
  border-radius: 50%;
  background-color: var(--color-surface-canvas);
  padding: var(--space-2);
  cursor: pointer;
}

.favourites-button {
  border: transparent !important;
  padding: 2px;
}

.ownedByCurrentUser .innerSticker {
  width: fit-content;
  border-radius: var(--radius-pill);
  gap: var(--space-2);
  padding: 0 var(--space-4);
}

/* Loading indicator for wishlist button */
.loading-button {
  cursor: default !important;
}

.loading-indicator {
  display: inline-block;
  margin-right: var(--space-2);
  flex-shrink: 0;
}

.favourites-button .compactImg {
  width: calc(var(--control-height-compact) * 0.68) !important;
  height: calc(var(--control-height-compact) * 0.68) !important;
}

/* Remove column-count-based styles (obsolete, cleaned up) */

.economy-restriction-message {
  background-color: var(--color-feedback-warning-surface);
  padding: var(--space-4);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  border: 1px solid var(--color-feedback-warning-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.economy-restriction-message p {
  margin: 0;
  font-size: var(--font-size-label);
  color: var(--color-text-secondary);
  font-weight: 500;
}

.economy-restriction-message .warning-icon {
  color: var(--color-feedback-warning-foreground);
  fill: currentColor;
  min-width: 20px;
}

/* Responsive column layout */
@media (max-width: 590px) {
  .masonry-item-title {
    font-size: var(--font-size-title);
  }
}

@media (min-width: 591px) and (max-width: 1080px) {
  /* Update based on manual column count */
}

@media (min-width: 1081px) {
  /* Update based on manual column count */
}

/* Skeleton loading styles */
.skeleton-item {
  background: var(--color-surface-canvas);
  border-radius: var(--radius-sm);
  position: relative;
  overflow: hidden;
  margin-bottom: var(--space-3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  animation: fadeIn 0.3s ease-in;
}

/* Special styling for skeleton loaders at the end of columns */
.skeleton-loader {
  background: var(--color-surface-canvas);
  border: 1px dashed var(--color-border-muted);
  border-radius: var(--radius-sm);
  opacity: 0.85;
  margin-top: var(--space-4);
  transform-origin: center top;
  animation: pulseOpacity 1.5s infinite alternate ease-in-out;
}

@keyframes pulseOpacity {
  from {
    opacity: 0.7;
  }
  to {
    opacity: 0.9;
  }
}

.skeleton-image {
  width: 100%;
  height: 240px;
  background: var(--color-surface-muted);
  border-radius: var(--radius-sm);
}

.skeleton-details {
  padding: var(--space-3);
}

.skeleton-title {
  height: 1rem;
  width: 80%;
  margin-bottom: var(--space-3);
  background-color: var(--color-border-subtle);
  border-radius: var(--radius-sm);
}

.skeleton-price {
  height: 1rem;
  width: 40%;
  background-color: var(--color-border-subtle);
  border-radius: var(--radius-sm);
}

/* Enhanced shimmer animation effect */
.shimmer {
  position: relative;
  overflow: hidden;
}

.shimmer::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Initial skeleton loader styles */
.initial-skeleton-container {
  display: flex;
  width: 100%;
  gap: var(--space-4);
  margin-top: var(--space-4);
  margin-bottom: var(--space-4);
}

.initial-skeleton-container .skeleton-item {
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* No results message styles */
.no-results-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 300px;
  padding: var(--space-5);
}

.no-results-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--space-5);
  max-width: 400px;
}

.no-results-icon {
  width: 48px;
  height: 48px;
  margin-bottom: var(--space-4);
  opacity: 0.6;
}

.no-results-message h3 {
  margin: var(--space-3) 0;
  font-weight: 500;
}

.no-results-message p {
  margin-bottom: var(--space-5);
  color: var(--color-text-muted);
}
</style>
<style scoped>
.above-grid-lists {
  margin: 0;
}
/* Only add margins when slot content has visible children */
.above-grid-lists:has(> div) {
  margin: var(--space-4) 0 var(--space-3);
}
.above-grid-lists .separator {
  height: 1px;
  background: var(--color-divider-strong);
  margin-top: var(--space-4);
}
.market-toolbar-and-lists {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0; /* override .column gap — child margins handle spacing */
  margin-bottom: var(--space-5);
}
/* Remove FiltersComponent bottom margin to prevent double spacing */
.market-toolbar-and-lists > :deep(.divSmallMarginBottom) {
  margin-bottom: 0;
}
/* Mobile stacking: keep scrollable lists directly above toolbar pinned at bottom */
@media (max-width: 768px) {
  .market-toolbar-and-lists {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--color-surface-canvas);
    z-index: 6;
    padding: var(--space-5) var(--space-4) calc(var(--space-4) - 2px);
    gap: var(--space-2);
    box-shadow: var(--shadow-surface-floating);
    transition: transform 0.25s ease;
    margin-bottom: 0;
  }
  /* Slide toolbar down off-screen when navbar hides (mirrors the navbar slide-up) */
  .market-toolbar-and-lists.hidden-toolbar {
    transform: translateY(100%);
    padding: 0;
    gap: 0;
    height: 0;
    min-height: 0;
    overflow: hidden;
  }
  /* Override the internal mobile fixed styling of the toolbar so it flows inside stack */
  .market-toolbar-and-lists .marketToolBar {
    position: static !important;
    width: 100%;
    background: transparent;
    padding: 0;
    margin: 0;
    box-shadow: none;
  }
  .market-toolbar-and-lists .above-grid-lists {
    order: 1;
    margin: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  /* Ensure toolbar stays beneath the lists visually */
  .market-toolbar-and-lists .marketToolBar {
    order: 2;
  }
  /* Provide bottom padding to main content so it's not obscured by fixed stack */
  .masonry-marketplace > .masonry-grid-container,
  .masonry-marketplace > .initial-skeleton-container {
    padding-bottom: 220px; /* approximate combined height of lists + toolbar; adjust if needed */
  }
}

/* Dropdown actions menu styles */
.actions-menu {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 140px;
}

.actions-menu .menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  font-size: var(--font-size-label);
  border-radius: var(--radius-sm);
}

.actions-menu .menu-item:hover:not(.disabled) {
  background-color: var(--color-surface-menu-hover);
}

.actions-menu .menu-item.delete-item:hover {
  color: var(--color-feedback-danger-foreground) !important;
}

.actions-menu .menu-item.delete-item svg {
  color: var(--color-feedback-danger-foreground) !important;
  stroke: var(--color-feedback-danger-foreground) !important;
  fill: var(--color-feedback-danger-foreground) !important;
}

.actions-menu .menu-item.delete-item:hover {
  background-color: var(--color-feedback-danger-confirm-surface);
}

.actions-menu .menu-item.delete-item.confirm-phase {
  color: var(--color-feedback-danger-foreground) !important;
  background-color: var(--color-feedback-danger-confirm-surface);
}

.actions-menu .menu-item.delete-item.confirm-phase strong {
  font-weight: 700 !important;
  color: var(--color-feedback-danger-foreground) !important;
}

.actions-menu .menu-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.actions-menu .menu-item svg.tinyImg {
  flex-shrink: 0;
}
</style>
